import * as widget from './alan_api';
import * as client_bindings from '../../libraries/client_lib/types/client_bindings/alan_api';
import * as control_library from '../../libraries/control_bundle_lib/libraries/controls_lib/types/control_library/alan_api';
import * as phrases from '../phrases/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cwidget
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cwidget|undefined;
	public abstract get root():Cwidget;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
export type Twidget_implementation_node = {
	'attributes':Record<string, Tattributes__widget_implementation_node>;
};
export class Cwidget_implementation_node extends AlanNode {
	public readonly properties:{
		readonly attributes:Cwidget_implementation_node.Dattributes
	};
	constructor(init:Twidget_implementation_node, public location:AlanNode, public input: {
		context_control_node: () => control_library.Ccontrol_node,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cwidget_implementation_node.Dattributes(init['attributes'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kattributes__widget_implementation_node extends Reference<control_library.Cattributes__control_node, string> {
	constructor(key:string, $this:Cattributes__widget_implementation_node) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.context_control_node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'::key`; }
}
export type Tattributes__widget_implementation_node = {
	'attribute':Twidget_implementation_attribute;
};
export class Cattributes__widget_implementation_node extends AlanDictionaryEntry {
	public key:Kattributes__widget_implementation_node;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly attribute:Cwidget_implementation_attribute
	};
	constructor(key:string, init:Tattributes__widget_implementation_node, public parent:Cwidget_implementation_node) {
		super();
		const $this = this;
		this.key = new Kattributes__widget_implementation_node(key, $this);
		this.properties = {
			attribute: new Cattributes__widget_implementation_node.Dattribute(init['attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key.entry}']`; }
	public get entity() { return this; }
}
type Vwidget_implementation_context = { name: 'implicit', definition: widget.Cimplicit_implementation_context}|{ name: 'explicit', definition: widget.Cwidget_implementation_frame}
export class Cwidget_implementation_context extends AlanStruct {
	constructor(
		public readonly variant:Vwidget_implementation_context, public input: {
			binding_context: () => client_bindings.Cbinding_context,
			explicit_context: () => widget.Cwidget_implementation_context,
			widget_configuration_context: () => widget.Cwidget_configuration_node
		}) { super(); }
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
		explicit_context: () => widget.Cwidget_implementation_context;
		widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.binding_context()).result!),
		explicit_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.explicit_context()).result!),
		widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.widget_configuration_context()).result!)
	};
	public cast<K extends Vwidget_implementation_context['name']>(_variant:K):Extract<Vwidget_implementation_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vwidget_implementation_context['name']]:(($:Extract<Vwidget_implementation_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'widget implementation context'`; }
	public is(other:Cwidget_implementation_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vwidget_implementation_frame = { name: 'variable', definition: widget.Cvariable}
export class Cwidget_implementation_frame extends AlanStruct {
	constructor(
		public readonly variant:Vwidget_implementation_frame, public input: {
			binding_context: () => client_bindings.Cbinding_context,
			explicit_context: () => widget.Cwidget_implementation_context,
			let_declarations: () => widget.Clet_declarations__widget,
			parent_context: () => widget.Cwidget_implementation_context,
			widget_configuration_context: () => widget.Cwidget_configuration_node
		}) { super(); }
	public definitions:{
		widget_implementation_context: Cwidget_implementation_context;
	} = {
		widget_implementation_context: new Cwidget_implementation_context({name:'explicit', definition: this}, {
			binding_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.binding_context()).result!),
			explicit_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.explicit_context()).result!),
			widget_configuration_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.widget_configuration_context()).result!)
		})
	}
	public readonly output:{
		let_declarations: () => widget.Clet_declarations__widget;
		parent_context: () => widget.Cwidget_implementation_context;
	} = {
		let_declarations: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.let_declarations()).result!),
		parent_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent_context()).result!)
	};
	public cast<K extends Vwidget_implementation_frame['name']>(_variant:K):Extract<Vwidget_implementation_frame, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vwidget_implementation_frame['name']]:(($:Extract<Vwidget_implementation_frame, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'widget implementation frame'`; }
	public is(other:Cwidget_implementation_frame):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Twidget_implementation_attribute = {
	'expression':Tone_or_more_expression;
};
export class Cwidget_implementation_attribute extends AlanNode {
	public readonly properties:{
		readonly expression:Cone_or_more_expression
	};
	public readonly output:{
		attribute: () => control_library.Cattributes__control_node;
	} = {
		attribute: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.attribute()).result!)
	};
	constructor(init:Twidget_implementation_attribute, public location:AlanNode, public input: {
		attribute: () => control_library.Cattributes__control_node,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cwidget_implementation_attribute.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vwidget_configuration_node_location = { name: 'configuration list', definition: widget.Clist__type}|{ name: 'configuration state', definition: widget.Cstates__state_group}|{ name: 'binding', definition: widget.Cbinding__type__attributes}|{ name: 'window', definition: widget.Cwindow__type}|{ name: 'widget', definition: widget.Cwidget}
export class Cwidget_configuration_node_location extends AlanStruct {
	constructor(
		public readonly variant:Vwidget_configuration_node_location) { super(); }
	public cast<K extends Vwidget_configuration_node_location['name']>(_variant:K):Extract<Vwidget_configuration_node_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vwidget_configuration_node_location['name']]:(($:Extract<Vwidget_configuration_node_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'widget configuration node location'`; }
	public is(other:Cwidget_configuration_node_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Twidget_configuration_node = {
	'attributes':Record<string, Tattributes__widget_configuration_node>;
};
export class Cwidget_configuration_node extends AlanNode {
	public readonly properties:{
		readonly attributes:Cwidget_configuration_node.Dattributes
	};
	public readonly output:{
		location: () => widget.Cwidget_configuration_node_location;
	} = {
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!)
	};
	constructor(init:Twidget_configuration_node, public location:AlanNode, public input: {
		client_binding_context: () => client_bindings.Cbinding_context,
		location: () => widget.Cwidget_configuration_node_location,
		requirement: () => widget.Cconfiguration_requirement
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cwidget_configuration_node.Dattributes(init['attributes'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tattributes__widget_configuration_node = {
	'switch client binding context':'no'|['no', {}]|['yes', Tyes__switch_client_binding_context__attributes];
	'type':['binding', Tbinding__type__attributes]|['configuration', Tconfiguration__type__attributes__widget_configuration_node]|'view'|['view', {}]|'widget'|['widget', {}]|['window', Twindow__type];
};
export class Cattributes__widget_configuration_node extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly switch_client_binding_context:Cattributes__widget_configuration_node.Dswitch_client_binding_context,
		readonly type:Cattributes__widget_configuration_node.Dtype
	};
	constructor(key:string, init:Tattributes__widget_configuration_node, public parent:Cwidget_configuration_node) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			switch_client_binding_context: new Cattributes__widget_configuration_node.Dswitch_client_binding_context(init['switch client binding context'], $this),
			type: new Cattributes__widget_configuration_node.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__switch_client_binding_context__attributes = {
};
export class Cno__switch_client_binding_context__attributes extends AlanNode {
	public readonly output:{
		client_binding: () => client_bindings.Cbinding_context;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.client_binding_context()).result!)
	}
	constructor(init:Tno__switch_client_binding_context__attributes, public parent:Cattributes__widget_configuration_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch client binding context'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__switch_client_binding_context__attributes = {
	'constrained on containing binding':['no', Tno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes]|'yes'|['yes', {}];
	'type path':Tclient_binding_type_path;
};
export class Cyes__switch_client_binding_context__attributes extends AlanNode {
	public readonly properties:{
		readonly constrained_on_containing_binding:Cyes__switch_client_binding_context__attributes.Dconstrained_on_containing_binding,
		readonly type_path:Cclient_binding_type_path
	};
	public readonly output:{
		client_binding: () => client_bindings.Cbinding_context;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type_path)
			.then(context => context?.component_root.output.result_node())
			.then(context => context?.definitions.binding_context).result!)
	}
	constructor(init:Tyes__switch_client_binding_context__attributes, public parent:Cattributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			constrained_on_containing_binding: new Cyes__switch_client_binding_context__attributes.Dconstrained_on_containing_binding(init['constrained on containing binding'], $this),
			type_path: new Cyes__switch_client_binding_context__attributes.Dtype_path(init['type path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch client binding context'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes = {
	'instance binding':string;
};
export class Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes extends AlanNode {
	public readonly properties:{
		readonly instance_binding:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes.Dinstance_binding
	};
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.instance_binding?.ref)
			.then(context => context?.properties.binding).result!)
	}
	constructor(init:Tno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, public parent:Cyes__switch_client_binding_context__attributes) {
		super();
		const $this = this;
		this.properties = {
			instance_binding: new Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes.Dinstance_binding(init['instance binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes = {
};
export class Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes extends AlanNode {
	public readonly output:{
		node: () => client_bindings.Cnode__client_bindings;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node()).result!)
	}
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__widget_configuration_node__attributes__switch_client_binding_context__yes__constrained_on_containing_binding__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.client_binding_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, public parent:Cyes__switch_client_binding_context__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding__type__attributes = {
	'constrained on containing binding':['no', Tno__constrained_on_containing_binding__binding]|['yes', Tyes__constrained_on_containing_binding__binding];
	'node':Twidget_configuration_node;
};
export class Cbinding__type__attributes extends AlanNode {
	public definitions:{
		widget_configuration_node_location: Cwidget_configuration_node_location;
	} = {
		widget_configuration_node_location: new Cwidget_configuration_node_location({name:'binding', definition: this})
	}
	public readonly properties:{
		readonly constrained_on_containing_binding:Cbinding__type__attributes.Dconstrained_on_containing_binding,
		readonly node:Cwidget_configuration_node
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	constructor(init:Tbinding__type__attributes, public parent:Cattributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			constrained_on_containing_binding: new Cbinding__type__attributes.Dconstrained_on_containing_binding(init['constrained on containing binding'], $this),
			node: new Cbinding__type__attributes.Dnode(init['node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__constrained_on_containing_binding__binding = {
	'instance binding':string;
};
export class Cno__constrained_on_containing_binding__binding extends AlanNode {
	public readonly properties:{
		readonly instance_binding:Cno__constrained_on_containing_binding__binding.Dinstance_binding
	};
	public readonly output:{
		client_binding: () => client_bindings.Cbindings__client_bindings;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.instance_binding?.ref).result!)
	}
	constructor(init:Tno__constrained_on_containing_binding__binding, public parent:Cbinding__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			instance_binding: new Cno__constrained_on_containing_binding__binding.Dinstance_binding(init['instance binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__constrained_on_containing_binding__binding = {
	'instance binding':string;
	'type path':Tclient_binding_type_path;
};
export class Cyes__constrained_on_containing_binding__binding extends AlanNode {
	public readonly properties:{
		readonly instance_binding:Cyes__constrained_on_containing_binding__binding.Dinstance_binding,
		readonly type_path:Cclient_binding_type_path
	};
	public readonly output:{
		client_binding: () => client_bindings.Cbindings__client_bindings;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.instance_binding?.ref)
			.then(context => context?.properties.binding?.ref).result!)
	}
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__widget_configuration_node__attributes__type__binding__constrained_on_containing_binding__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.switch_client_binding_context?.state.node.output.client_binding())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Tyes__constrained_on_containing_binding__binding, public parent:Cbinding__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			instance_binding: new Cyes__constrained_on_containing_binding__binding.Dinstance_binding(init['instance binding'], $this),
			type_path: new Cyes__constrained_on_containing_binding__binding.Dtype_path(init['type path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconfiguration__type__attributes__widget_configuration_node = {
	'type':['list', Tlist__type]|['number', Tnumber__type__configuration__type__attributes__widget_configuration_node]|['state group', Tstate_group__type__configuration__type__attributes__widget_configuration_node]|['text', Ttext__type__configuration__type__attributes__widget_configuration_node];
};
export class Cconfiguration__type__attributes__widget_configuration_node extends AlanNode {
	public readonly properties:{
		readonly type:Cconfiguration__type__attributes__widget_configuration_node.Dtype
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.requirement()).result!)
	}
	constructor(init:Tconfiguration__type__attributes__widget_configuration_node, public parent:Cattributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			type: new Cconfiguration__type__attributes__widget_configuration_node.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist__type = {
	'node':Twidget_configuration_node;
};
export class Clist__type extends AlanNode {
	public definitions:{
		widget_configuration_node_location: Cwidget_configuration_node_location;
	} = {
		widget_configuration_node_location: new Cwidget_configuration_node_location({name:'configuration list', definition: this})
	}
	public readonly properties:{
		readonly node:Cwidget_configuration_node
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	constructor(init:Tlist__type, public parent:Cconfiguration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			node: new Clist__type.Dnode(init['node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'list'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__configuration__type__attributes__widget_configuration_node = {
	'has default':'no'|['no', {}]|['yes', Tyes__has_default__number];
	'persistence':Tconfiguration_attribute_persistence;
};
export class Cnumber__type__configuration__type__attributes__widget_configuration_node extends AlanNode {
	public readonly properties:{
		readonly has_default:Cnumber__type__configuration__type__attributes__widget_configuration_node.Dhas_default,
		readonly persistence:Cconfiguration_attribute_persistence
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_default?.state.node.output.requirement()).result!)
	}
	constructor(init:Tnumber__type__configuration__type__attributes__widget_configuration_node, public parent:Cconfiguration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			has_default: new Cnumber__type__configuration__type__attributes__widget_configuration_node.Dhas_default(init['has default'], $this),
			persistence: new Cnumber__type__configuration__type__attributes__widget_configuration_node.Dpersistence(init['persistence'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_default__number = {
};
export class Cno__has_default__number extends AlanNode {
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	public readonly inferences:{
		required: () => widget.Crequired
	} = {
		required: cache((detach:boolean) => {
			const widget__widget_configuration_node__attributes__type__configuration__type__number__has_default__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.requirement())
				.then(context => context?.variant.name === 'required' ? context.variant.definition as widget.Crequired : undefined).result!;
		})

	}
	constructor(init:Tno__has_default__number, public parent:Cnumber__type__configuration__type__attributes__widget_configuration_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_default__number = {
	'value':number;
};
export class Cyes__has_default__number extends AlanNode {
	public readonly properties:{
		readonly value:Cyes__has_default__number.Dvalue
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Poptional).result!)
	}
	constructor(init:Tyes__has_default__number, public parent:Cnumber__type__configuration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			value: new Cyes__has_default__number.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__configuration__type__attributes__widget_configuration_node = {
	'has default':'no'|['no', {}]|['yes', Tyes__has_default__state_group];
	'persistence':Tconfiguration_attribute_persistence;
	'states':Record<string, Tstates__state_group>;
};
export class Cstate_group__type__configuration__type__attributes__widget_configuration_node extends AlanNode {
	public readonly properties:{
		readonly has_default:Cstate_group__type__configuration__type__attributes__widget_configuration_node.Dhas_default,
		readonly persistence:Cconfiguration_attribute_persistence,
		readonly states:Cstate_group__type__configuration__type__attributes__widget_configuration_node.Dstates
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_default?.state.node.output.requirement()).result!)
	}
	constructor(init:Tstate_group__type__configuration__type__attributes__widget_configuration_node, public parent:Cconfiguration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			has_default: new Cstate_group__type__configuration__type__attributes__widget_configuration_node.Dhas_default(init['has default'], $this),
			persistence: new Cstate_group__type__configuration__type__attributes__widget_configuration_node.Dpersistence(init['persistence'], $this),
			states: new Cstate_group__type__configuration__type__attributes__widget_configuration_node.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_default__state_group = {
};
export class Cno__has_default__state_group extends AlanNode {
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	public readonly inferences:{
		required: () => widget.Crequired
	} = {
		required: cache((detach:boolean) => {
			const widget__widget_configuration_node__attributes__type__configuration__type__state_group__has_default__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.requirement())
				.then(context => context?.variant.name === 'required' ? context.variant.definition as widget.Crequired : undefined).result!;
		})

	}
	constructor(init:Tno__has_default__state_group, public parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_default__state_group = {
	'state':string;
};
export class Cyes__has_default__state_group extends AlanNode {
	public readonly properties:{
		readonly state:Cyes__has_default__state_group.Dstate
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Poptional).result!)
	}
	constructor(init:Tyes__has_default__state_group, public parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			state: new Cyes__has_default__state_group.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstates__state_group = {
	'node':Twidget_configuration_node;
	'state default':Toptional_state_HACK;
};
export class Cstates__state_group extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		widget_configuration_node_location: Cwidget_configuration_node_location;
	} = {
		widget_configuration_node_location: new Cwidget_configuration_node_location({name:'configuration state', definition: this})
	}
	public readonly properties:{
		readonly node:Cwidget_configuration_node,
		readonly state_default:Coptional_state_HACK & { readonly inferences: {
			requirement: () => widget.Cconfiguration_requirement;
		} }
	};
	constructor(key:string, init:Tstates__state_group, public parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			node: new Cstates__state_group.Dnode(init['node'], $this),
			state_default: new Cstates__state_group.Dstate_default(init['state default'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key}']`; }
	public get entity() { return this; }
}
export type Ttext__type__configuration__type__attributes__widget_configuration_node = {
	'has default':'no'|['no', {}]|['yes', Tyes__has_default__text];
	'persistence':Tconfiguration_attribute_persistence;
};
export class Ctext__type__configuration__type__attributes__widget_configuration_node extends AlanNode {
	public readonly properties:{
		readonly has_default:Ctext__type__configuration__type__attributes__widget_configuration_node.Dhas_default,
		readonly persistence:Cconfiguration_attribute_persistence
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_default?.state.node.output.requirement()).result!)
	}
	constructor(init:Ttext__type__configuration__type__attributes__widget_configuration_node, public parent:Cconfiguration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			has_default: new Ctext__type__configuration__type__attributes__widget_configuration_node.Dhas_default(init['has default'], $this),
			persistence: new Ctext__type__configuration__type__attributes__widget_configuration_node.Dpersistence(init['persistence'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_default__text = {
};
export class Cno__has_default__text extends AlanNode {
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	public readonly inferences:{
		required: () => widget.Crequired
	} = {
		required: cache((detach:boolean) => {
			const widget__widget_configuration_node__attributes__type__configuration__type__text__has_default__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.requirement())
				.then(context => context?.variant.name === 'required' ? context.variant.definition as widget.Crequired : undefined).result!;
		})

	}
	constructor(init:Tno__has_default__text, public parent:Ctext__type__configuration__type__attributes__widget_configuration_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_default__text = {
	'type':['static', Tstatic__type]|'unique id'|['unique id', {}];
};
export class Cyes__has_default__text extends AlanNode {
	public readonly properties:{
		readonly type:Cyes__has_default__text.Dtype
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Poptional).result!)
	}
	constructor(init:Tyes__has_default__text, public parent:Ctext__type__configuration__type__attributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			type: new Cyes__has_default__text.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type = {
	'value':string;
};
export class Cstatic__type extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tstatic__type, public parent:Cyes__has_default__text) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunique_id = {
};
export class Cunique_id extends AlanNode {
	constructor(init:Tunique_id, public parent:Cyes__has_default__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'unique id'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview__type__attributes = {
};
export class Cview__type__attributes extends AlanNode {
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	constructor(init:Tview__type__attributes, public parent:Cattributes__widget_configuration_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget__type = {
};
export class Cwidget__type extends AlanNode {
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	constructor(init:Twidget__type, public parent:Cattributes__widget_configuration_node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Twindow__type = {
	'node':Twidget_configuration_node;
};
export class Cwindow__type extends AlanNode {
	public definitions:{
		widget_configuration_node_location: Cwidget_configuration_node_location;
	} = {
		widget_configuration_node_location: new Cwidget_configuration_node_location({name:'window', definition: this})
	}
	public readonly properties:{
		readonly node:Cwidget_configuration_node
	};
	public readonly output:{
		requirement: () => widget.Cconfiguration_requirement;
	} = {
		requirement: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cconfiguration_requirement.Prequired).result!)
	}
	constructor(init:Twindow__type, public parent:Cattributes__widget_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			node: new Cwindow__type.Dnode(init['node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'window'`; }
	public get entity() { return this.parent.entity; }
}
type Vimplicit_implementation_context = { name: 'non assigned variable', definition: widget.Cno__assign}|{ name: 'variable', definition: widget.Cvariable}|{ name: 'root', definition: widget.Cwidget}
export class Cimplicit_implementation_context extends AlanStruct {
	constructor(
		public readonly variant:Vimplicit_implementation_context, public input: {
			binding_context: () => client_bindings.Cbinding_context,
			explicit_context: () => widget.Cwidget_implementation_context,
			widget_configuration_context: () => widget.Cwidget_configuration_node
		}) { super(); }
	public definitions:{
		widget_implementation_context: Cwidget_implementation_context;
	} = {
		widget_implementation_context: new Cwidget_implementation_context({name:'implicit', definition: this}, {
			binding_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.binding_context()).result!),
			explicit_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.explicit_context()).result!),
			widget_configuration_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.widget_configuration_context()).result!)
		})
	}
	public cast<K extends Vimplicit_implementation_context['name']>(_variant:K):Extract<Vimplicit_implementation_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vimplicit_implementation_context['name']]:(($:Extract<Vimplicit_implementation_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'implicit implementation context'`; }
	public is(other:Cimplicit_implementation_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tvariable_assignment = {
	'assign':'no'|['no', {}]|['yes', Tyes__assign];
};
export class Cvariable_assignment extends AlanNode {
	public readonly properties:{
		readonly assign:Cvariable_assignment.Dassign
	};
	public readonly output:{
		widget_implementation_context: () => widget.Cwidget_implementation_context;
	} = {
		widget_implementation_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.assign?.state.node.output.context()).result!)
	};
	constructor(init:Tvariable_assignment, public location:AlanNode, public input: {
		binding_context: () => client_bindings.Cbinding_context,
		current_context: () => widget.Cwidget_implementation_context,
		widget_configuration_context: () => widget.Cwidget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			assign: new Cvariable_assignment.Dassign(init['assign'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__assign = {
};
export class Cno__assign extends AlanNode {
	public definitions:{
		implicit_implementation_context: Cimplicit_implementation_context;
	} = {
		implicit_implementation_context: new Cimplicit_implementation_context({name:'non assigned variable', definition: this}, {
			binding_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.binding_context()).result!),
			explicit_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.current_context())
				.then(context => context?.component_root.output.explicit_context()).result!),
			widget_configuration_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.widget_configuration_context()).result!)
		})
	}
	public readonly output:{
		context: () => widget.Cwidget_implementation_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.definitions.implicit_implementation_context)
			.then(context => context?.definitions.widget_implementation_context).result!)
	}
	constructor(init:Tno__assign, public parent:Cvariable_assignment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'assign'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__assign = {
	'variable':Tvariable;
};
export class Cyes__assign extends AlanNode {
	public readonly properties:{
		readonly variable:Cvariable
	};
	public readonly output:{
		context: () => widget.Cwidget_implementation_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.variable)
			.then(context => context?.component_root.output.widget_implementation_context()).result!)
	}
	constructor(init:Tyes__assign, public parent:Cvariable_assignment) {
		super();
		const $this = this;
		this.properties = {
			variable: new Cyes__assign.Dvariable(init['variable'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'assign'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvariable = {
	'context':Tcontext_selection;
	'identifier':'anonymous'|['anonymous', {}]|['named', Tnamed__identifier];
	'let declarations':Tlet_declarations__widget;
	'more variables':'no'|['no', {}]|['yes', Tyes__more_variables];
};
export class Cvariable extends AlanNode {
	public definitions:{
		implicit_implementation_context: Cimplicit_implementation_context;
		widget_implementation_frame: Cwidget_implementation_frame;
	} = {
		implicit_implementation_context: new Cimplicit_implementation_context({name:'variable', definition: this}, {
			binding_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.binding_context()).result!),
			explicit_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.current_context())
				.then(context => context?.component_root.output.explicit_context()).result!),
			widget_configuration_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.widget_configuration_context()).result!)
		}),
		widget_implementation_frame: new Cwidget_implementation_frame({name:'variable', definition: this}, {
			binding_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_binding_context()).result!),
			explicit_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.definitions.widget_implementation_frame)
				.then(context => context?.definitions.widget_implementation_context).result!),
			let_declarations: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.let_declarations).result!),
			parent_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.current_context())
				.then(context => context?.component_root.output.explicit_context()).result!),
			widget_configuration_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node()).result!)
		})
	}
	public readonly properties:{
		readonly context:Ccontext_selection,
		readonly identifier:Cvariable.Didentifier,
		readonly let_declarations:Clet_declarations__widget,
		readonly more_variables:Cvariable.Dmore_variables
	};
	public readonly output:{
		widget_implementation_context: () => widget.Cwidget_implementation_context;
	} = {
		widget_implementation_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.more_variables?.state.node.output.context()).result!)
	};
	constructor(init:Tvariable, public location:AlanNode, public input: {
		binding_context: () => client_bindings.Cbinding_context,
		current_context: () => widget.Cwidget_implementation_context,
		widget_configuration_context: () => widget.Cwidget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			context: new Cvariable.Dcontext(init['context'], $this),
			identifier: new Cvariable.Didentifier(init['identifier'], $this),
			let_declarations: new Cvariable.Dlet_declarations(init['let declarations'], $this),
			more_variables: new Cvariable.Dmore_variables(init['more variables'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tanonymous__identifier = {
};
export class Canonymous__identifier extends AlanNode {
	constructor(init:Tanonymous__identifier, public parent:Cvariable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'identifier'?'anonymous'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnamed__identifier = {
	'name':string;
	'named values':Record<string, Tnamed_values>;
};
export class Cnamed__identifier extends AlanNode {
	public readonly properties:{
		readonly name:Cnamed__identifier.Dname,
		readonly named_values:Cnamed__identifier.Dnamed_values
	};
	constructor(init:Tnamed__identifier, public parent:Cvariable) {
		super();
		const $this = this;
		this.properties = {
			name: new Cnamed__identifier.Dname(init['name'], $this),
			named_values: new Cnamed__identifier.Dnamed_values(init['named values'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'identifier'?'named'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnamed_values = {
	'has successor':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cnamed_values extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_successor:Cnamed_values.Dhas_successor
	};
	constructor(key:string, init:Tnamed_values, public parent:Cnamed__identifier) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_successor: new Cnamed_values.Dhas_successor(init['has successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'named values'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor = {
};
export class Cno__has_successor extends AlanNode {
	constructor(init:Tno__has_successor, public parent:Cnamed_values) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor = {
};
export class Cyes__has_successor extends AlanNode {
	constructor(init:Tyes__has_successor, public parent:Cnamed_values) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__more_variables = {
};
export class Cno__more_variables extends AlanNode {
	public readonly output:{
		context: () => widget.Cwidget_implementation_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.definitions.widget_implementation_frame)
			.then(context => context?.definitions.widget_implementation_context).result!)
	}
	constructor(init:Tno__more_variables, public parent:Cvariable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'more variables'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__more_variables = {
	'variable':Tvariable;
};
export class Cyes__more_variables extends AlanNode {
	public readonly properties:{
		readonly variable:Cvariable
	};
	public readonly output:{
		context: () => widget.Cwidget_implementation_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.variable)
			.then(context => context?.component_root.output.widget_implementation_context()).result!)
	}
	constructor(init:Tyes__more_variables, public parent:Cvariable) {
		super();
		const $this = this;
		this.properties = {
			variable: new Cyes__more_variables.Dvariable(init['variable'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'more variables'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vupper_bound_cardinality = { name: 'many', definition: (typeof Cupper_bound_cardinality.Pmany), widening_index: 0}|{ name: 'one', definition: (typeof Cupper_bound_cardinality.Pone), widening_index: 1}
export class Cupper_bound_cardinality extends AlanStruct {
	public static Pmany:Cupper_bound_cardinality = new class PrimitiveInstance extends Cupper_bound_cardinality {
		constructor () {
			super({name: 'many', definition: undefined as unknown as Cupper_bound_cardinality, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pone:Cupper_bound_cardinality = new class PrimitiveInstance extends Cupper_bound_cardinality {
		constructor () {
			super({name: 'one', definition: undefined as unknown as Cupper_bound_cardinality, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vupper_bound_cardinality) { super(); }
	public cast<K extends Vupper_bound_cardinality['name']>(_variant:K):Extract<Vupper_bound_cardinality, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vupper_bound_cardinality['name']]:(($:Extract<Vupper_bound_cardinality, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'upper bound cardinality'`; }
	public is(other:Cupper_bound_cardinality):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cmany = widget.Cupper_bound_cardinality;
export import Cone__upper_bound_cardinality = widget.Cupper_bound_cardinality;
type Vtarget = { name: 'select state', definition: control_library.Cstate_group__type__property__type__attributes__control_node}|{ name: 'create node', definition: control_library.Ccontrol_node}|{ name: 'scalar', definition: widget.Cscalar_target}|{ name: 'instruction attribute', definition: control_library.Cinstruction}|{ name: 'collection sort', definition: widget.Ccollection_sort_location}|{ name: 'control', definition: (typeof Ctarget.Pcontrol)}
export class Ctarget extends AlanStruct {
	public static Pcontrol:Ctarget = new class PrimitiveInstance extends Ctarget {
		constructor () {
			super({name: 'control', definition: undefined as unknown as Ctarget})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vtarget) { super(); }
	public cast<K extends Vtarget['name']>(_variant:K):Extract<Vtarget, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vtarget['name']]:(($:Extract<Vtarget, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'target'`; }
	public is(other:Ctarget):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Ccontrol__target = widget.Ctarget;
type Vscalar_target = { name: 'number', definition: widget.Cnumber_target}|{ name: 'text', definition: widget.Ctext_target}
export class Cscalar_target extends AlanStruct {
	constructor(
		public readonly variant:Vscalar_target) { super(); }
	public definitions:{
		target: Ctarget;
	} = {
		target: new Ctarget({name:'scalar', definition: this})
	}
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => {
				switch (context?.variant.name) {
					case 'number': {
						return resolve(context)
							.then(() => this)
							.then(() => widget.Cscalar_value.Pnumber).result;
					}
					case 'text': {
						return resolve(context)
							.then(() => this)
							.then(() => widget.Cscalar_value.Ptext).result;
					}
					case undefined: return undefined;
					default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
				};
			}).result!)
	};
	public cast<K extends Vscalar_target['name']>(_variant:K):Extract<Vscalar_target, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vscalar_target['name']]:(($:Extract<Vscalar_target, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'scalar target'`; }
	public is(other:Cscalar_target):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vtext_target = { name: 'other', definition: (typeof Ctext_target.Pother)}|{ name: 'event', definition: control_library.Cinstruction}
export class Ctext_target extends AlanStruct {
	public static Pother:Ctext_target = new class PrimitiveInstance extends Ctext_target {
		constructor () {
			super({name: 'other', definition: undefined as unknown as Ctext_target})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vtext_target) { super(); }
	public definitions:{
		scalar_target: Cscalar_target;
	} = {
		scalar_target: new Cscalar_target({name:'text', definition: this})
	}
	public cast<K extends Vtext_target['name']>(_variant:K):Extract<Vtext_target, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vtext_target['name']]:(($:Extract<Vtext_target, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'text target'`; }
	public is(other:Ctext_target):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cother__text_target = widget.Ctext_target;
export type Ttext_argument = {
	'expression':Texpression;
};
export class Ctext_argument extends AlanNode {
	public readonly properties:{
		readonly expression:Cexpression
	};
	constructor(init:Ttext_argument, public location:AlanNode, public input: {
		control_instruction: () => control_library.Cinstruction,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Ctext_argument.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vscalar_value = { name: 'text', definition: (typeof Cscalar_value.Ptext), widening_index: 0}|{ name: 'number', definition: (typeof Cscalar_value.Pnumber), widening_index: 1}
export class Cscalar_value extends AlanStruct {
	public static Ptext:Cscalar_value = new class PrimitiveInstance extends Cscalar_value {
		constructor () {
			super({name: 'text', definition: undefined as unknown as Cscalar_value, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pnumber:Cscalar_value = new class PrimitiveInstance extends Cscalar_value {
		constructor () {
			super({name: 'number', definition: undefined as unknown as Cscalar_value, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vscalar_value) { super(); }
	public cast<K extends Vscalar_value['name']>(_variant:K):Extract<Vscalar_value, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vscalar_value['name']]:(($:Extract<Vscalar_value, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'scalar value'`; }
	public is(other:Cscalar_value):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnumber__scalar_value = widget.Cscalar_value;
export import Ctext__scalar_value = widget.Cscalar_value;
export type Tscalar__widget = {
	'binding type':['binding', Tbinding__binding_type]|['configuration', Tconfiguration__binding_type]|['current time', Tcurrent_time]|['event', Tevent]|['list expression', Tlist_expression]|['list index', Tlist_index]|['phrase', Tphrase]|['static number', Tstatic_number]|['static text', Tstatic_text]|['unary expression', Tunary_expression];
	'format':'no'|['no', {}]|['yes', Tyes__format];
	'transform':'no'|['no', {}]|['yes', Tyes__transform];
};
export class Cscalar__widget extends AlanNode {
	public readonly properties:{
		readonly binding_type:Cscalar__widget.Dbinding_type,
		readonly format:Cscalar__widget.Dformat,
		readonly transform:Cscalar__widget.Dtransform
	};
	constructor(init:Tscalar__widget, public location:AlanNode, public input: {
		target: () => widget.Cscalar_target,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			binding_type: new Cscalar__widget.Dbinding_type(init['binding type'], $this),
			format: new Cscalar__widget.Dformat(init['format'], $this),
			transform: new Cscalar__widget.Dtransform(init['transform'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tbinding__binding_type = {
	'context':Tcontext_selection;
	'type':'key'|['key', {}]|['property', Tproperty];
};
export class Cbinding__binding_type extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection & { readonly inferences: {
			node: () => client_bindings.Cnode__client_bindings;
		} },
		readonly type:Cbinding__binding_type.Dtype
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value()).result!)
	}
	constructor(init:Tbinding__binding_type, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			context: new Cbinding__binding_type.Dcontext(init['context'], $this),
			type: new Cbinding__binding_type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tkey = {
};
export class Ckey extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Ptext).result!)
	}
	public readonly inferences:{
		collection: () => client_bindings.Centry_key
	} = {
		collection: cache((detach:boolean) => {
			const widget__scalar__binding_type__binding__type__key_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.context?.inferences.node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.key())
				.then(context => context?.variant.name === 'entry' ? context.variant.definition as client_bindings.Centry_key : undefined).result!;
		})

	}
	constructor(init:Tkey, public parent:Cbinding__binding_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'key'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty = {
	'property':string;
	'type':'number'|['number', {}]|'reference'|['reference', {}]|'text'|['text', {}];
};
export class Cproperty extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty.Dproperty,
		readonly type:Cproperty.Dtype
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value()).result!)
	}
	constructor(init:Tproperty, public parent:Cbinding__binding_type) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty.Dproperty(init['property'], $this),
			type: new Cproperty.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property = {
};
export class Cnumber__type__property extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	public readonly inferences:{
		number_type: () => client_bindings.Cnumber__type__properties
	} = {
		number_type: cache((detach:boolean) => {
			const widget__scalar__binding_type__binding__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as client_bindings.Cnumber__type__properties).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property, public parent:Cproperty) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference = {
};
export class Creference extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Ptext).result!)
	}
	public readonly inferences:{
		reference_type: () => client_bindings.Creference
	} = {
		reference_type: cache((detach:boolean) => {
			const widget__scalar__binding_type__binding__type__property__type__reference_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'reference') {
						return resolve(context.properties.type.state.node as client_bindings.Creference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Treference, public parent:Cproperty) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__property = {
};
export class Ctext__type__property extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Ptext).result!)
	}
	public readonly inferences:{
		text_type: () => client_bindings.Ctext__type__properties
	} = {
		text_type: cache((detach:boolean) => {
			const widget__scalar__binding_type__binding__type__property__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as client_bindings.Ctext__type__properties).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__property, public parent:Cproperty) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconfiguration__binding_type = {
	'attribute':string;
	'context':Tcontext_selection;
	'type':'number'|['number', {}]|'text'|['text', {}];
};
export class Cconfiguration__binding_type extends AlanNode {
	public readonly properties:{
		readonly attribute:Cconfiguration__binding_type.Dattribute,
		readonly context:Ccontext_selection,
		readonly type:Cconfiguration__binding_type.Dtype
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value()).result!)
	}
	constructor(init:Tconfiguration__binding_type, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cconfiguration__binding_type.Dattribute(init['attribute'], $this),
			context: new Cconfiguration__binding_type.Dcontext(init['context'], $this),
			type: new Cconfiguration__binding_type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__configuration__binding_type = {
};
export class Cnumber__type__configuration__binding_type extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	public readonly inferences:{
		number_type: () => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node
	} = {
		number_type: cache((detach:boolean) => {
			const widget__scalar__binding_type__configuration__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as widget.Cnumber__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__configuration__binding_type, public parent:Cconfiguration__binding_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__configuration__binding_type = {
};
export class Ctext__type__configuration__binding_type extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Ptext).result!)
	}
	public readonly inferences:{
		text_type: () => widget.Ctext__type__configuration__type__attributes__widget_configuration_node
	} = {
		text_type: cache((detach:boolean) => {
			const widget__scalar__binding_type__configuration__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as widget.Ctext__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__configuration__binding_type, public parent:Cconfiguration__binding_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent_time = {
	'throttle':'no'|['no', {}]|['yes', Tyes__throttle];
};
export class Ccurrent_time extends AlanNode {
	public readonly properties:{
		readonly throttle:Ccurrent_time.Dthrottle
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	constructor(init:Tcurrent_time, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			throttle: new Ccurrent_time.Dthrottle(init['throttle'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'current time'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__throttle = {
};
export class Cno__throttle extends AlanNode {
	constructor(init:Tno__throttle, public parent:Ccurrent_time) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'throttle'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__throttle = {
	'interval':number;
};
export class Cyes__throttle extends AlanNode {
	public readonly properties:{
		readonly interval:Cyes__throttle.Dinterval
	};
	constructor(init:Tyes__throttle, public parent:Ccurrent_time) {
		super();
		const $this = this;
		this.properties = {
			interval: new Cyes__throttle.Dinterval(init['interval'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'throttle'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tevent = {
	'type':'number'|['number', {}]|'text'|['text', {}];
};
export class Cevent extends AlanNode {
	public readonly properties:{
		readonly type:Cevent.Dtype
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.value()).result!)
	}
	public readonly inferences:{
		event: () => control_library.Cinstruction
	} = {
		event: cache((detach:boolean) => {
			const widget__scalar__binding_type__event_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => {
					switch (context?.variant.name) {
						case 'number': {
							const widget__scalar__binding_type__event_inf___event___number_nval = context.cast('number');
							return resolve(context)
								.then(context => widget__scalar__binding_type__event_inf___event___number_nval)
								.then(context => context?.variant.name === 'event' ? context.variant.definition as control_library.Cinstruction : undefined).result;
						}
						case 'text': {
							const widget__scalar__binding_type__event_inf___event___text_nval = context.cast('text');
							return resolve(context)
								.then(context => widget__scalar__binding_type__event_inf___event___text_nval)
								.then(context => context?.variant.name === 'event' ? context.variant.definition as control_library.Cinstruction : undefined).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!;
		})

	}
	constructor(init:Tevent, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			type: new Cevent.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'event'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__event = {
};
export class Cnumber__type__event extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	public readonly inferences:{
		number_type: () => control_library.Cnumber__argument
	} = {
		number_type: cache((detach:boolean) => {
			const widget__scalar__binding_type__event__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.event())
				.then(context => {
					if (context?.properties.argument.state.name === 'number') {
						return resolve(context.properties.argument.state.node as control_library.Cnumber__argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__event, public parent:Cevent) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__event = {
};
export class Ctext__type__event extends AlanNode {
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Ptext).result!)
	}
	public readonly inferences:{
		text_type: () => control_library.Ctext__argument
	} = {
		text_type: cache((detach:boolean) => {
			const widget__scalar__binding_type__event__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.event())
				.then(context => {
					if (context?.properties.argument.state.name === 'text') {
						return resolve(context.properties.argument.state.node as control_library.Ctext__argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__event, public parent:Cevent) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist_expression = {
	'operation':'maximum'|['maximum', {}]|'minimum'|['minimum', {}]|'product'|['product', {}]|'sum'|['sum', {}];
	'value':Tone_or_more_expression;
};
export class Clist_expression extends AlanNode {
	public readonly properties:{
		readonly operation:Clist_expression.Doperation,
		readonly value:Cone_or_more_expression
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	constructor(init:Tlist_expression, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			operation: new Clist_expression.Doperation(init['operation'], $this),
			value: new Clist_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'list expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmaximum = {
};
export class Cmaximum extends AlanNode {
	constructor(init:Tmaximum, public parent:Clist_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'maximum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminimum = {
};
export class Cminimum extends AlanNode {
	constructor(init:Tminimum, public parent:Clist_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'minimum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduct = {
};
export class Cproduct extends AlanNode {
	constructor(init:Tproduct, public parent:Clist_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'product'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsum = {
};
export class Csum extends AlanNode {
	constructor(init:Tsum, public parent:Clist_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'sum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist_index = {
	'context':Tcontext_selection;
};
export class Clist_index extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection & { readonly inferences: {
			list: () => widget.Clist__type;
		} }
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	constructor(init:Tlist_index, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			context: new Clist_index.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'list index'`; }
	public get entity() { return this.parent.entity; }
}
export type Tphrase = {
	'phrase':string;
};
export class Cphrase extends AlanNode {
	public readonly properties:{
		readonly phrase:Cphrase.Dphrase
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Ptext).result!)
	}
	constructor(init:Tphrase, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			phrase: new Cphrase.Dphrase(init['phrase'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'phrase'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic_number = {
	'value':number;
};
export class Cstatic_number extends AlanNode {
	public readonly properties:{
		readonly value:Cstatic_number.Dvalue
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	constructor(init:Tstatic_number, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			value: new Cstatic_number.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'static number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic_text = {
	'text':string;
};
export class Cstatic_text extends AlanNode {
	public readonly properties:{
		readonly text:string
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Ptext).result!)
	}
	constructor(init:Tstatic_text, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			text: init['text']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'static text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunary_expression = {
	'type':'absolute value'|['absolute value', {}]|'sign inversion'|['sign inversion', {}];
	'value':Tscalar__widget;
};
export class Cunary_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cunary_expression.Dtype,
		readonly value:Cscalar__widget
	};
	public readonly output:{
		value: () => widget.Cscalar_value;
	} = {
		value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	constructor(init:Tunary_expression, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			type: new Cunary_expression.Dtype(init['type'], $this),
			value: new Cunary_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'unary expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tabsolute_value = {
};
export class Cabsolute_value extends AlanNode {
	constructor(init:Tabsolute_value, public parent:Cunary_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'absolute value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsign_inversion = {
};
export class Csign_inversion extends AlanNode {
	constructor(init:Tsign_inversion, public parent:Cunary_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sign inversion'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__format = {
};
export class Cno__format extends AlanNode {
	constructor(init:Tno__format, public parent:Cscalar__widget) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'format'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__format = {
	'formatter':string;
};
export class Cyes__format extends AlanNode {
	public readonly properties:{
		readonly formatter:Cyes__format.Dformatter
	};
	public readonly inferences:{
		expected_target_type: () => widget.Cscalar_value
	} = {
		expected_target_type: cache((detach:boolean) => {
			const widget__scalar__format__yes_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.value()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(() => widget.Cscalar_value.Ptext).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tyes__format, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			formatter: new Cyes__format.Dformatter(init['formatter'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'format'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__transform = {
};
export class Cno__transform extends AlanNode {
	public readonly output:{
		transformed_value: () => widget.Cscalar_value;
	} = {
		transformed_value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.binding_type?.state.node.output.value()).result!)
	}
	public readonly inferences:{
		expected_value_type: () => widget.Cscalar_value
	} = {
		expected_value_type: cache((detach:boolean) => {
			const widget__scalar__transform__no_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.properties.binding_type?.state.node.output.value()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.target())
						.then(context => context?.component_root.output.value()).result;
					return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tno__transform, public parent:Cscalar__widget) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'transform'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__transform = {
	'transformer':string;
};
export class Cyes__transform extends AlanNode {
	public readonly properties:{
		readonly transformer:Cyes__transform.Dtransformer
	};
	public readonly output:{
		transformed_value: () => widget.Cscalar_value;
	} = {
		transformed_value: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => widget.Cscalar_value.Pnumber).result!)
	}
	constructor(init:Tyes__transform, public parent:Cscalar__widget) {
		super();
		const $this = this;
		this.properties = {
			transformer: new Cyes__transform.Dtransformer(init['transformer'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'transform'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Toptional_state_HACK = {
};
export class Coptional_state_HACK extends AlanNode {
	constructor(init:Toptional_state_HACK, public location:AlanNode, private key: string) {
		super();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Toptional_context_definition = {
	'define context':'no'|['no', {}]|['yes', Tyes__define_context];
};
export class Coptional_context_definition extends AlanNode {
	public readonly properties:{
		readonly define_context:Coptional_context_definition.Ddefine_context
	};
	public readonly output:{
		widget_implementation_context: () => widget.Cwidget_implementation_context;
	} = {
		widget_implementation_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.define_context?.state.node.output.widget_context()).result!)
	};
	constructor(init:Toptional_context_definition, public location:AlanNode, public input: {
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			define_context: new Coptional_context_definition.Ddefine_context(init['define context'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__define_context = {
};
export class Cno__define_context extends AlanNode {
	public readonly output:{
		widget_context: () => widget.Cwidget_implementation_context;
	} = {
		widget_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.widget_implementation_context()).result!)
	}
	constructor(init:Tno__define_context, public parent:Coptional_context_definition) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'define context'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__define_context = {
	'variable':Tvariable;
};
export class Cyes__define_context extends AlanNode {
	public readonly properties:{
		readonly variable:Cvariable
	};
	public readonly output:{
		widget_context: () => widget.Cwidget_implementation_context;
	} = {
		widget_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.variable)
			.then(context => context?.component_root.output.widget_implementation_context()).result!)
	}
	constructor(init:Tyes__define_context, public parent:Coptional_context_definition) {
		super();
		const $this = this;
		this.properties = {
			variable: new Cyes__define_context.Dvariable(init['variable'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'define context'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tone_or_more_expression = {
	'multiplicity':['plural', Tplural]|['singular', Tsingular];
};
export class Cone_or_more_expression extends AlanNode {
	public readonly properties:{
		readonly multiplicity:Cone_or_more_expression.Dmultiplicity
	};
	constructor(init:Tone_or_more_expression, public location:AlanNode, public input: {
		bounds: () => widget.Cbounds,
		target: () => widget.Ctarget,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			multiplicity: new Cone_or_more_expression.Dmultiplicity(init['multiplicity'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tplural = {
	'expression':Texpression_list;
};
export class Cplural extends AlanNode {
	public readonly properties:{
		readonly expression:Cexpression_list
	};
	public readonly inferences:{
		bounds: () => widget.Cupper_bound_cardinality
	} = {
		bounds: cache((detach:boolean) => {
			const widget__one_or_more_expression__multiplicity__plural_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.bounds())
						.then(context => context?.properties.upper_bound_cardinality()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(() => widget.Cupper_bound_cardinality.Pmany).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tplural, public parent:Cone_or_more_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cplural.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'multiplicity'?'plural'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsingular = {
	'expression':Texpression;
};
export class Csingular extends AlanNode {
	public readonly properties:{
		readonly expression:Cexpression
	};
	constructor(init:Tsingular, public parent:Cone_or_more_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Csingular.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'multiplicity'?'singular'`; }
	public get entity() { return this.parent.entity; }
}
type Vnumber_target = { name: 'other', definition: (typeof Cnumber_target.Pother)}|{ name: 'event', definition: control_library.Cinstruction}
export class Cnumber_target extends AlanStruct {
	public static Pother:Cnumber_target = new class PrimitiveInstance extends Cnumber_target {
		constructor () {
			super({name: 'other', definition: undefined as unknown as Cnumber_target})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnumber_target) { super(); }
	public definitions:{
		scalar_target: Cscalar_target;
	} = {
		scalar_target: new Cscalar_target({name:'number', definition: this})
	}
	public cast<K extends Vnumber_target['name']>(_variant:K):Extract<Vnumber_target, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnumber_target['name']]:(($:Extract<Vnumber_target, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'number target'`; }
	public is(other:Cnumber_target):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cother__number_target = widget.Cnumber_target;
export type Tnumber_argument = {
	'expression':Texpression;
};
export class Cnumber_argument extends AlanNode {
	public readonly properties:{
		readonly expression:Cexpression
	};
	constructor(init:Tnumber_argument, public location:AlanNode, public input: {
		control_instruction: () => control_library.Cinstruction,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnumber_argument.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
type Vlower_bound_cardinality = { name: 'zero', definition: (typeof Clower_bound_cardinality.Pzero), widening_index: 0}|{ name: 'one', definition: (typeof Clower_bound_cardinality.Pone), widening_index: 1}
export class Clower_bound_cardinality extends AlanStruct {
	public static Pzero:Clower_bound_cardinality = new class PrimitiveInstance extends Clower_bound_cardinality {
		constructor () {
			super({name: 'zero', definition: undefined as unknown as Clower_bound_cardinality, widening_index: 0})
			this.variant.definition = this;
		}
	}
	public static Pone:Clower_bound_cardinality = new class PrimitiveInstance extends Clower_bound_cardinality {
		constructor () {
			super({name: 'one', definition: undefined as unknown as Clower_bound_cardinality, widening_index: 1})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vlower_bound_cardinality) { super(); }
	public cast<K extends Vlower_bound_cardinality['name']>(_variant:K):Extract<Vlower_bound_cardinality, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vlower_bound_cardinality['name']]:(($:Extract<Vlower_bound_cardinality, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'lower bound cardinality'`; }
	public is(other:Clower_bound_cardinality):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cone__lower_bound_cardinality = widget.Clower_bound_cardinality;
export import Czero = widget.Clower_bound_cardinality;
export type Tlet_declarations__widget = {
	'let declarations':Record<string, Tlet_declarations__let_declarations>;
};
export class Clet_declarations__widget extends AlanNode {
	public readonly properties:{
		readonly let_declarations:Clet_declarations__widget.Dlet_declarations
	};
	constructor(init:Tlet_declarations__widget, public location:AlanNode, public input: {
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			let_declarations: new Clet_declarations__widget.Dlet_declarations(init['let declarations'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tlet_declarations__let_declarations = {
	'expression':Texpression;
};
export class Clet_declarations__let_declarations extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly expression:Cexpression
	};
	constructor(key:string, init:Tlet_declarations__let_declarations, public parent:Clet_declarations__widget) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			expression: new Clet_declarations__let_declarations.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'let declarations'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tinstruction_selection = {
	'configuration attribute type':['binding', Tbinding__configuration_attribute_type]|['configuration', Tconfiguration__configuration_attribute_type];
	'context':Tcontext_selection;
};
export class Cinstruction_selection extends AlanNode {
	public readonly properties:{
		readonly configuration_attribute_type:Cinstruction_selection.Dconfiguration_attribute_type,
		readonly context:Ccontext_selection
	};
	constructor(init:Tinstruction_selection, public location:AlanNode, public input: {
		control_instruction: () => control_library.Cinstruction,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			configuration_attribute_type: new Cinstruction_selection.Dconfiguration_attribute_type(init['configuration attribute type'], $this),
			context: new Cinstruction_selection.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tbinding__configuration_attribute_type = {
	'has arguments':'no'|['no', {}]|['yes', Tyes__has_arguments];
	'instruction':string;
};
export class Cbinding__configuration_attribute_type extends AlanNode {
	public readonly properties:{
		readonly has_arguments:Cbinding__configuration_attribute_type.Dhas_arguments,
		readonly instruction:Cbinding__configuration_attribute_type.Dinstruction
	};
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__instruction_selection__configuration_attribute_type__binding_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_binding_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Tbinding__configuration_attribute_type, public parent:Cinstruction_selection) {
		super();
		const $this = this;
		this.properties = {
			has_arguments: new Cbinding__configuration_attribute_type.Dhas_arguments(init['has arguments'], $this),
			instruction: new Cbinding__configuration_attribute_type.Dinstruction(init['instruction'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'configuration attribute type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_arguments = {
};
export class Cno__has_arguments extends AlanNode {
	public readonly inferences:{
		has_no_arguments: () => client_bindings.Cno__has_arguments
	} = {
		has_no_arguments: cache((detach:boolean) => {
			const widget__instruction_selection__configuration_attribute_type__binding__has_arguments__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.instruction?.ref)
				.then(context => {
					if (context?.properties.has_arguments.state.name === 'no') {
						return resolve(context.properties.has_arguments.state.node as client_bindings.Cno__has_arguments).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__has_arguments, public parent:Cbinding__configuration_attribute_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has arguments'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_arguments = {
	'first argument':Tinstruction_argument;
};
export class Cyes__has_arguments extends AlanNode {
	public readonly properties:{
		readonly first_argument:Cinstruction_argument
	};
	public readonly inferences:{
		has_arguments: () => client_bindings.Cyes__has_arguments
	} = {
		has_arguments: cache((detach:boolean) => {
			const widget__instruction_selection__configuration_attribute_type__binding__has_arguments__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.instruction?.ref)
				.then(context => {
					if (context?.properties.has_arguments.state.name === 'yes') {
						return resolve(context.properties.has_arguments.state.node as client_bindings.Cyes__has_arguments).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_arguments, public parent:Cbinding__configuration_attribute_type) {
		super();
		const $this = this;
		this.properties = {
			first_argument: new Cyes__has_arguments.Dfirst_argument(init['first argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has arguments'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconfiguration__configuration_attribute_type = {
	'instruction':['set number', Tset_number]|['set state', Tset_state]|['set text', Tset_text];
};
export class Cconfiguration__configuration_attribute_type extends AlanNode {
	public readonly properties:{
		readonly instruction:Cconfiguration__configuration_attribute_type.Dinstruction
	};
	constructor(init:Tconfiguration__configuration_attribute_type, public parent:Cinstruction_selection) {
		super();
		const $this = this;
		this.properties = {
			instruction: new Cconfiguration__configuration_attribute_type.Dinstruction(init['instruction'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'configuration attribute type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset_number = {
	'argument':Tnumber_argument;
	'number':string;
};
export class Cset_number extends AlanNode {
	public readonly properties:{
		readonly argument:Cnumber_argument,
		readonly number:Cset_number.Dnumber
	};
	constructor(init:Tset_number, public parent:Cconfiguration__configuration_attribute_type) {
		super();
		const $this = this;
		this.properties = {
			argument: new Cset_number.Dargument(init['argument'], $this),
			number: new Cset_number.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'instruction'?'set number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset_state = {
	'node':Tinstruction_argument_configuration_node;
	'state':string;
	'state group':string;
};
export class Cset_state extends AlanNode {
	public readonly properties:{
		readonly node:Cinstruction_argument_configuration_node,
		readonly state:Cset_state.Dstate,
		readonly state_group:Cset_state.Dstate_group
	};
	constructor(init:Tset_state, public parent:Cconfiguration__configuration_attribute_type) {
		super();
		const $this = this;
		this.properties = {
			node: new Cset_state.Dnode(init['node'], $this),
			state: new Cset_state.Dstate(init['state'], $this),
			state_group: new Cset_state.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'instruction'?'set state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset_text = {
	'argument':Ttext_argument;
	'text':string;
};
export class Cset_text extends AlanNode {
	public readonly properties:{
		readonly argument:Ctext_argument,
		readonly text:Cset_text.Dtext
	};
	constructor(init:Tset_text, public parent:Cconfiguration__configuration_attribute_type) {
		super();
		const $this = this;
		this.properties = {
			argument: new Cset_text.Dargument(init['argument'], $this),
			text: new Cset_text.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'instruction'?'set text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinstruction_argument_configuration_node = {
	'attributes':Record<string, Tattributes__instruction_argument_configuration_node>;
};
export class Cinstruction_argument_configuration_node extends AlanNode {
	public readonly properties:{
		readonly attributes:Cinstruction_argument_configuration_node.Dattributes
	};
	constructor(init:Tinstruction_argument_configuration_node, public location:AlanNode, public input: {
		control_instruction: () => control_library.Cinstruction,
		widget_configuration_node: () => widget.Cwidget_configuration_node,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cinstruction_argument_configuration_node.Dattributes(init['attributes'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kattributes__instruction_argument_configuration_node extends Reference<widget.Cattributes__widget_configuration_node, string> {
	constructor(key:string, $this:Cattributes__instruction_argument_configuration_node) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.widget_configuration_node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'::key`; }
}
export type Tattributes__instruction_argument_configuration_node = {
	'type':['configuration', Tconfiguration__type__attributes__instruction_argument_configuration_node];
};
export class Cattributes__instruction_argument_configuration_node extends AlanDictionaryEntry {
	public key:Kattributes__instruction_argument_configuration_node;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Cattributes__instruction_argument_configuration_node.Dtype
	};
	constructor(key:string, init:Tattributes__instruction_argument_configuration_node, public parent:Cinstruction_argument_configuration_node) {
		super();
		const $this = this;
		this.key = new Kattributes__instruction_argument_configuration_node(key, $this);
		this.properties = {
			type: new Cattributes__instruction_argument_configuration_node.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tconfiguration__type__attributes__instruction_argument_configuration_node = {
	'type':['number', Tnumber__type__configuration__type__attributes__instruction_argument_configuration_node]|['state group', Tstate_group__type__configuration__type__attributes__instruction_argument_configuration_node]|['text', Ttext__type__configuration__type__attributes__instruction_argument_configuration_node];
};
export class Cconfiguration__type__attributes__instruction_argument_configuration_node extends AlanNode {
	public readonly properties:{
		readonly type:Cconfiguration__type__attributes__instruction_argument_configuration_node.Dtype
	};
	public readonly inferences:{
		configuration_type: () => widget.Cconfiguration__type__attributes__widget_configuration_node
	} = {
		configuration_type: cache((detach:boolean) => {
			const widget__instruction_argument_configuration_node__attributes__type__configuration_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'configuration') {
						return resolve(context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tconfiguration__type__attributes__instruction_argument_configuration_node, public parent:Cattributes__instruction_argument_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			type: new Cconfiguration__type__attributes__instruction_argument_configuration_node.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__configuration__type__attributes__instruction_argument_configuration_node = {
	'argument':Tnumber_argument;
};
export class Cnumber__type__configuration__type__attributes__instruction_argument_configuration_node extends AlanNode {
	public readonly properties:{
		readonly argument:Cnumber_argument
	};
	public readonly inferences:{
		number_type: () => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node
	} = {
		number_type: cache((detach:boolean) => {
			const widget__instruction_argument_configuration_node__attributes__type__configuration__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration_type())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as widget.Cnumber__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__configuration__type__attributes__instruction_argument_configuration_node, public parent:Cconfiguration__type__attributes__instruction_argument_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			argument: new Cnumber__type__configuration__type__attributes__instruction_argument_configuration_node.Dargument(init['argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__configuration__type__attributes__instruction_argument_configuration_node = {
	'node':Tinstruction_argument_configuration_node;
	'state':string;
};
export class Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node extends AlanNode {
	public readonly properties:{
		readonly node:Cinstruction_argument_configuration_node,
		readonly state:Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node.Dstate
	};
	public readonly inferences:{
		state_group_type: () => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node
	} = {
		state_group_type: cache((detach:boolean) => {
			const widget__instruction_argument_configuration_node__attributes__type__configuration__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration_type())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__configuration__type__attributes__instruction_argument_configuration_node, public parent:Cconfiguration__type__attributes__instruction_argument_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			node: new Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node.Dnode(init['node'], $this),
			state: new Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__configuration__type__attributes__instruction_argument_configuration_node = {
	'argument':Ttext_argument;
};
export class Ctext__type__configuration__type__attributes__instruction_argument_configuration_node extends AlanNode {
	public readonly properties:{
		readonly argument:Ctext_argument
	};
	public readonly inferences:{
		text_type: () => widget.Ctext__type__configuration__type__attributes__widget_configuration_node
	} = {
		text_type: cache((detach:boolean) => {
			const widget__instruction_argument_configuration_node__attributes__type__configuration__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration_type())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as widget.Ctext__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__configuration__type__attributes__instruction_argument_configuration_node, public parent:Cconfiguration__type__attributes__instruction_argument_configuration_node) {
		super();
		const $this = this;
		this.properties = {
			argument: new Ctext__type__configuration__type__attributes__instruction_argument_configuration_node.Dargument(init['argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinstruction_argument = {
	'next argument':['exists', Texists]|'no'|['no', {}];
	'type':'file'|['file', {}]|['number', Tnumber__type__instruction_argument]|['text', Ttext__type__instruction_argument]|['view', Tview__type__instruction_argument];
};
export class Cinstruction_argument extends AlanNode {
	public readonly properties:{
		readonly next_argument:Cinstruction_argument.Dnext_argument,
		readonly type:Cinstruction_argument.Dtype
	};
	constructor(init:Tinstruction_argument, public location:AlanNode, public input: {
		client_bindings_argument: () => client_bindings.Cinstruction_argument,
		control_instruction: () => control_library.Cinstruction,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			next_argument: new Cinstruction_argument.Dnext_argument(init['next argument'], $this),
			type: new Cinstruction_argument.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Texists = {
	'argument':Tinstruction_argument;
};
export class Cexists extends AlanNode {
	public readonly properties:{
		readonly argument:Cinstruction_argument
	};
	public readonly inferences:{
		next_argument_exists: () => client_bindings.Cexists
	} = {
		next_argument_exists: cache((detach:boolean) => {
			const widget__instruction_argument__next_argument__exists_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.client_bindings_argument())
				.then(context => {
					if (context?.properties.next_argument.state.name === 'exists') {
						return resolve(context.properties.next_argument.state.node as client_bindings.Cexists).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texists, public parent:Cinstruction_argument) {
		super();
		const $this = this;
		this.properties = {
			argument: new Cexists.Dargument(init['argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'next argument'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__next_argument = {
};
export class Cno__next_argument extends AlanNode {
	public readonly inferences:{
		no_next_argument: () => client_bindings.Cno__next_argument
	} = {
		no_next_argument: cache((detach:boolean) => {
			const widget__instruction_argument__next_argument__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.client_bindings_argument())
				.then(context => {
					if (context?.properties.next_argument.state.name === 'no') {
						return resolve(context.properties.next_argument.state.node as client_bindings.Cno__next_argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__next_argument, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'next argument'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile = {
};
export class Cfile extends AlanNode {
	public readonly inferences:{
		control_file: () => control_library.Cfile,
		file: () => client_bindings.Cfile__type__instruction_argument
	} = {
		control_file: cache((detach:boolean) => {
			const widget__instruction_argument__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.control_instruction())
				.then(context => {
					if (context?.properties.argument.state.name === 'file') {
						return resolve(context.properties.argument.state.node as control_library.Cfile).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		file: cache((detach:boolean) => {
			const widget__instruction_argument__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.client_bindings_argument())
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as client_bindings.Cfile__type__instruction_argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile, public parent:Cinstruction_argument) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__instruction_argument = {
	'argument':Tnumber_argument;
};
export class Cnumber__type__instruction_argument extends AlanNode {
	public readonly properties:{
		readonly argument:Cnumber_argument
	};
	public readonly inferences:{
		number: () => client_bindings.Cnumber__type__instruction_argument
	} = {
		number: cache((detach:boolean) => {
			const widget__instruction_argument__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.client_bindings_argument())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as client_bindings.Cnumber__type__instruction_argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__instruction_argument, public parent:Cinstruction_argument) {
		super();
		const $this = this;
		this.properties = {
			argument: new Cnumber__type__instruction_argument.Dargument(init['argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__instruction_argument = {
	'argument':Ttext_argument;
};
export class Ctext__type__instruction_argument extends AlanNode {
	public readonly properties:{
		readonly argument:Ctext_argument
	};
	public readonly inferences:{
		text: () => client_bindings.Ctext__type__instruction_argument
	} = {
		text: cache((detach:boolean) => {
			const widget__instruction_argument__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.client_bindings_argument())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as client_bindings.Ctext__type__instruction_argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__instruction_argument, public parent:Cinstruction_argument) {
		super();
		const $this = this;
		this.properties = {
			argument: new Ctext__type__instruction_argument.Dargument(init['argument'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tview__type__instruction_argument = {
	'context':Tcontext_selection;
	'view configuration':string;
};
export class Cview__type__instruction_argument extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection,
		readonly view_configuration:Cview__type__instruction_argument.Dview_configuration
	};
	public readonly inferences:{
		view: () => client_bindings.Cview__type__instruction_argument
	} = {
		view: cache((detach:boolean) => {
			const widget__instruction_argument__type__view_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.client_bindings_argument())
				.then(context => {
					if (context?.properties.type.state.name === 'view') {
						return resolve(context.properties.type.state.node as client_bindings.Cview__type__instruction_argument).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tview__type__instruction_argument, public parent:Cinstruction_argument) {
		super();
		const $this = this;
		this.properties = {
			context: new Cview__type__instruction_argument.Dcontext(init['context'], $this),
			view_configuration: new Cview__type__instruction_argument.Dview_configuration(init['view configuration'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'view'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression_list = {
	'expression':Texpression;
	'has next':'no'|['no', {}]|['yes', Tyes__has_next];
	'optional context definition':Toptional_context_definition;
};
export class Cexpression_list extends AlanNode {
	public readonly properties:{
		readonly expression:Cexpression,
		readonly has_next:Cexpression_list.Dhas_next,
		readonly optional_context_definition:Coptional_context_definition
	};
	constructor(init:Texpression_list, public location:AlanNode, public input: {
		bounds: () => widget.Cbounds,
		target: () => widget.Ctarget,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cexpression_list.Dexpression(init['expression'], $this),
			has_next: new Cexpression_list.Dhas_next(init['has next'], $this),
			optional_context_definition: new Cexpression_list.Doptional_context_definition(init['optional context definition'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next = {
};
export class Cno__has_next extends AlanNode {
	constructor(init:Tno__has_next, public parent:Cexpression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next = {
	'next':Texpression_list;
};
export class Cyes__has_next extends AlanNode {
	public readonly properties:{
		readonly next:Cexpression_list
	};
	constructor(init:Tyes__has_next, public parent:Cexpression_list) {
		super();
		const $this = this;
		this.properties = {
			next: new Cyes__has_next.Dnext(init['next'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vcollection_sort_location = { name: 'collection', definition: widget.Cwidget_binding}
export class Ccollection_sort_location extends AlanStruct {
	constructor(
		public readonly variant:Vcollection_sort_location) { super(); }
	public definitions:{
		target: Ctarget;
	} = {
		target: new Ctarget({name:'collection sort', definition: this})
	}
	public cast<K extends Vcollection_sort_location['name']>(_variant:K):Extract<Vcollection_sort_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vcollection_sort_location['name']]:(($:Extract<Vcollection_sort_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'collection sort location'`; }
	public is(other:Ccollection_sort_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Texpression = {
	'type':['control', Tcontrol__type]|['create entry', Tcreate_entry]|['instruction', Tinstruction]|['match', Tmatch]|['node switch', Tnode_switch]|'none'|['none', {}]|['scalar', Tscalar__type]|['sort collection', Tsort_collection]|['switch', Tswitch]|['walk', Twalk];
};
export class Cexpression extends AlanNode {
	public readonly properties:{
		readonly type:Cexpression.Dtype
	};
	constructor(init:Texpression, public location:AlanNode, public input: {
		bounds: () => widget.Cbounds,
		target: () => widget.Ctarget,
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cexpression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tcontrol__type = {
	'control binding':Tcontrol_binding;
};
export class Ccontrol__type extends AlanNode {
	public readonly properties:{
		readonly control_binding:Ccontrol_binding
	};
	public readonly inferences:{
		control_target: () => widget.Ccontrol__target
	} = {
		control_target: cache((detach:boolean) => {
			const widget__expression__type__control_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'control' ? context.variant.definition as widget.Ccontrol__target : undefined).result!;
		})

	}
	constructor(init:Tcontrol__type, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			control_binding: new Ccontrol__type.Dcontrol_binding(init['control binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'control'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcreate_entry = {
	'for':'control node'|['control node', {}]|['state', Tstate__for];
	'node':Twidget_implementation_node;
};
export class Ccreate_entry extends AlanNode {
	public readonly properties:{
		readonly for_:Ccreate_entry.Dfor_,
		readonly node:Cwidget_implementation_node
	};
	constructor(init:Tcreate_entry, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			for_: new Ccreate_entry.Dfor_(init['for'], $this),
			node: new Ccreate_entry.Dnode(init['node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'create entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontrol_node = {
};
export class Ccontrol_node extends AlanNode {
	public readonly output:{
		control_node: () => control_library.Ccontrol_node;
	} = {
		control_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.create_node()).result!)
	}
	public readonly inferences:{
		create_node: () => control_library.Ccontrol_node
	} = {
		create_node: cache((detach:boolean) => {
			const widget__expression__type__create_entry__for__control_node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'create node' ? context.variant.definition as control_library.Ccontrol_node : undefined).result!;
		})

	}
	constructor(init:Tcontrol_node, public parent:Ccreate_entry) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'for'?'control node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__for = {
	'state':string;
};
export class Cstate__for extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__for.Dstate
	};
	public readonly output:{
		control_node: () => control_library.Ccontrol_node;
	} = {
		control_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		stategroup: () => control_library.Cstate_group__type__property__type__attributes__control_node
	} = {
		stategroup: cache((detach:boolean) => {
			const widget__expression__type__create_entry__for__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'select state' ? context.variant.definition as control_library.Cstate_group__type__property__type__attributes__control_node : undefined).result!;
		})

	}
	constructor(init:Tstate__for, public parent:Ccreate_entry) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__for.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'for'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinstruction = {
	'instruction':Tinstruction_selection;
};
export class Cinstruction extends AlanNode {
	public readonly properties:{
		readonly instruction:Cinstruction_selection
	};
	public readonly inferences:{
		instruction_attribute: () => control_library.Cinstruction
	} = {
		instruction_attribute: cache((detach:boolean) => {
			const widget__expression__type__instruction_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'instruction attribute' ? context.variant.definition as control_library.Cinstruction : undefined).result!;
		})

	}
	constructor(init:Tinstruction, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			instruction: new Cinstruction.Dinstruction(init['instruction'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'instruction'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmatch = {
	'cases':Tcases__match;
	'type':['number', Tnumber__type__match]|['text', Ttext__type__match];
};
export class Cmatch extends AlanNode {
	public readonly properties:{
		readonly cases:Ccases__match,
		readonly type:Cmatch.Dtype
	};
	constructor(init:Tmatch, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Ccases__match(init['cases'], $this),
			type: new Cmatch.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'match'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcases__match = {
	'false':Tone_or_more_expression;
	'true':Tone_or_more_expression;
};
export class Ccases__match extends AlanNode {
	public readonly properties:{
		readonly false_:Cone_or_more_expression,
		readonly true_:Cone_or_more_expression
	};
	constructor(init:Tcases__match, public parent:Cmatch) {
		super();
		const $this = this;
		this.properties = {
			false_: new Ccases__match.Dfalse_(init['false'], $this),
			true_: new Ccases__match.Dtrue_(init['true'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__match = {
	'left expression':Tscalar__widget;
	'right expression':Tscalar__widget;
};
export class Cnumber__type__match extends AlanNode {
	public readonly properties:{
		readonly left_expression:Cscalar__widget,
		readonly right_expression:Cscalar__widget
	};
	constructor(init:Tnumber__type__match, public parent:Cmatch) {
		super();
		const $this = this;
		this.properties = {
			left_expression: new Cnumber__type__match.Dleft_expression(init['left expression'], $this),
			right_expression: new Cnumber__type__match.Dright_expression(init['right expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__match = {
	'left expression':Tscalar__widget;
	'right expression':Tscalar__widget;
};
export class Ctext__type__match extends AlanNode {
	public readonly properties:{
		readonly left_expression:Cscalar__widget,
		readonly right_expression:Cscalar__widget
	};
	constructor(init:Ttext__type__match, public parent:Cmatch) {
		super();
		const $this = this;
		this.properties = {
			left_expression: new Ctext__type__match.Dleft_expression(init['left expression'], $this),
			right_expression: new Ctext__type__match.Dright_expression(init['right expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_switch = {
	'cases':Tcases__node_switch;
	'context':Tcontext_selection;
	'from':['collection', Tcollection__from]|['list', Tlist__from];
};
export class Cnode_switch extends AlanNode {
	public readonly properties:{
		readonly cases:Ccases__node_switch,
		readonly context:Ccontext_selection,
		readonly from:Cnode_switch.Dfrom
	};
	constructor(init:Tnode_switch, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Ccases__node_switch(init['cases'], $this),
			context: new Cnode_switch.Dcontext(init['context'], $this),
			from: new Cnode_switch.Dfrom(init['from'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcases__node_switch = {
	'node':Tone_or_more_expression;
	'none':Tone_or_more_expression;
	'variable assignment':Tvariable_assignment;
};
export class Ccases__node_switch extends AlanNode {
	public readonly properties:{
		readonly node:Cone_or_more_expression,
		readonly none:Cone_or_more_expression,
		readonly variable_assignment:Cvariable_assignment
	};
	constructor(init:Tcases__node_switch, public parent:Cnode_switch) {
		super();
		const $this = this;
		this.properties = {
			node: new Ccases__node_switch.Dnode(init['node'], $this),
			none: new Ccases__node_switch.Dnone(init['none'], $this),
			variable_assignment: new Ccases__node_switch.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__from = {
	'collection':string;
	'key':Tscalar__widget;
};
export class Ccollection__from extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection__from.Dcollection,
		readonly key:Cscalar__widget
	};
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
		widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node)
			.then(context => context?.definitions.binding_context).result!),
		widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.context)
			.then(context => context?.component_root.output.result_widget_configuration_node()).result!)
	}
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__expression__type__node_switch__from__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_binding_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Tcollection__from, public parent:Cnode_switch) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection__from.Dcollection(init['collection'], $this),
			key: new Ccollection__from.Dkey(init['key'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist__from = {
	'index':Tscalar__widget;
	'list':string;
};
export class Clist__from extends AlanNode {
	public readonly properties:{
		readonly index:Cscalar__widget,
		readonly list:Clist__from.Dlist
	};
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
		widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.context)
			.then(context => context?.component_root.output.result_binding_context()).result!),
		widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.list?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tlist__from, public parent:Cnode_switch) {
		super();
		const $this = this;
		this.properties = {
			index: new Clist__from.Dindex(init['index'], $this),
			list: new Clist__from.Dlist(init['list'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'from'?'list'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__type = {
};
export class Cnone__type extends AlanNode {
	public readonly inferences:{
		bounds: () => widget.Clower_bound_cardinality
	} = {
		bounds: cache((detach:boolean) => {
			const widget__expression__type__none_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.bounds())
						.then(context => context?.properties.lower_bound_cardinality()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(() => widget.Clower_bound_cardinality.Pzero).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tnone__type, public parent:Cexpression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscalar__type = {
	'value':Tscalar__widget;
};
export class Cscalar__type extends AlanNode {
	public readonly properties:{
		readonly value:Cscalar__widget
	};
	public readonly inferences:{
		scalar: () => widget.Cscalar_target
	} = {
		scalar: cache((detach:boolean) => {
			const widget__expression__type__scalar_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'scalar' ? context.variant.definition as widget.Cscalar_target : undefined).result!;
		})

	}
	constructor(init:Tscalar__type, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cscalar__type.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'scalar'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsort_collection = {
	'context':Tcontext_selection;
	'direction':'ascending'|['ascending', {}]|'descending'|['descending', {}];
};
export class Csort_collection extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection & { readonly inferences: {
			is_sortable: () => client_bindings.Cyes__sortable;
			node: () => client_bindings.Cnode__client_bindings;
		} },
		readonly direction:Csort_collection.Ddirection
	};
	public readonly inferences:{
		collection_attribute: () => widget.Ccollection_sort_location
	} = {
		collection_attribute: cache((detach:boolean) => {
			const widget__expression__type__sort_collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.target())
				.then(context => context?.variant.name === 'collection sort' ? context.variant.definition as widget.Ccollection_sort_location : undefined).result!;
		})

	}
	constructor(init:Tsort_collection, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			context: new Csort_collection.Dcontext(init['context'], $this),
			direction: new Csort_collection.Ddirection(init['direction'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sort collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tascending = {
};
export class Cascending extends AlanNode {
	constructor(init:Tascending, public parent:Csort_collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'direction'?'ascending'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdescending = {
};
export class Cdescending extends AlanNode {
	constructor(init:Tdescending, public parent:Csort_collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'direction'?'descending'`; }
	public get entity() { return this.parent.entity; }
}
export type Tswitch = {
	'context':Tcontext_selection;
	'type':['binding', Tbinding__type__switch]|['configuration', Tconfiguration__type__switch];
};
export class Cswitch extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection,
		readonly type:Cswitch.Dtype
	};
	constructor(init:Tswitch, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			context: new Cswitch.Dcontext(init['context'], $this),
			type: new Cswitch.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding__type__switch = {
	'property':string;
	'states':Record<string, Tstates__binding>;
};
export class Cbinding__type__switch extends AlanNode {
	public readonly properties:{
		readonly property:Cbinding__type__switch.Dproperty,
		readonly states:Cbinding__type__switch.Dstates
	};
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__expression__type__switch__type__binding_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_binding_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Tbinding__type__switch, public parent:Cswitch) {
		super();
		const $this = this;
		this.properties = {
			property: new Cbinding__type__switch.Dproperty(init['property'], $this),
			states: new Cbinding__type__switch.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__binding extends Reference<client_bindings.Cstates, string> {
	constructor(key:string, $this:Cstates__binding) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.property?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__binding = {
	'next':Tone_or_more_expression;
	'variable assignment':Tvariable_assignment;
};
export class Cstates__binding extends AlanDictionaryEntry {
	public key:Kstates__binding;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly next:Cone_or_more_expression,
		readonly variable_assignment:Cvariable_assignment
	};
	constructor(key:string, init:Tstates__binding, public parent:Cbinding__type__switch) {
		super();
		const $this = this;
		this.key = new Kstates__binding(key, $this);
		this.properties = {
			next: new Cstates__binding.Dnext(init['next'], $this),
			variable_assignment: new Cstates__binding.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tconfiguration__type__switch = {
	'state group':string;
	'states':Record<string, Tstates__configuration>;
};
export class Cconfiguration__type__switch extends AlanNode {
	public readonly properties:{
		readonly state_group:Cconfiguration__type__switch.Dstate_group,
		readonly states:Cconfiguration__type__switch.Dstates
	};
	constructor(init:Tconfiguration__type__switch, public parent:Cswitch) {
		super();
		const $this = this;
		this.properties = {
			state_group: new Cconfiguration__type__switch.Dstate_group(init['state group'], $this),
			states: new Cconfiguration__type__switch.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__configuration extends Reference<widget.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__configuration) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__configuration = {
	'next':Tone_or_more_expression;
	'variable assignment':Tvariable_assignment;
};
export class Cstates__configuration extends AlanDictionaryEntry {
	public key:Kstates__configuration;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly next:Cone_or_more_expression,
		readonly variable_assignment:Cvariable_assignment
	};
	constructor(key:string, init:Tstates__configuration, public parent:Cconfiguration__type__switch) {
		super();
		const $this = this;
		this.key = new Kstates__configuration(key, $this);
		this.properties = {
			next: new Cstates__configuration.Dnext(init['next'], $this),
			variable_assignment: new Cstates__configuration.Dvariable_assignment(init['variable assignment'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Twalk = {
	'context':Tcontext_selection;
	'entry expression':Tone_or_more_expression;
	'type':['configuration', Tconfiguration__type__walk]|['widget binding', Twidget_binding];
	'variable':Tvariable;
};
export class Cwalk extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection,
		readonly entry_expression:Cone_or_more_expression,
		readonly type:Cwalk.Dtype,
		readonly variable:Cvariable
	};
	public readonly inferences:{
		bounds: () => widget.Cupper_bound_cardinality
	} = {
		bounds: cache((detach:boolean) => {
			const widget__expression__type__walk_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.parent)
						.then(context => context?.component_root.input.bounds())
						.then(context => context?.properties.upper_bound_cardinality()).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(() => widget.Cupper_bound_cardinality.Pmany).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Twalk, public parent:Cexpression) {
		super();
		const $this = this;
		this.properties = {
			context: new Cwalk.Dcontext(init['context'], $this),
			entry_expression: new Cwalk.Dentry_expression(init['entry expression'], $this),
			type: new Cwalk.Dtype(init['type'], $this),
			variable: new Cwalk.Dvariable(init['variable'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'walk'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconfiguration__type__walk = {
	'list':string;
};
export class Cconfiguration__type__walk extends AlanNode {
	public readonly properties:{
		readonly list:Cconfiguration__type__walk.Dlist
	};
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
		widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.context)
			.then(context => context?.component_root.output.result_binding_context()).result!),
		widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.list?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tconfiguration__type__walk, public parent:Cwalk) {
		super();
		const $this = this;
		this.properties = {
			list: new Cconfiguration__type__walk.Dlist(init['list'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget_binding = {
	'collection property':string;
	'sort':'no'|['no', {}]|['yes', Tyes__sort];
};
export class Cwidget_binding extends AlanNode {
	public definitions:{
		collection_sort_location: Ccollection_sort_location;
	} = {
		collection_sort_location: new Ccollection_sort_location({name:'collection', definition: this})
	}
	public readonly properties:{
		readonly collection_property:Cwidget_binding.Dcollection_property,
		readonly sort:Cwidget_binding.Dsort
	};
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
		widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection_property?.ref)
			.then(context => context?.properties.node)
			.then(context => context?.definitions.binding_context).result!),
		widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.context)
			.then(context => context?.component_root.output.result_widget_configuration_node()).result!)
	}
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__expression__type__walk__type__widget_binding_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_binding_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Twidget_binding, public parent:Cwalk) {
		super();
		const $this = this;
		this.properties = {
			collection_property: new Cwidget_binding.Dcollection_property(init['collection property'], $this),
			sort: new Cwidget_binding.Dsort(init['sort'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'widget binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__sort = {
};
export class Cno__sort extends AlanNode {
	constructor(init:Tno__sort, public parent:Cwidget_binding) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sort'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__sort = {
	'by':Texpression;
};
export class Cyes__sort extends AlanNode {
	public readonly properties:{
		readonly by:Cexpression
	};
	constructor(init:Tyes__sort, public parent:Cwidget_binding) {
		super();
		const $this = this;
		this.properties = {
			by: new Cyes__sort.Dby(init['by'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sort'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontrol_binding = {
	'binding type':['client binding widget', Tclient_binding_widget]|['let declaration', Tlet_declaration]|['static', Tstatic__binding_type]|['widget', Twidget__binding_type]|['window', Twindow__binding_type];
};
export class Ccontrol_binding extends AlanNode {
	public readonly properties:{
		readonly binding_type:Ccontrol_binding.Dbinding_type
	};
	constructor(init:Tcontrol_binding, public location:AlanNode, public input: {
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			binding_type: new Ccontrol_binding.Dbinding_type(init['binding type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tclient_binding_widget = {
	'context':Tcontext_selection;
};
export class Cclient_binding_widget extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection & { readonly inferences: {
			has_widget: () => client_bindings.Cyes__has_widget;
			node: () => client_bindings.Cnode__client_bindings;
		} }
	};
	constructor(init:Tclient_binding_widget, public parent:Ccontrol_binding) {
		super();
		const $this = this;
		this.properties = {
			context: new Cclient_binding_widget.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'client binding widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlet_declaration = {
	'declaration':string;
	'path':Tcontext_ancestor_path;
};
export class Clet_declaration extends AlanNode {
	public readonly properties:{
		readonly declaration:Clet_declaration.Ddeclaration,
		readonly path:Ccontext_ancestor_path & { readonly inferences: {
			frame: () => widget.Cwidget_implementation_frame;
		} }
	};
	constructor(init:Tlet_declaration, public parent:Ccontrol_binding) {
		super();
		const $this = this;
		this.properties = {
			declaration: new Clet_declaration.Ddeclaration(init['declaration'], $this),
			path: new Clet_declaration.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'let declaration'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__binding_type = {
	'control':string;
	'node binding':Twidget_implementation_node;
};
export class Cstatic__binding_type extends AlanNode {
	public readonly properties:{
		readonly control:Cstatic__binding_type.Dcontrol,
		readonly node_binding:Cwidget_implementation_node
	};
	constructor(init:Tstatic__binding_type, public parent:Ccontrol_binding) {
		super();
		const $this = this;
		this.properties = {
			control: new Cstatic__binding_type.Dcontrol(init['control'], $this),
			node_binding: new Cstatic__binding_type.Dnode_binding(init['node binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget__binding_type = {
	'context':Tcontext_selection;
	'widget':string;
};
export class Cwidget__binding_type extends AlanNode {
	public readonly properties:{
		readonly context:Ccontext_selection,
		readonly widget:Cwidget__binding_type.Dwidget
	};
	constructor(init:Twidget__binding_type, public parent:Ccontrol_binding) {
		super();
		const $this = this;
		this.properties = {
			context: new Cwidget__binding_type.Dcontext(init['context'], $this),
			widget: new Cwidget__binding_type.Dwidget(init['widget'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Twindow__binding_type = {
	'control binding':Tcontrol_binding;
	'variable':Tvariable;
	'window':string;
};
export class Cwindow__binding_type extends AlanNode {
	public readonly properties:{
		readonly control_binding:Ccontrol_binding,
		readonly variable:Cvariable,
		readonly window:Cwindow__binding_type.Dwindow
	};
	constructor(init:Twindow__binding_type, public parent:Ccontrol_binding) {
		super();
		const $this = this;
		this.properties = {
			control_binding: new Cwindow__binding_type.Dcontrol_binding(init['control binding'], $this),
			variable: new Cwindow__binding_type.Dvariable(init['variable'], $this),
			window: new Cwindow__binding_type.Dwindow(init['window'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding type'?'window'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontext_selection_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__context_selection_path];
};
export class Ccontext_selection_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Ccontext_selection_path.Dhas_steps
	};
	public readonly output:{
		result_binding_context: () => client_bindings.Cbinding_context;
		result_widget_configuration_node: () => widget.Cwidget_configuration_node;
	} = {
		result_binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_binding_context()).result!),
		result_widget_configuration_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_widget_configuration_node()).result!)
	};
	constructor(init:Tcontext_selection_path, public location:AlanNode, public input: {
		binding_context: () => client_bindings.Cbinding_context,
		widget_configuration_context: () => widget.Cwidget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Ccontext_selection_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__context_selection_path = {
};
export class Cno__has_steps__context_selection_path extends AlanNode {
	public readonly output:{
		result_binding_context: () => client_bindings.Cbinding_context;
		result_widget_configuration_node: () => widget.Cwidget_configuration_node;
	} = {
		result_binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.binding_context()).result!),
		result_widget_configuration_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.widget_configuration_context()).result!)
	}
	constructor(init:Tno__has_steps__context_selection_path, public parent:Ccontext_selection_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__context_selection_path = {
	'tail':Tcontext_selection_path;
	'type':['client binding', Tclient_binding]|['constrained configuration attribute', Tconstrained_configuration_attribute]|['unconstrained configuration attribute', Tunconstrained_configuration_attribute];
};
export class Cyes__has_steps__context_selection_path extends AlanNode {
	public readonly properties:{
		readonly tail:Ccontext_selection_path,
		readonly type:Cyes__has_steps__context_selection_path.Dtype
	};
	public readonly output:{
		result_binding_context: () => client_bindings.Cbinding_context;
		result_widget_configuration_node: () => widget.Cwidget_configuration_node;
	} = {
		result_binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_binding_context()).result!),
		result_widget_configuration_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_widget_configuration_node()).result!)
	}
	constructor(init:Tyes__has_steps__context_selection_path, public parent:Ccontext_selection_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__context_selection_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__context_selection_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tclient_binding = {
	'binding':string;
};
export class Cclient_binding extends AlanNode {
	public readonly properties:{
		readonly binding:Cclient_binding.Dbinding
	};
	public readonly output:{
		new_binding_context: () => client_bindings.Cnode__client_bindings;
		new_widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		new_binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.binding?.ref)
			.then(context => context?.properties.binding?.ref)
			.then(context => context?.properties.binding).result!),
		new_widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.widget_configuration_context()).result!)
	}
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__context_selection_path__has_steps__yes__type__client_binding_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.binding_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Tclient_binding, public parent:Cyes__has_steps__context_selection_path) {
		super();
		const $this = this;
		this.properties = {
			binding: new Cclient_binding.Dbinding(init['binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'client binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconstrained_configuration_attribute = {
	'configuration attribute':string;
};
export class Cconstrained_configuration_attribute extends AlanNode {
	public readonly properties:{
		readonly configuration_attribute:Cconstrained_configuration_attribute.Dconfiguration_attribute
	};
	public readonly output:{
		new_binding_context: () => client_bindings.Cnode__client_bindings;
		new_widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		new_binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.configuration_attribute?.inferences.binding())
			.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
			.then(context => context?.properties.binding).result!),
		new_widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.configuration_attribute?.inferences.binding())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		node: () => client_bindings.Cnode__client_bindings
	} = {
		node: cache((detach:boolean) => {
			const widget__context_selection_path__has_steps__yes__type__constrained_configuration_attribute_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.binding_context())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
		})

	}
	constructor(init:Tconstrained_configuration_attribute, public parent:Cyes__has_steps__context_selection_path) {
		super();
		const $this = this;
		this.properties = {
			configuration_attribute: new Cconstrained_configuration_attribute.Dconfiguration_attribute(init['configuration attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'constrained configuration attribute'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunconstrained_configuration_attribute = {
	'configuration attribute':string;
};
export class Cunconstrained_configuration_attribute extends AlanNode {
	public readonly properties:{
		readonly configuration_attribute:Cunconstrained_configuration_attribute.Dconfiguration_attribute
	};
	public readonly output:{
		new_binding_context: () => client_bindings.Cnode__client_bindings;
		new_widget_configuration_context: () => widget.Cwidget_configuration_node;
	} = {
		new_binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.configuration_attribute?.inferences.binding())
			.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
			.then(context => context?.properties.binding).result!),
		new_widget_configuration_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.configuration_attribute?.inferences.binding())
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tunconstrained_configuration_attribute, public parent:Cyes__has_steps__context_selection_path) {
		super();
		const $this = this;
		this.properties = {
			configuration_attribute: new Cunconstrained_configuration_attribute.Dconfiguration_attribute(init['configuration attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'unconstrained configuration attribute'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontext_selection = {
	'change context to':['engine state', Tengine_state]|['other context', Tother_context];
};
export class Ccontext_selection extends AlanNode {
	public readonly properties:{
		readonly change_context_to:Ccontext_selection.Dchange_context_to
	};
	public readonly output:{
		result_binding_context: () => client_bindings.Cbinding_context;
		result_widget_configuration_node: () => widget.Cwidget_configuration_node;
	} = {
		result_binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.change_context_to?.state.node.output.binding_context()).result!),
		result_widget_configuration_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.change_context_to?.state.node.output.widget_configuration_node()).result!)
	};
	constructor(init:Tcontext_selection, public location:AlanNode, public input: {
		widget_implementation_context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			change_context_to: new Ccontext_selection.Dchange_context_to(init['change context to'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tengine_state = {
	'engine state binding':string;
};
export class Cengine_state extends AlanNode {
	public readonly properties:{
		readonly engine_state_binding:Cengine_state.Dengine_state_binding
	};
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
		widget_configuration_node: () => widget.Cwidget_configuration_node;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.engine_state_binding?.ref)
			.then(context => context?.properties.binding)
			.then(context => context?.definitions.binding_context).result!),
		widget_configuration_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.widget_implementation_context())
			.then(context => context?.component_root.output.widget_configuration_context()).result!)
	}
	constructor(init:Tengine_state, public parent:Ccontext_selection) {
		super();
		const $this = this;
		this.properties = {
			engine_state_binding: new Cengine_state.Dengine_state_binding(init['engine state binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'change context to'?'engine state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tother_context = {
	'ancestor path':Tcontext_ancestor_path;
	'path':Tcontext_selection_path;
};
export class Cother_context extends AlanNode {
	public readonly properties:{
		readonly ancestor_path:Ccontext_ancestor_path,
		readonly path:Ccontext_selection_path
	};
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
		widget_configuration_node: () => widget.Cwidget_configuration_node;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.result_binding_context()).result!),
		widget_configuration_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.path)
			.then(context => context?.component_root.output.result_widget_configuration_node()).result!)
	}
	constructor(init:Tother_context, public parent:Ccontext_selection) {
		super();
		const $this = this;
		this.properties = {
			ancestor_path: new Cother_context.Dancestor_path(init['ancestor path'], $this),
			path: new Cother_context.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'change context to'?'other context'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontext_ancestor_path = {
	'has step':['no', Tno__has_step]|'root'|['root', {}]|['yes', Tyes__has_step];
};
export class Ccontext_ancestor_path extends AlanNode {
	public readonly properties:{
		readonly has_step:Ccontext_ancestor_path.Dhas_step
	};
	public readonly output:{
		result: () => widget.Cwidget_implementation_context;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_step?.state.node.output.result()).result!)
	};
	constructor(init:Tcontext_ancestor_path, public location:AlanNode, public input: {
		context: () => widget.Cwidget_implementation_context
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_step: new Ccontext_ancestor_path.Dhas_step(init['has step'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_step = {
	'context':'anonymous'|['anonymous', {}]|'implicit'|['implicit', {}]|['named', Tnamed__context];
};
export class Cno__has_step extends AlanNode {
	public readonly properties:{
		readonly context:Cno__has_step.Dcontext
	};
	public readonly output:{
		result: () => widget.Cwidget_implementation_context;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.context?.state.node.output.result()).result!)
	}
	constructor(init:Tno__has_step, public parent:Ccontext_ancestor_path) {
		super();
		const $this = this;
		this.properties = {
			context: new Cno__has_step.Dcontext(init['context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tanonymous__context = {
};
export class Canonymous__context extends AlanNode {
	public readonly output:{
		result: () => widget.Cwidget_implementation_context;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.explicit_context()).result!)
	}
	public readonly inferences:{
		named_variable: () => widget.Canonymous__identifier,
		variable: () => widget.Cvariable
	} = {
		named_variable: cache((detach:boolean) => {
			const widget__context_ancestor_path__has_step__no__context__anonymous_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.variable())
				.then(context => {
					if (context?.properties.identifier.state.name === 'anonymous') {
						return resolve(context.properties.identifier.state.node as widget.Canonymous__identifier).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		variable: cache((detach:boolean) => {
			const widget__context_ancestor_path__has_step__no__context__anonymous_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.explicit_context())
				.then(context => context?.variant.name === 'explicit' ? context.variant.definition as widget.Cwidget_implementation_frame : undefined)
				.then(context => context?.variant.name === 'variable' ? context.variant.definition as widget.Cvariable : undefined).result!;
		})

	}
	constructor(init:Tanonymous__context, public parent:Cno__has_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'anonymous'`; }
	public get entity() { return this.parent.entity; }
}
export type Timplicit = {
};
export class Cimplicit extends AlanNode {
	public readonly output:{
		result: () => widget.Cwidget_implementation_context;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context()).result!)
	}
	public readonly inferences:{
		required_context: () => widget.Cimplicit_implementation_context
	} = {
		required_context: cache((detach:boolean) => {
			const widget__context_ancestor_path__has_step__no__context__implicit_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.variant.name === 'implicit' ? context.variant.definition as widget.Cimplicit_implementation_context : undefined).result!;
		})

	}
	constructor(init:Timplicit, public parent:Cno__has_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'implicit'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnamed__context = {
	'variable name':string;
};
export class Cnamed__context extends AlanNode {
	public readonly properties:{
		readonly variable_name:Cnamed__context.Dvariable_name
	};
	public readonly output:{
		result: () => widget.Cwidget_implementation_context;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context())
			.then(context => context?.component_root.output.explicit_context()).result!)
	}
	public readonly inferences:{
		named_variable: () => widget.Cnamed__identifier,
		variable: () => widget.Cvariable
	} = {
		named_variable: cache((detach:boolean) => {
			const widget__context_ancestor_path__has_step__no__context__named_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.variable())
				.then(context => {
					if (context?.properties.identifier.state.name === 'named') {
						return resolve(context.properties.identifier.state.node as widget.Cnamed__identifier).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		variable: cache((detach:boolean) => {
			const widget__context_ancestor_path__has_step__no__context__named_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.explicit_context())
				.then(context => context?.variant.name === 'explicit' ? context.variant.definition as widget.Cwidget_implementation_frame : undefined)
				.then(context => context?.variant.name === 'variable' ? context.variant.definition as widget.Cvariable : undefined).result!;
		})

	}
	constructor(init:Tnamed__context, public parent:Cno__has_step) {
		super();
		const $this = this;
		this.properties = {
			variable_name: new Cnamed__context.Dvariable_name(init['variable name'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'named'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot = {
};
export class Croot extends AlanNode {
	public readonly output:{
		result: () => widget.Cwidget_implementation_context;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.root)
			.then(context => context?.definitions.implicit_implementation_context)
			.then(context => context?.definitions.widget_implementation_context).result!)
	}
	constructor(init:Troot, public parent:Ccontext_ancestor_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_step = {
	'tail':Tcontext_ancestor_path;
};
export class Cyes__has_step extends AlanNode {
	public readonly properties:{
		readonly tail:Ccontext_ancestor_path
	};
	public readonly output:{
		result: () => widget.Cwidget_implementation_context;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result()).result!)
	}
	public readonly inferences:{
		valid_context: () => widget.Cwidget_implementation_frame
	} = {
		valid_context: cache((detach:boolean) => {
			const widget__context_ancestor_path__has_step__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context())
				.then(context => context?.component_root.output.explicit_context())
				.then(context => context?.variant.name === 'explicit' ? context.variant.definition as widget.Cwidget_implementation_frame : undefined).result!;
		})

	}
	constructor(init:Tyes__has_step, public parent:Ccontext_ancestor_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_step.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vconfiguration_requirement = { name: 'optional', definition: (typeof Cconfiguration_requirement.Poptional)}|{ name: 'required', definition: (typeof Cconfiguration_requirement.Prequired)}
export class Cconfiguration_requirement extends AlanStruct {
	public static Poptional:Cconfiguration_requirement = new class PrimitiveInstance extends Cconfiguration_requirement {
		constructor () {
			super({name: 'optional', definition: undefined as unknown as Cconfiguration_requirement})
			this.variant.definition = this;
		}
	}
	public static Prequired:Cconfiguration_requirement = new class PrimitiveInstance extends Cconfiguration_requirement {
		constructor () {
			super({name: 'required', definition: undefined as unknown as Cconfiguration_requirement})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vconfiguration_requirement) { super(); }
	public cast<K extends Vconfiguration_requirement['name']>(_variant:K):Extract<Vconfiguration_requirement, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vconfiguration_requirement['name']]:(($:Extract<Vconfiguration_requirement, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'configuration requirement'`; }
	public is(other:Cconfiguration_requirement):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Coptional = widget.Cconfiguration_requirement;
export import Crequired = widget.Cconfiguration_requirement;
export type Tconfiguration_attribute_persistence = {
	'persist':'no'|['no', {}]|['yes', Tyes__persist];
};
export class Cconfiguration_attribute_persistence extends AlanNode {
	public readonly properties:{
		readonly persist:Cconfiguration_attribute_persistence.Dpersist
	};
	constructor(init:Tconfiguration_attribute_persistence, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			persist: new Cconfiguration_attribute_persistence.Dpersist(init['persist'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__persist = {
};
export class Cno__persist extends AlanNode {
	constructor(init:Tno__persist, public parent:Cconfiguration_attribute_persistence) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'persist'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__persist = {
	'per entry':'no'|['no', {}]|'yes'|['yes', {}];
	'per session':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__persist extends AlanNode {
	public readonly properties:{
		readonly per_entry:Cyes__persist.Dper_entry,
		readonly per_session:Cyes__persist.Dper_session
	};
	constructor(init:Tyes__persist, public parent:Cconfiguration_attribute_persistence) {
		super();
		const $this = this;
		this.properties = {
			per_entry: new Cyes__persist.Dper_entry(init['per entry'], $this),
			per_session: new Cyes__persist.Dper_session(init['per session'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'persist'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__per_entry = {
};
export class Cno__per_entry extends AlanNode {
	constructor(init:Tno__per_entry, public parent:Cyes__persist) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'per entry'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__per_entry = {
};
export class Cyes__per_entry extends AlanNode {
	constructor(init:Tyes__per_entry, public parent:Cyes__persist) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'per entry'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__per_session = {
};
export class Cno__per_session extends AlanNode {
	constructor(init:Tno__per_session, public parent:Cyes__persist) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'per session'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__per_session = {
};
export class Cyes__per_session extends AlanNode {
	constructor(init:Tyes__per_session, public parent:Cyes__persist) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'per session'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tclient_binding_type_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__client_binding_type_path];
};
export class Cclient_binding_type_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cclient_binding_type_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => client_bindings.Cnode__client_bindings;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tclient_binding_type_path, public location:AlanNode, public input: {
		node: () => client_bindings.Cnode__client_bindings
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cclient_binding_type_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__client_binding_type_path = {
};
export class Cno__has_steps__client_binding_type_path extends AlanNode {
	public readonly output:{
		result_node: () => client_bindings.Cnode__client_bindings;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_steps__client_binding_type_path, public parent:Cclient_binding_type_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__client_binding_type_path = {
	'tail':Tclient_binding_type_path;
	'type':['binding', Tbinding__type__yes]|['collection', Tcollection__type]|['state', Tstate__type];
};
export class Cyes__has_steps__client_binding_type_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cclient_binding_type_path,
		readonly type:Cyes__has_steps__client_binding_type_path.Dtype
	};
	public readonly output:{
		result_node: () => client_bindings.Cnode__client_bindings;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__client_binding_type_path, public parent:Cclient_binding_type_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__client_binding_type_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__client_binding_type_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding__type__yes = {
	'instance binding':string;
};
export class Cbinding__type__yes extends AlanNode {
	public readonly properties:{
		readonly instance_binding:Cbinding__type__yes.Dinstance_binding
	};
	public readonly output:{
		result_node: () => client_bindings.Cnode__client_bindings;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.instance_binding?.ref)
			.then(context => context?.properties.binding?.ref)
			.then(context => context?.properties.binding).result!)
	}
	constructor(init:Tbinding__type__yes, public parent:Cyes__has_steps__client_binding_type_path) {
		super();
		const $this = this;
		this.properties = {
			instance_binding: new Cbinding__type__yes.Dinstance_binding(init['instance binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type = {
	'collection':string;
};
export class Ccollection__type extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection__type.Dcollection
	};
	public readonly output:{
		result_node: () => client_bindings.Cnode__client_bindings;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection__type, public parent:Cyes__has_steps__client_binding_type_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection__type.Dcollection(init['collection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type = {
	'state':string;
	'state group':string;
};
export class Cstate__type extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type.Dstate,
		readonly state_group:Cstate__type.Dstate_group
	};
	public readonly output:{
		result_node: () => client_bindings.Cnode__client_bindings;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type, public parent:Cyes__has_steps__client_binding_type_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type.Dstate(init['state'], $this),
			state_group: new Cstate__type.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export class Cbounds extends AlanStruct {
	constructor(
		public readonly properties: {
			lower_bound_cardinality: () => widget.Clower_bound_cardinality, 
			upper_bound_cardinality: () => widget.Cupper_bound_cardinality
		}
	) { super(); }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'bounds'`; }
	public is(other:Cbounds):boolean {
		if (!this.properties.lower_bound_cardinality().is(other.properties.lower_bound_cardinality())) return false;
		if (!this.properties.upper_bound_cardinality().is(other.properties.upper_bound_cardinality())) return false;
		return true;
	}
}

export type Twidget = {
	'binding context':'none'|['none', {}]|['select', Tselect];
	'expression':Texpression;
	'optional context definition':Toptional_context_definition;
	'widget':Twidget_configuration_node;
};
export class Cwidget extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public definitions:{
		implicit_implementation_context: Cimplicit_implementation_context;
		widget_configuration_node_location: Cwidget_configuration_node_location;
	} = {
		implicit_implementation_context: new Cimplicit_implementation_context({name:'root', definition: this}, {
			binding_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.binding_context?.state.node.output.binding_context()).result!),
			explicit_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.definitions.implicit_implementation_context)
				.then(context => context?.definitions.widget_implementation_context).result!),
			widget_configuration_context: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.widget).result!)
		}),
		widget_configuration_node_location: new Cwidget_configuration_node_location({name:'widget', definition: this})
	}
	public readonly properties:{
		readonly binding_context:Cwidget.Dbinding_context,
		readonly expression:Cexpression,
		readonly optional_context_definition:Coptional_context_definition,
		readonly widget:Cwidget_configuration_node
	};
	constructor(init:Twidget, public readonly input: {
	'client_bindings':client_bindings.Cclient_bindings
	'control_library':{[name:string]:control_library.Ccontrol_library}
	'phrases':phrases.Cphrases}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			binding_context: new Cwidget.Dbinding_context(init['binding context'], $this),
			expression: new Cwidget.Dexpression(init['expression'], $this),
			optional_context_definition: new Cwidget.Doptional_context_definition(init['optional context definition'], $this),
			widget: new Cwidget.Dwidget(init['widget'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tnone__binding_context = {
};
export class Cnone__binding_context extends AlanNode {
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_bindings.Cbinding_context.Pundefined).result!)
	}
	constructor(init:Tnone__binding_context, public parent:Cwidget) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding context'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tselect = {
	'binding':string;
	'switch client binding context':'no'|['no', {}]|['yes', Tyes__switch_client_binding_context__select];
};
export class Cselect extends AlanNode {
	public readonly properties:{
		readonly binding:Cselect.Dbinding,
		readonly switch_client_binding_context:Cselect.Dswitch_client_binding_context
	};
	public readonly output:{
		binding_context: () => client_bindings.Cbinding_context;
	} = {
		binding_context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.switch_client_binding_context?.state.node.output.client_binding())
			.then(context => context?.properties.binding)
			.then(context => context?.definitions.binding_context).result!)
	}
	constructor(init:Tselect, public parent:Cwidget) {
		super();
		const $this = this;
		this.properties = {
			binding: new Cselect.Dbinding(init['binding'], $this),
			switch_client_binding_context: new Cselect.Dswitch_client_binding_context(init['switch client binding context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'binding context'?'select'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__switch_client_binding_context__select = {
};
export class Cno__switch_client_binding_context__select extends AlanNode {
	public readonly output:{
		client_binding: () => client_bindings.Cbindings__client_bindings;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.binding?.ref).result!)
	}
	constructor(init:Tno__switch_client_binding_context__select, public parent:Cselect) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch client binding context'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__switch_client_binding_context__select = {
	'constrained on containing binding':['no', Tno__constrained_on_containing_binding__yes__switch_client_binding_context__select]|['yes', Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__select];
};
export class Cyes__switch_client_binding_context__select extends AlanNode {
	public readonly properties:{
		readonly constrained_on_containing_binding:Cyes__switch_client_binding_context__select.Dconstrained_on_containing_binding
	};
	public readonly output:{
		client_binding: () => client_bindings.Cbindings__client_bindings;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding()).result!)
	}
	constructor(init:Tyes__switch_client_binding_context__select, public parent:Cselect) {
		super();
		const $this = this;
		this.properties = {
			constrained_on_containing_binding: new Cyes__switch_client_binding_context__select.Dconstrained_on_containing_binding(init['constrained on containing binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'switch client binding context'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__constrained_on_containing_binding__yes__switch_client_binding_context__select = {
	'instance binding':string;
};
export class Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select extends AlanNode {
	public readonly properties:{
		readonly instance_binding:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select.Dinstance_binding
	};
	public readonly output:{
		client_binding: () => client_bindings.Cbindings__client_bindings;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.instance_binding?.ref).result!)
	}
	constructor(init:Tno__constrained_on_containing_binding__yes__switch_client_binding_context__select, public parent:Cyes__switch_client_binding_context__select) {
		super();
		const $this = this;
		this.properties = {
			instance_binding: new Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select.Dinstance_binding(init['instance binding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__select = {
	'instance binding':string;
	'type path':Tclient_binding_type_path;
};
export class Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select extends AlanNode {
	public readonly properties:{
		readonly instance_binding:Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select.Dinstance_binding,
		readonly type_path:Cclient_binding_type_path
	};
	public readonly output:{
		client_binding: () => client_bindings.Cbindings__client_bindings;
	} = {
		client_binding: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.instance_binding?.ref)
			.then(context => context?.properties.binding?.ref).result!)
	}
	constructor(init:Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__select, public parent:Cyes__switch_client_binding_context__select) {
		super();
		const $this = this;
		this.properties = {
			instance_binding: new Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select.Dinstance_binding(init['instance binding'], $this),
			type_path: new Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select.Dtype_path(init['type path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'?'yes'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Cclient_binding_type_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__client_binding_type_path, init:Tno__has_steps__client_binding_type_path}|
		{ name: 'yes', node:Cyes__has_steps__client_binding_type_path, init:Tyes__has_steps__client_binding_type_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__client_binding_type_path, parent:Cclient_binding_type_path) => new Cno__has_steps__client_binding_type_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__client_binding_type_path, parent:Cclient_binding_type_path) => new Cyes__has_steps__client_binding_type_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__client_binding_type_path;
				case 'yes': return finalize_yes__has_steps__client_binding_type_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_binding_type_path['has steps'], parent:Cclient_binding_type_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__client_binding_type_path {
	export class Dtail extends Cclient_binding_type_path {
		constructor(data:Tyes__has_steps__client_binding_type_path['tail'], parent:Cyes__has_steps__client_binding_type_path) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'binding', node:Cbinding__type__yes, init:Tbinding__type__yes}|
		{ name: 'collection', node:Ccollection__type, init:Tcollection__type}|
		{ name: 'state', node:Cstate__type, init:Tstate__type}> {
		protected initializer(state: 'binding'|'collection'|'state') {
			switch (state) {
				case 'binding': return (init:Tbinding__type__yes, parent:Cyes__has_steps__client_binding_type_path) => new Cbinding__type__yes(init, parent);
				case 'collection': return (init:Tcollection__type, parent:Cyes__has_steps__client_binding_type_path) => new Ccollection__type(init, parent);
				case 'state': return (init:Tstate__type, parent:Cyes__has_steps__client_binding_type_path) => new Cstate__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding'|'collection'|'state') {
			switch (state) {
				case 'binding': return finalize_binding__type__yes;
				case 'collection': return finalize_collection__type;
				case 'state': return finalize_state__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__client_binding_type_path['type'], parent:Cyes__has_steps__client_binding_type_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbinding__type__yes {
	export class Dinstance_binding extends Reference<client_bindings.Cbindings__node,string> {
		public readonly inferences:{
			instance_binding: () => client_bindings.Cyes__switchable_in_configuration_context_path
		}

		constructor(data:string, $this:Cbinding__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				instance_binding: cache((detach:boolean) => {
					const widget__client_binding_type_path__has_steps__yes__type__binding__instance_binding_nval = $this.properties.instance_binding.ref;
					return resolve($this.properties.instance_binding.ref)
						.then(context => widget__client_binding_type_path__has_steps__yes__type__binding__instance_binding_nval)
						.then(context => {
							if (context?.properties.switchable_in_configuration_context_path.state.name === 'yes') {
								return resolve(context.properties.switchable_in_configuration_context_path.state.node as client_bindings.Cyes__switchable_in_configuration_context_path).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instance binding'`; }
	}
}
export namespace Ccollection__type {
	export class Dcollection extends Reference<client_bindings.Ccollection__type__properties,string> {

		constructor(data:string, $this:Ccollection__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as client_bindings.Ccollection__type__properties;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cstate__type {
	export class Dstate extends Reference<client_bindings.Cstates,string> {

		constructor(data:string, $this:Cstate__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<client_bindings.Cstate_group__type__properties,string> {

		constructor(data:string, $this:Cstate__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as client_bindings.Cstate_group__type__properties;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cconfiguration_attribute_persistence {
	export class Dpersist extends StateGroup<
		{ name: 'no', node:Cno__persist, init:Tno__persist}|
		{ name: 'yes', node:Cyes__persist, init:Tyes__persist}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__persist, parent:Cconfiguration_attribute_persistence) => new Cno__persist(init, parent);
				case 'yes': return (init:Tyes__persist, parent:Cconfiguration_attribute_persistence) => new Cyes__persist(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__persist;
				case 'yes': return finalize_yes__persist;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconfiguration_attribute_persistence['persist'], parent:Cconfiguration_attribute_persistence) {
			super(data, parent, 'persist');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'persist'`; }
	}
}
export namespace Cyes__persist {
	export class Dper_entry extends StateGroup<
		{ name: 'no', node:Cno__per_entry, init:Tno__per_entry}|
		{ name: 'yes', node:Cyes__per_entry, init:Tyes__per_entry}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__per_entry, parent:Cyes__persist) => new Cno__per_entry(init, parent);
				case 'yes': return (init:Tyes__per_entry, parent:Cyes__persist) => new Cyes__per_entry(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__per_entry;
				case 'yes': return finalize_yes__per_entry;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__persist['per entry'], parent:Cyes__persist) {
			super(data, parent, 'per entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'per entry'`; }
	}
	export class Dper_session extends StateGroup<
		{ name: 'no', node:Cno__per_session, init:Tno__per_session}|
		{ name: 'yes', node:Cyes__per_session, init:Tyes__per_session}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__per_session, parent:Cyes__persist) => new Cno__per_session(init, parent);
				case 'yes': return (init:Tyes__per_session, parent:Cyes__persist) => new Cyes__per_session(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__per_session;
				case 'yes': return finalize_yes__per_session;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__persist['per session'], parent:Cyes__persist) {
			super(data, parent, 'per session');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'per session'`; }
	}
}
export namespace Ccontext_ancestor_path {
	export class Dhas_step extends StateGroup<
		{ name: 'no', node:Cno__has_step, init:Tno__has_step}|
		{ name: 'root', node:Croot, init:Troot}|
		{ name: 'yes', node:Cyes__has_step, init:Tyes__has_step}> {
		protected initializer(state: 'no'|'root'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_step, parent:Ccontext_ancestor_path) => new Cno__has_step(init, parent);
				case 'root': return (init:Troot, parent:Ccontext_ancestor_path) => new Croot(init, parent);
				case 'yes': return (init:Tyes__has_step, parent:Ccontext_ancestor_path) => new Cyes__has_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'root'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_step;
				case 'root': return finalize_root;
				case 'yes': return finalize_yes__has_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontext_ancestor_path['has step'], parent:Ccontext_ancestor_path) {
			super(data, parent, 'has step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has step'`; }
	}
}
export namespace Cno__has_step {
	export class Dcontext extends StateGroup<
		{ name: 'anonymous', node:Canonymous__context, init:Tanonymous__context}|
		{ name: 'implicit', node:Cimplicit, init:Timplicit}|
		{ name: 'named', node:Cnamed__context, init:Tnamed__context}> {
		protected initializer(state: 'anonymous'|'implicit'|'named') {
			switch (state) {
				case 'anonymous': return (init:Tanonymous__context, parent:Cno__has_step) => new Canonymous__context(init, parent);
				case 'implicit': return (init:Timplicit, parent:Cno__has_step) => new Cimplicit(init, parent);
				case 'named': return (init:Tnamed__context, parent:Cno__has_step) => new Cnamed__context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'anonymous'|'implicit'|'named') {
			switch (state) {
				case 'anonymous': return finalize_anonymous__context;
				case 'implicit': return finalize_implicit;
				case 'named': return finalize_named__context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__has_step['context'], parent:Cno__has_step) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
}
export namespace Cnamed__context {
	export class Dvariable_name extends Reference<widget.Cnamed_values,string> {

		constructor(data:string, $this:Cnamed__context) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.named_variable())
				.then(context => {
					const entry = context?.properties.named_values.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'variable name'`; }
	}
}
export namespace Cyes__has_step {
	export class Dtail extends Ccontext_ancestor_path {
		constructor(data:Tyes__has_step['tail'], parent:Cyes__has_step) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.valid_context())
					.then(context => context?.component_root.output.parent_context()).result!)
			}, 'tail')
		}
	}
}
export namespace Ccontext_selection {
	export class Dchange_context_to extends StateGroup<
		{ name: 'engine state', node:Cengine_state, init:Tengine_state}|
		{ name: 'other context', node:Cother_context, init:Tother_context}> {
		protected initializer(state: 'engine state'|'other context') {
			switch (state) {
				case 'engine state': return (init:Tengine_state, parent:Ccontext_selection) => new Cengine_state(init, parent);
				case 'other context': return (init:Tother_context, parent:Ccontext_selection) => new Cother_context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'engine state'|'other context') {
			switch (state) {
				case 'engine state': return finalize_engine_state;
				case 'other context': return finalize_other_context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontext_selection['change context to'], parent:Ccontext_selection) {
			super(data, parent, 'change context to');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'change context to'`; }
	}
}
export namespace Cengine_state {
	export class Dengine_state_binding extends Reference<client_bindings.Cbindings__client_bindings,string> {
		public readonly inferences:{
			engine_state_binding: () => client_bindings.Cengine_state__type
		}

		constructor(data:string, $this:Cengine_state) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				engine_state_binding: cache((detach:boolean) => {
					const widget__context_selection__change_context_to__engine_state__engine_state_binding_nval = $this.properties.engine_state_binding.ref;
					return resolve($this.properties.engine_state_binding.ref)
						.then(context => widget__context_selection__change_context_to__engine_state__engine_state_binding_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'engine state') {
								return resolve(context.properties.type.state.node as client_bindings.Cengine_state__type).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'engine state binding'`; }
	}
}
export namespace Cother_context {
	export class Dancestor_path extends Ccontext_ancestor_path {
		constructor(data:Tother_context['ancestor path'], parent:Cother_context) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'ancestor path')
		}
	}
	export class Dpath extends Ccontext_selection_path {
		constructor(data:Tother_context['path'], parent:Cother_context) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.ancestor_path)
					.then(context => context?.component_root.output.result())
					.then(context => context?.component_root.output.binding_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.ancestor_path)
					.then(context => context?.component_root.output.result())
					.then(context => context?.component_root.output.widget_configuration_context()).result!)
			}, 'path')
		}
	}
}
export namespace Ccontext_selection_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__context_selection_path, init:Tno__has_steps__context_selection_path}|
		{ name: 'yes', node:Cyes__has_steps__context_selection_path, init:Tyes__has_steps__context_selection_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__context_selection_path, parent:Ccontext_selection_path) => new Cno__has_steps__context_selection_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__context_selection_path, parent:Ccontext_selection_path) => new Cyes__has_steps__context_selection_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__context_selection_path;
				case 'yes': return finalize_yes__has_steps__context_selection_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontext_selection_path['has steps'], parent:Ccontext_selection_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__context_selection_path {
	export class Dtail extends Ccontext_selection_path {
		constructor(data:Tyes__has_steps__context_selection_path['tail'], parent:Cyes__has_steps__context_selection_path) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.new_binding_context())
					.then(context => context?.definitions.binding_context).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.new_widget_configuration_context()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'client binding', node:Cclient_binding, init:Tclient_binding}|
		{ name: 'constrained configuration attribute', node:Cconstrained_configuration_attribute, init:Tconstrained_configuration_attribute}|
		{ name: 'unconstrained configuration attribute', node:Cunconstrained_configuration_attribute, init:Tunconstrained_configuration_attribute}> {
		protected initializer(state: 'client binding'|'constrained configuration attribute'|'unconstrained configuration attribute') {
			switch (state) {
				case 'client binding': return (init:Tclient_binding, parent:Cyes__has_steps__context_selection_path) => new Cclient_binding(init, parent);
				case 'constrained configuration attribute': return (init:Tconstrained_configuration_attribute, parent:Cyes__has_steps__context_selection_path) => new Cconstrained_configuration_attribute(init, parent);
				case 'unconstrained configuration attribute': return (init:Tunconstrained_configuration_attribute, parent:Cyes__has_steps__context_selection_path) => new Cunconstrained_configuration_attribute(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'client binding'|'constrained configuration attribute'|'unconstrained configuration attribute') {
			switch (state) {
				case 'client binding': return finalize_client_binding;
				case 'constrained configuration attribute': return finalize_constrained_configuration_attribute;
				case 'unconstrained configuration attribute': return finalize_unconstrained_configuration_attribute;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__context_selection_path['type'], parent:Cyes__has_steps__context_selection_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cclient_binding {
	export class Dbinding extends Reference<client_bindings.Cbindings__node,string> {
		public readonly inferences:{
			binding: () => client_bindings.Cyes__switchable_in_configuration_context_path
		}

		constructor(data:string, $this:Cclient_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.node())
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				binding: cache((detach:boolean) => {
					const widget__context_selection_path__has_steps__yes__type__client_binding__binding_nval = $this.properties.binding.ref;
					return resolve($this.properties.binding.ref)
						.then(context => widget__context_selection_path__has_steps__yes__type__client_binding__binding_nval)
						.then(context => {
							if (context?.properties.switchable_in_configuration_context_path.state.name === 'yes') {
								return resolve(context.properties.switchable_in_configuration_context_path.state.node as client_bindings.Cyes__switchable_in_configuration_context_path).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding'`; }
	}
}
export namespace Cconstrained_configuration_attribute {
	export class Dconfiguration_attribute extends Reference<widget.Cattributes__widget_configuration_node,string> {
		public readonly inferences:{
			binding: () => widget.Cbinding__type__attributes,
			constrained: () => widget.Cyes__constrained_on_containing_binding__binding,
			valid_node_type: () => client_bindings.Cnode__client_bindings
		}

		constructor(data:string, $this:Cconstrained_configuration_attribute) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.widget_configuration_context())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				binding: cache((detach:boolean) => {
					const widget__context_selection_path__has_steps__yes__type__constrained_configuration_attribute__configuration_attribute_nval = $this.properties.configuration_attribute.ref;
					return resolve($this.properties.configuration_attribute.ref)
						.then(context => widget__context_selection_path__has_steps__yes__type__constrained_configuration_attribute__configuration_attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'binding') {
								return resolve(context.properties.type.state.node as widget.Cbinding__type__attributes).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				constrained: cache((detach:boolean) => {
					const widget__context_selection_path__has_steps__yes__type__constrained_configuration_attribute__configuration_attribute_nval = $this.properties.configuration_attribute.ref;
					return resolve($this.properties.configuration_attribute.ref)
						.then(() => $this.properties.configuration_attribute.inferences.binding())
						.then(context => {
							if (context?.properties.constrained_on_containing_binding.state.name === 'yes') {
								return resolve(context.properties.constrained_on_containing_binding.state.node as widget.Cyes__constrained_on_containing_binding__binding).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				valid_node_type: cache((detach:boolean) => {
					const widget__context_selection_path__has_steps__yes__type__constrained_configuration_attribute__configuration_attribute_nval = $this.properties.configuration_attribute.ref;
					return resolve($this.properties.configuration_attribute.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.configuration_attribute.inferences.constrained())
								.then(context => context?.properties.type_path)
								.then(context => context?.component_root.output.result_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'configuration attribute'`; }
	}
}
export namespace Cunconstrained_configuration_attribute {
	export class Dconfiguration_attribute extends Reference<widget.Cattributes__widget_configuration_node,string> {
		public readonly inferences:{
			binding: () => widget.Cbinding__type__attributes,
			unconstrained: () => widget.Cno__constrained_on_containing_binding__binding
		}

		constructor(data:string, $this:Cunconstrained_configuration_attribute) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.widget_configuration_context())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				binding: cache((detach:boolean) => {
					const widget__context_selection_path__has_steps__yes__type__unconstrained_configuration_attribute__configuration_attribute_nval = $this.properties.configuration_attribute.ref;
					return resolve($this.properties.configuration_attribute.ref)
						.then(context => widget__context_selection_path__has_steps__yes__type__unconstrained_configuration_attribute__configuration_attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'binding') {
								return resolve(context.properties.type.state.node as widget.Cbinding__type__attributes).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				unconstrained: cache((detach:boolean) => {
					const widget__context_selection_path__has_steps__yes__type__unconstrained_configuration_attribute__configuration_attribute_nval = $this.properties.configuration_attribute.ref;
					return resolve($this.properties.configuration_attribute.ref)
						.then(() => $this.properties.configuration_attribute.inferences.binding())
						.then(context => {
							if (context?.properties.constrained_on_containing_binding.state.name === 'no') {
								return resolve(context.properties.constrained_on_containing_binding.state.node as widget.Cno__constrained_on_containing_binding__binding).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'configuration attribute'`; }
	}
}
export namespace Ccontrol_binding {
	export class Dbinding_type extends StateGroup<
		{ name: 'client binding widget', node:Cclient_binding_widget, init:Tclient_binding_widget}|
		{ name: 'let declaration', node:Clet_declaration, init:Tlet_declaration}|
		{ name: 'static', node:Cstatic__binding_type, init:Tstatic__binding_type}|
		{ name: 'widget', node:Cwidget__binding_type, init:Twidget__binding_type}|
		{ name: 'window', node:Cwindow__binding_type, init:Twindow__binding_type}> {
		protected initializer(state: 'client binding widget'|'let declaration'|'static'|'widget'|'window') {
			switch (state) {
				case 'client binding widget': return (init:Tclient_binding_widget, parent:Ccontrol_binding) => new Cclient_binding_widget(init, parent);
				case 'let declaration': return (init:Tlet_declaration, parent:Ccontrol_binding) => new Clet_declaration(init, parent);
				case 'static': return (init:Tstatic__binding_type, parent:Ccontrol_binding) => new Cstatic__binding_type(init, parent);
				case 'widget': return (init:Twidget__binding_type, parent:Ccontrol_binding) => new Cwidget__binding_type(init, parent);
				case 'window': return (init:Twindow__binding_type, parent:Ccontrol_binding) => new Cwindow__binding_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'client binding widget'|'let declaration'|'static'|'widget'|'window') {
			switch (state) {
				case 'client binding widget': return finalize_client_binding_widget;
				case 'let declaration': return finalize_let_declaration;
				case 'static': return finalize_static__binding_type;
				case 'widget': return finalize_widget__binding_type;
				case 'window': return finalize_window__binding_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontrol_binding['binding type'], parent:Ccontrol_binding) {
			super(data, parent, 'binding type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding type'`; }
	}
}
export namespace Cclient_binding_widget {
	export class Dcontext extends Ccontext_selection {
		public readonly inferences:{
			has_widget: () => client_bindings.Cyes__has_widget,
			node: () => client_bindings.Cnode__client_bindings
		}
		constructor(data:Tclient_binding_widget['context'], parent:Cclient_binding_widget) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
			this.inferences = {
				has_widget: cache((detach:boolean) => {
					const widget__control_binding__binding_type__client_binding_widget__context_nval = this;
					return resolve(this)
						.then(() => parent.properties.context.inferences.node())
						.then(context => {
							if (context?.properties.has_widget.state.name === 'yes') {
								return resolve(context.properties.has_widget.state.node as client_bindings.Cyes__has_widget).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				node: cache((detach:boolean) => {
					const widget__control_binding__binding_type__client_binding_widget__context_nval = this;
					return resolve(this)
						.then(context => widget__control_binding__binding_type__client_binding_widget__context_nval)
						.then(context => context?.component_root.output.result_binding_context())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
				})

			}
		}
	}
}
export namespace Clet_declaration {
	export class Ddeclaration extends Reference<widget.Clet_declarations__let_declarations,string> {

		constructor(data:string, $this:Clet_declaration) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path?.inferences.frame())
				.then(context => context?.component_root.output.let_declarations())
				.then(context => {
					const entry = context?.properties.let_declarations.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'declaration'`; }
	}
	export class Dpath extends Ccontext_ancestor_path {
		public readonly inferences:{
			frame: () => widget.Cwidget_implementation_frame
		}
		constructor(data:Tlet_declaration['path'], parent:Clet_declaration) {
			super(data, parent, {
				context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'path')
			this.inferences = {
				frame: cache((detach:boolean) => {
					const widget__control_binding__binding_type__let_declaration__path_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.path)
						.then(context => context?.component_root.output.result())
						.then(context => context?.variant.name === 'explicit' ? context.variant.definition as widget.Cwidget_implementation_frame : undefined).result!;
				})

			}
		}
	}
}
export namespace Cstatic__binding_type {
	export class Dcontrol extends Reference<control_library.Ccontrol_library,string> {

		constructor(data:string, $this:Cstatic__binding_type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.control_library[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'control'`; }
	}
	export class Dnode_binding extends Cwidget_implementation_node {
		constructor(data:Tstatic__binding_type['node binding'], parent:Cstatic__binding_type) {
			super(data, parent, {
				context_control_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.control?.ref)
					.then(context => context?.properties.node).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'node binding')
		}
	}
}
export namespace Cwidget__binding_type {
	export class Dcontext extends Ccontext_selection {
		constructor(data:Twidget__binding_type['context'], parent:Cwidget__binding_type) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
		}
	}
	export class Dwidget extends Reference<widget.Cwidget__type,string> {
		public readonly inferences:{
			valid_binding_context: () => client_bindings.Cbinding_context
		}

		constructor(data:string, $this:Cwidget__binding_type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'widget') {
							return context.properties.type.state.node as widget.Cwidget__type;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				valid_binding_context: cache((detach:boolean) => {
					const widget__control_binding__binding_type__widget__widget_nval = $this.properties.widget.ref;
					return resolve($this.properties.widget.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => widget__control_binding__binding_type__widget__widget_nval)
								.then(context => context?.parent)
								.then(context => context?.properties.switch_client_binding_context?.state.node.output.client_binding()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.properties.context)
								.then(context => context?.component_root.output.result_binding_context()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'widget'`; }
	}
}
export namespace Cwindow__binding_type {
	export class Dcontrol_binding extends Ccontrol_binding {
		constructor(data:Twindow__binding_type['control binding'], parent:Cwindow__binding_type) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable)
					.then(context => context?.component_root.output.widget_implementation_context()).result!)
			}, 'control binding')
		}
	}
	export class Dvariable extends Cvariable {
		constructor(data:Twindow__binding_type['variable'], parent:Cwindow__binding_type) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context())
					.then(context => context?.component_root.output.binding_context()).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.window?.ref)
					.then(context => context?.properties.node).result!)
			}, 'variable')
		}
	}
	export class Dwindow extends Reference<widget.Cwindow__type,string> {

		constructor(data:string, $this:Cwindow__binding_type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.widget_implementation_context())
				.then(context => context?.component_root.output.widget_configuration_context())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'window') {
							return context.properties.type.state.node as widget.Cwindow__type;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'window'`; }
	}
}
export namespace Cexpression {
	export class Dtype extends StateGroup<
		{ name: 'control', node:Ccontrol__type, init:Tcontrol__type}|
		{ name: 'create entry', node:Ccreate_entry, init:Tcreate_entry}|
		{ name: 'instruction', node:Cinstruction, init:Tinstruction}|
		{ name: 'match', node:Cmatch, init:Tmatch}|
		{ name: 'node switch', node:Cnode_switch, init:Tnode_switch}|
		{ name: 'none', node:Cnone__type, init:Tnone__type}|
		{ name: 'scalar', node:Cscalar__type, init:Tscalar__type}|
		{ name: 'sort collection', node:Csort_collection, init:Tsort_collection}|
		{ name: 'switch', node:Cswitch, init:Tswitch}|
		{ name: 'walk', node:Cwalk, init:Twalk}> {
		protected initializer(state: 'control'|'create entry'|'instruction'|'match'|'node switch'|'none'|'scalar'|'sort collection'|'switch'|'walk') {
			switch (state) {
				case 'control': return (init:Tcontrol__type, parent:Cexpression) => new Ccontrol__type(init, parent);
				case 'create entry': return (init:Tcreate_entry, parent:Cexpression) => new Ccreate_entry(init, parent);
				case 'instruction': return (init:Tinstruction, parent:Cexpression) => new Cinstruction(init, parent);
				case 'match': return (init:Tmatch, parent:Cexpression) => new Cmatch(init, parent);
				case 'node switch': return (init:Tnode_switch, parent:Cexpression) => new Cnode_switch(init, parent);
				case 'none': return (init:Tnone__type, parent:Cexpression) => new Cnone__type(init, parent);
				case 'scalar': return (init:Tscalar__type, parent:Cexpression) => new Cscalar__type(init, parent);
				case 'sort collection': return (init:Tsort_collection, parent:Cexpression) => new Csort_collection(init, parent);
				case 'switch': return (init:Tswitch, parent:Cexpression) => new Cswitch(init, parent);
				case 'walk': return (init:Twalk, parent:Cexpression) => new Cwalk(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'control'|'create entry'|'instruction'|'match'|'node switch'|'none'|'scalar'|'sort collection'|'switch'|'walk') {
			switch (state) {
				case 'control': return finalize_control__type;
				case 'create entry': return finalize_create_entry;
				case 'instruction': return finalize_instruction;
				case 'match': return finalize_match;
				case 'node switch': return finalize_node_switch;
				case 'none': return finalize_none__type;
				case 'scalar': return finalize_scalar__type;
				case 'sort collection': return finalize_sort_collection;
				case 'switch': return finalize_switch;
				case 'walk': return finalize_walk;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texpression['type'], parent:Cexpression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccontrol__type {
	export class Dcontrol_binding extends Ccontrol_binding {
		constructor(data:Tcontrol__type['control binding'], parent:Ccontrol__type) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'control binding')
		}
	}
}
export namespace Ccreate_entry {
	export class Dfor_ extends StateGroup<
		{ name: 'control node', node:Ccontrol_node, init:Tcontrol_node}|
		{ name: 'state', node:Cstate__for, init:Tstate__for}> {
		protected initializer(state: 'control node'|'state') {
			switch (state) {
				case 'control node': return (init:Tcontrol_node, parent:Ccreate_entry) => new Ccontrol_node(init, parent);
				case 'state': return (init:Tstate__for, parent:Ccreate_entry) => new Cstate__for(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'control node'|'state') {
			switch (state) {
				case 'control node': return finalize_control_node;
				case 'state': return finalize_state__for;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcreate_entry['for'], parent:Ccreate_entry) {
			super(data, parent, 'for');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'for'`; }
	}
	export class Dnode extends Cwidget_implementation_node {
		constructor(data:Tcreate_entry['node'], parent:Ccreate_entry) {
			super(data, parent, {
				context_control_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.for_?.state.node.output.control_node()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'node')
		}
	}
}
export namespace Cstate__for {
	export class Dstate extends Reference<control_library.Cstates,string> {

		constructor(data:string, $this:Cstate__for) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.stategroup())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cinstruction {
	export class Dinstruction extends Cinstruction_selection {
		constructor(data:Tinstruction['instruction'], parent:Cinstruction) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.instruction_attribute()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'instruction')
		}
	}
}
export namespace Cmatch {
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__match, init:Tnumber__type__match}|
		{ name: 'text', node:Ctext__type__match, init:Ttext__type__match}> {
		protected initializer(state: 'number'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__match, parent:Cmatch) => new Cnumber__type__match(init, parent);
				case 'text': return (init:Ttext__type__match, parent:Cmatch) => new Ctext__type__match(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__match;
				case 'text': return finalize_text__type__match;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmatch['type'], parent:Cmatch) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccases__match {
	export class Dfalse_ extends Cone_or_more_expression {
		constructor(data:Tcases__match['false'], parent:Ccases__match) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'false')
		}
	}
	export class Dtrue_ extends Cone_or_more_expression {
		constructor(data:Tcases__match['true'], parent:Ccases__match) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'true')
		}
	}
}
export namespace Cnumber__type__match {
	export class Dleft_expression extends Cscalar__widget {
		constructor(data:Tnumber__type__match['left expression'], parent:Cnumber__type__match) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cnumber_target.Pother)
					.then(context => context?.definitions.scalar_target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'left expression')
		}
	}
	export class Dright_expression extends Cscalar__widget {
		constructor(data:Tnumber__type__match['right expression'], parent:Cnumber__type__match) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cnumber_target.Pother)
					.then(context => context?.definitions.scalar_target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'right expression')
		}
	}
}
export namespace Ctext__type__match {
	export class Dleft_expression extends Cscalar__widget {
		constructor(data:Ttext__type__match['left expression'], parent:Ctext__type__match) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Ctext_target.Pother)
					.then(context => context?.definitions.scalar_target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'left expression')
		}
	}
	export class Dright_expression extends Cscalar__widget {
		constructor(data:Ttext__type__match['right expression'], parent:Ctext__type__match) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Ctext_target.Pother)
					.then(context => context?.definitions.scalar_target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'right expression')
		}
	}
}
export namespace Cnode_switch {
	export class Dcontext extends Ccontext_selection {
		constructor(data:Tnode_switch['context'], parent:Cnode_switch) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
		}
	}
	export class Dfrom extends StateGroup<
		{ name: 'collection', node:Ccollection__from, init:Tcollection__from}|
		{ name: 'list', node:Clist__from, init:Tlist__from}> {
		protected initializer(state: 'collection'|'list') {
			switch (state) {
				case 'collection': return (init:Tcollection__from, parent:Cnode_switch) => new Ccollection__from(init, parent);
				case 'list': return (init:Tlist__from, parent:Cnode_switch) => new Clist__from(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'list') {
			switch (state) {
				case 'collection': return finalize_collection__from;
				case 'list': return finalize_list__from;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_switch['from'], parent:Cnode_switch) {
			super(data, parent, 'from');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'from'`; }
	}
}
export namespace Ccases__node_switch {
	export class Dnode extends Cone_or_more_expression {
		constructor(data:Tcases__node_switch['node'], parent:Ccases__node_switch) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.widget_implementation_context()).result!)
			}, 'node')
		}
	}
	export class Dnone extends Cone_or_more_expression {
		constructor(data:Tcases__node_switch['none'], parent:Ccases__node_switch) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'none')
		}
	}
	export class Dvariable_assignment extends Cvariable_assignment {
		constructor(data:Tcases__node_switch['variable assignment'], parent:Ccases__node_switch) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.from?.state.node.output.binding_context()).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.from?.state.node.output.widget_configuration_context()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Ccollection__from {
	export class Dcollection extends Reference<client_bindings.Ccollection__type__properties,string> {

		constructor(data:string, $this:Ccollection__from) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.node())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as client_bindings.Ccollection__type__properties;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dkey extends Cscalar__widget {
		constructor(data:Tcollection__from['key'], parent:Ccollection__from) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Ctext_target.Pother)
					.then(context => context?.definitions.scalar_target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'key')
		}
	}
}
export namespace Clist__from {
	export class Dindex extends Cscalar__widget {
		constructor(data:Tlist__from['index'], parent:Clist__from) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cnumber_target.Pother)
					.then(context => context?.definitions.scalar_target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'index')
		}
	}
	export class Dlist extends Reference<widget.Clist__type,string> {

		constructor(data:string, $this:Clist__from) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'list') {
							return context.properties.type.state.node as widget.Clist__type;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'list'`; }
	}
}
export namespace Cscalar__type {
	export class Dvalue extends Cscalar__widget {
		constructor(data:Tscalar__type['value'], parent:Cscalar__type) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.scalar()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'value')
		}
	}
}
export namespace Csort_collection {
	export class Dcontext extends Ccontext_selection {
		public readonly inferences:{
			is_sortable: () => client_bindings.Cyes__sortable,
			node: () => client_bindings.Cnode__client_bindings
		}
		constructor(data:Tsort_collection['context'], parent:Csort_collection) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
			this.inferences = {
				is_sortable: cache((detach:boolean) => {
					const widget__expression__type__sort_collection__context_nval = this;
					return resolve(this)
						.then(() => parent.properties.context.inferences.node())
						.then(context => {
							if (context?.properties.sortable.state.name === 'yes') {
								return resolve(context.properties.sortable.state.node as client_bindings.Cyes__sortable).result;
							} else {
								return undefined;
							}
						}).result!;
				})
				,
				node: cache((detach:boolean) => {
					const widget__expression__type__sort_collection__context_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.context)
						.then(context => context?.component_root.output.result_binding_context())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
				})

			}
		}
	}
	export class Ddirection extends StateGroup<
		{ name: 'ascending', node:Cascending, init:Tascending}|
		{ name: 'descending', node:Cdescending, init:Tdescending}> {
		protected initializer(state: 'ascending'|'descending') {
			switch (state) {
				case 'ascending': return (init:Tascending, parent:Csort_collection) => new Cascending(init, parent);
				case 'descending': return (init:Tdescending, parent:Csort_collection) => new Cdescending(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ascending'|'descending') {
			switch (state) {
				case 'ascending': return finalize_ascending;
				case 'descending': return finalize_descending;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsort_collection['direction'], parent:Csort_collection) {
			super(data, parent, 'direction');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'direction'`; }
	}
}
export namespace Cswitch {
	export class Dcontext extends Ccontext_selection {
		constructor(data:Tswitch['context'], parent:Cswitch) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'binding', node:Cbinding__type__switch, init:Tbinding__type__switch}|
		{ name: 'configuration', node:Cconfiguration__type__switch, init:Tconfiguration__type__switch}> {
		protected initializer(state: 'binding'|'configuration') {
			switch (state) {
				case 'binding': return (init:Tbinding__type__switch, parent:Cswitch) => new Cbinding__type__switch(init, parent);
				case 'configuration': return (init:Tconfiguration__type__switch, parent:Cswitch) => new Cconfiguration__type__switch(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding'|'configuration') {
			switch (state) {
				case 'binding': return finalize_binding__type__switch;
				case 'configuration': return finalize_configuration__type__switch;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tswitch['type'], parent:Cswitch) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cbinding__type__switch {
	export class Dproperty extends Reference<client_bindings.Cstate_group__type__properties,string> {

		constructor(data:string, $this:Cbinding__type__switch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.node())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as client_bindings.Cstate_group__type__properties;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__binding, init:Tstates__binding},Cbinding__type__switch> {
		protected initialize(parent:Cbinding__type__switch, key:string, entry_init:Tstates__binding) { return new Cstates__binding(key, entry_init, parent); }
		protected finalize = finalize_states__binding
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(widget__expression__type__switch__type__binding__states_key_nval:client_bindings.Cstates):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.property?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tbinding__type__switch['states'], parent:Cbinding__type__switch) {
			super(data, parent);
		}
	}
}
export namespace Cstates__binding {
	export class Dnext extends Cone_or_more_expression {
		constructor(data:Tstates__binding['next'], parent:Cstates__binding) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.widget_implementation_context()).result!)
			}, 'next')
		}
	}
	export class Dvariable_assignment extends Cvariable_assignment {
		constructor(data:Tstates__binding['variable assignment'], parent:Cstates__binding) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node)
					.then(context => context?.definitions.binding_context).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context)
					.then(context => context?.component_root.output.result_widget_configuration_node()).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cconfiguration__type__switch {
	export class Dstate_group extends Reference<widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node,string> {

		constructor(data:string, $this:Cconfiguration__type__switch) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__configuration, init:Tstates__configuration},Cconfiguration__type__switch> {
		protected initialize(parent:Cconfiguration__type__switch, key:string, entry_init:Tstates__configuration) { return new Cstates__configuration(key, entry_init, parent); }
		protected finalize = finalize_states__configuration
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(widget__expression__type__switch__type__configuration__states_key_nval:widget.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tconfiguration__type__switch['states'], parent:Cconfiguration__type__switch) {
			super(data, parent);
		}
	}
}
export namespace Cstates__configuration {
	export class Dnext extends Cone_or_more_expression {
		constructor(data:Tstates__configuration['next'], parent:Cstates__configuration) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable_assignment)
					.then(context => context?.component_root.output.widget_implementation_context()).result!)
			}, 'next')
		}
	}
	export class Dvariable_assignment extends Cvariable_assignment {
		constructor(data:Tstates__configuration['variable assignment'], parent:Cstates__configuration) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context)
					.then(context => context?.component_root.output.result_binding_context()).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!)
			}, 'variable assignment')
		}
	}
}
export namespace Cwalk {
	export class Dcontext extends Ccontext_selection {
		constructor(data:Twalk['context'], parent:Cwalk) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
		}
	}
	export class Dentry_expression extends Cone_or_more_expression {
		constructor(data:Twalk['entry expression'], parent:Cwalk) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.variable)
					.then(context => context?.component_root.output.widget_implementation_context()).result!)
			}, 'entry expression')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'configuration', node:Cconfiguration__type__walk, init:Tconfiguration__type__walk}|
		{ name: 'widget binding', node:Cwidget_binding, init:Twidget_binding}> {
		protected initializer(state: 'configuration'|'widget binding') {
			switch (state) {
				case 'configuration': return (init:Tconfiguration__type__walk, parent:Cwalk) => new Cconfiguration__type__walk(init, parent);
				case 'widget binding': return (init:Twidget_binding, parent:Cwalk) => new Cwidget_binding(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'configuration'|'widget binding') {
			switch (state) {
				case 'configuration': return finalize_configuration__type__walk;
				case 'widget binding': return finalize_widget_binding;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twalk['type'], parent:Cwalk) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dvariable extends Cvariable {
		constructor(data:Twalk['variable'], parent:Cwalk) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.binding_context()).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.widget_configuration_context()).result!)
			}, 'variable')
		}
	}
}
export namespace Cconfiguration__type__walk {
	export class Dlist extends Reference<widget.Clist__type,string> {

		constructor(data:string, $this:Cconfiguration__type__walk) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'list') {
							return context.properties.type.state.node as widget.Clist__type;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'list'`; }
	}
}
export namespace Cwidget_binding {
	export class Dcollection_property extends Reference<client_bindings.Ccollection__type__properties,string> {

		constructor(data:string, $this:Cwidget_binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.node())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as client_bindings.Ccollection__type__properties;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection property'`; }
	}
	export class Dsort extends StateGroup<
		{ name: 'no', node:Cno__sort, init:Tno__sort}|
		{ name: 'yes', node:Cyes__sort, init:Tyes__sort}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__sort, parent:Cwidget_binding) => new Cno__sort(init, parent);
				case 'yes': return (init:Tyes__sort, parent:Cwidget_binding) => new Cyes__sort(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__sort;
				case 'yes': return finalize_yes__sort;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twidget_binding['sort'], parent:Cwidget_binding) {
			super(data, parent, 'sort');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'sort'`; }
	}
}
export namespace Cyes__sort {
	export class Dby extends Cexpression {
		constructor(data:Tyes__sort['by'], parent:Cyes__sort) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this).then(context => new Cbounds({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Clower_bound_cardinality.Pzero).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.collection_sort_location)
					.then(context => context?.definitions.target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'by')
		}
	}
}
export namespace Cexpression_list {
	export class Dexpression extends Cexpression {
		constructor(data:Texpression_list['expression'], parent:Cexpression_list) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.optional_context_definition)
					.then(context => context?.component_root.output.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
	export class Dhas_next extends StateGroup<
		{ name: 'no', node:Cno__has_next, init:Tno__has_next}|
		{ name: 'yes', node:Cyes__has_next, init:Tyes__has_next}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next, parent:Cexpression_list) => new Cno__has_next(init, parent);
				case 'yes': return (init:Tyes__has_next, parent:Cexpression_list) => new Cyes__has_next(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next;
				case 'yes': return finalize_yes__has_next;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texpression_list['has next'], parent:Cexpression_list) {
			super(data, parent, 'has next');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next'`; }
	}
	export class Doptional_context_definition extends Coptional_context_definition {
		constructor(data:Texpression_list['optional context definition'], parent:Cexpression_list) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'optional context definition')
		}
	}
}
export namespace Cyes__has_next {
	export class Dnext extends Cexpression_list {
		constructor(data:Tyes__has_next['next'], parent:Cyes__has_next) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'next')
		}
	}
}
export namespace Cinstruction_argument {
	export class Dnext_argument extends StateGroup<
		{ name: 'exists', node:Cexists, init:Texists}|
		{ name: 'no', node:Cno__next_argument, init:Tno__next_argument}> {
		protected initializer(state: 'exists'|'no') {
			switch (state) {
				case 'exists': return (init:Texists, parent:Cinstruction_argument) => new Cexists(init, parent);
				case 'no': return (init:Tno__next_argument, parent:Cinstruction_argument) => new Cno__next_argument(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'exists'|'no') {
			switch (state) {
				case 'exists': return finalize_exists;
				case 'no': return finalize_no__next_argument;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction_argument['next argument'], parent:Cinstruction_argument) {
			super(data, parent, 'next argument');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'next argument'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'file', node:Cfile, init:Tfile}|
		{ name: 'number', node:Cnumber__type__instruction_argument, init:Tnumber__type__instruction_argument}|
		{ name: 'text', node:Ctext__type__instruction_argument, init:Ttext__type__instruction_argument}|
		{ name: 'view', node:Cview__type__instruction_argument, init:Tview__type__instruction_argument}> {
		protected initializer(state: 'file'|'number'|'text'|'view') {
			switch (state) {
				case 'file': return (init:Tfile, parent:Cinstruction_argument) => new Cfile(init, parent);
				case 'number': return (init:Tnumber__type__instruction_argument, parent:Cinstruction_argument) => new Cnumber__type__instruction_argument(init, parent);
				case 'text': return (init:Ttext__type__instruction_argument, parent:Cinstruction_argument) => new Ctext__type__instruction_argument(init, parent);
				case 'view': return (init:Tview__type__instruction_argument, parent:Cinstruction_argument) => new Cview__type__instruction_argument(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'file'|'number'|'text'|'view') {
			switch (state) {
				case 'file': return finalize_file;
				case 'number': return finalize_number__type__instruction_argument;
				case 'text': return finalize_text__type__instruction_argument;
				case 'view': return finalize_view__type__instruction_argument;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction_argument['type'], parent:Cinstruction_argument) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cexists {
	export class Dargument extends Cinstruction_argument {
		constructor(data:Texists['argument'], parent:Cexists) {
			super(data, parent, {
				client_bindings_argument: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.next_argument_exists())
					.then(context => context?.properties.argument).result!),
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'argument')
		}
	}
}
export namespace Cnumber__type__instruction_argument {
	export class Dargument extends Cnumber_argument {
		constructor(data:Tnumber__type__instruction_argument['argument'], parent:Cnumber__type__instruction_argument) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'argument')
		}
	}
}
export namespace Ctext__type__instruction_argument {
	export class Dargument extends Ctext_argument {
		constructor(data:Ttext__type__instruction_argument['argument'], parent:Ctext__type__instruction_argument) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'argument')
		}
	}
}
export namespace Cview__type__instruction_argument {
	export class Dcontext extends Ccontext_selection {
		constructor(data:Tview__type__instruction_argument['context'], parent:Cview__type__instruction_argument) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
		}
	}
	export class Dview_configuration extends Reference<widget.Cview__type__attributes,string> {

		constructor(data:string, $this:Cview__type__instruction_argument) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'view') {
							return context.properties.type.state.node as widget.Cview__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view configuration'`; }
	}
}
export namespace Cinstruction_argument_configuration_node {
	export class Dattributes extends AlanDictionary<{ node:Cattributes__instruction_argument_configuration_node, init:Tattributes__instruction_argument_configuration_node},Cinstruction_argument_configuration_node> {
		protected initialize(parent:Cinstruction_argument_configuration_node, key:string, entry_init:Tattributes__instruction_argument_configuration_node) { return new Cattributes__instruction_argument_configuration_node(key, entry_init, parent); }
		protected finalize = finalize_attributes__instruction_argument_configuration_node
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(widget__instruction_argument_configuration_node__attributes_key_nval:widget.Cattributes__widget_configuration_node):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.widget_configuration_node())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Tinstruction_argument_configuration_node['attributes'], parent:Cinstruction_argument_configuration_node) {
			super(data, parent);
		}
	}
}
export namespace Cattributes__instruction_argument_configuration_node {
	export class Dtype extends StateGroup<
		{ name: 'configuration', node:Cconfiguration__type__attributes__instruction_argument_configuration_node, init:Tconfiguration__type__attributes__instruction_argument_configuration_node}> {
		protected initializer(state: 'configuration') {
			switch (state) {
				case 'configuration': return (init:Tconfiguration__type__attributes__instruction_argument_configuration_node, parent:Cattributes__instruction_argument_configuration_node) => new Cconfiguration__type__attributes__instruction_argument_configuration_node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'configuration') {
			switch (state) {
				case 'configuration': return finalize_configuration__type__attributes__instruction_argument_configuration_node;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes__instruction_argument_configuration_node['type'], parent:Cattributes__instruction_argument_configuration_node) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cconfiguration__type__attributes__instruction_argument_configuration_node {
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__configuration__type__attributes__instruction_argument_configuration_node, init:Tnumber__type__configuration__type__attributes__instruction_argument_configuration_node}|
		{ name: 'state group', node:Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node, init:Tstate_group__type__configuration__type__attributes__instruction_argument_configuration_node}|
		{ name: 'text', node:Ctext__type__configuration__type__attributes__instruction_argument_configuration_node, init:Ttext__type__configuration__type__attributes__instruction_argument_configuration_node}> {
		protected initializer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__configuration__type__attributes__instruction_argument_configuration_node, parent:Cconfiguration__type__attributes__instruction_argument_configuration_node) => new Cnumber__type__configuration__type__attributes__instruction_argument_configuration_node(init, parent);
				case 'state group': return (init:Tstate_group__type__configuration__type__attributes__instruction_argument_configuration_node, parent:Cconfiguration__type__attributes__instruction_argument_configuration_node) => new Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node(init, parent);
				case 'text': return (init:Ttext__type__configuration__type__attributes__instruction_argument_configuration_node, parent:Cconfiguration__type__attributes__instruction_argument_configuration_node) => new Ctext__type__configuration__type__attributes__instruction_argument_configuration_node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__configuration__type__attributes__instruction_argument_configuration_node;
				case 'state group': return finalize_state_group__type__configuration__type__attributes__instruction_argument_configuration_node;
				case 'text': return finalize_text__type__configuration__type__attributes__instruction_argument_configuration_node;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconfiguration__type__attributes__instruction_argument_configuration_node['type'], parent:Cconfiguration__type__attributes__instruction_argument_configuration_node) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber__type__configuration__type__attributes__instruction_argument_configuration_node {
	export class Dargument extends Cnumber_argument {
		constructor(data:Tnumber__type__configuration__type__attributes__instruction_argument_configuration_node['argument'], parent:Cnumber__type__configuration__type__attributes__instruction_argument_configuration_node) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'argument')
		}
	}
}
export namespace Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node {
	export class Dnode extends Cinstruction_argument_configuration_node {
		constructor(data:Tstate_group__type__configuration__type__attributes__instruction_argument_configuration_node['node'], parent:Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'node')
		}
	}
	export class Dstate extends Reference<widget.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group_type())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Ctext__type__configuration__type__attributes__instruction_argument_configuration_node {
	export class Dargument extends Ctext_argument {
		constructor(data:Ttext__type__configuration__type__attributes__instruction_argument_configuration_node['argument'], parent:Ctext__type__configuration__type__attributes__instruction_argument_configuration_node) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'argument')
		}
	}
}
export namespace Cinstruction_selection {
	export class Dconfiguration_attribute_type extends StateGroup<
		{ name: 'binding', node:Cbinding__configuration_attribute_type, init:Tbinding__configuration_attribute_type}|
		{ name: 'configuration', node:Cconfiguration__configuration_attribute_type, init:Tconfiguration__configuration_attribute_type}> {
		protected initializer(state: 'binding'|'configuration') {
			switch (state) {
				case 'binding': return (init:Tbinding__configuration_attribute_type, parent:Cinstruction_selection) => new Cbinding__configuration_attribute_type(init, parent);
				case 'configuration': return (init:Tconfiguration__configuration_attribute_type, parent:Cinstruction_selection) => new Cconfiguration__configuration_attribute_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding'|'configuration') {
			switch (state) {
				case 'binding': return finalize_binding__configuration_attribute_type;
				case 'configuration': return finalize_configuration__configuration_attribute_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tinstruction_selection['configuration attribute type'], parent:Cinstruction_selection) {
			super(data, parent, 'configuration attribute type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'configuration attribute type'`; }
	}
	export class Dcontext extends Ccontext_selection {
		constructor(data:Tinstruction_selection['context'], parent:Cinstruction_selection) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
		}
	}
}
export namespace Cbinding__configuration_attribute_type {
	export class Dhas_arguments extends StateGroup<
		{ name: 'no', node:Cno__has_arguments, init:Tno__has_arguments}|
		{ name: 'yes', node:Cyes__has_arguments, init:Tyes__has_arguments}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_arguments, parent:Cbinding__configuration_attribute_type) => new Cno__has_arguments(init, parent);
				case 'yes': return (init:Tyes__has_arguments, parent:Cbinding__configuration_attribute_type) => new Cyes__has_arguments(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_arguments;
				case 'yes': return finalize_yes__has_arguments;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinding__configuration_attribute_type['has arguments'], parent:Cbinding__configuration_attribute_type) {
			super(data, parent, 'has arguments');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has arguments'`; }
	}
	export class Dinstruction extends Reference<client_bindings.Cinstructions,string> {

		constructor(data:string, $this:Cbinding__configuration_attribute_type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.node())
				.then(context => {
					const entry = context?.properties.instructions.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instruction'`; }
	}
}
export namespace Cyes__has_arguments {
	export class Dfirst_argument extends Cinstruction_argument {
		constructor(data:Tyes__has_arguments['first argument'], parent:Cyes__has_arguments) {
			super(data, parent, {
				client_bindings_argument: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.has_arguments())
					.then(context => context?.properties.first_argument).result!),
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'first argument')
		}
	}
}
export namespace Cconfiguration__configuration_attribute_type {
	export class Dinstruction extends StateGroup<
		{ name: 'set number', node:Cset_number, init:Tset_number}|
		{ name: 'set state', node:Cset_state, init:Tset_state}|
		{ name: 'set text', node:Cset_text, init:Tset_text}> {
		protected initializer(state: 'set number'|'set state'|'set text') {
			switch (state) {
				case 'set number': return (init:Tset_number, parent:Cconfiguration__configuration_attribute_type) => new Cset_number(init, parent);
				case 'set state': return (init:Tset_state, parent:Cconfiguration__configuration_attribute_type) => new Cset_state(init, parent);
				case 'set text': return (init:Tset_text, parent:Cconfiguration__configuration_attribute_type) => new Cset_text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'set number'|'set state'|'set text') {
			switch (state) {
				case 'set number': return finalize_set_number;
				case 'set state': return finalize_set_state;
				case 'set text': return finalize_set_text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconfiguration__configuration_attribute_type['instruction'], parent:Cconfiguration__configuration_attribute_type) {
			super(data, parent, 'instruction');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instruction'`; }
	}
}
export namespace Cset_number {
	export class Dargument extends Cnumber_argument {
		constructor(data:Tset_number['argument'], parent:Cset_number) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'argument')
		}
	}
	export class Dnumber extends Reference<widget.Cnumber__type__configuration__type__attributes__widget_configuration_node,string> {

		constructor(data:string, $this:Cset_number) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as widget.Cnumber__type__configuration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cset_state {
	export class Dnode extends Cinstruction_argument_configuration_node {
		constructor(data:Tset_state['node'], parent:Cset_state) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_configuration_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'node')
		}
	}
	export class Dstate extends Reference<widget.Cstates__state_group,string> {

		constructor(data:string, $this:Cset_state) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node,string> {

		constructor(data:string, $this:Cset_state) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cset_text {
	export class Dargument extends Ctext_argument {
		constructor(data:Tset_text['argument'], parent:Cset_text) {
			super(data, parent, {
				control_instruction: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'argument')
		}
	}
	export class Dtext extends Reference<widget.Ctext__type__configuration__type__attributes__widget_configuration_node,string> {

		constructor(data:string, $this:Cset_text) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as widget.Ctext__type__configuration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Clet_declarations__widget {
	export class Dlet_declarations extends AlanDictionary<{ node:Clet_declarations__let_declarations, init:Tlet_declarations__let_declarations},Clet_declarations__widget> {
		protected initialize(parent:Clet_declarations__widget, key:string, entry_init:Tlet_declarations__let_declarations) { return new Clet_declarations__let_declarations(key, entry_init, parent); }
		protected finalize = finalize_let_declarations__let_declarations
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'let declarations'`; }
		constructor(data:Tlet_declarations__widget['let declarations'], parent:Clet_declarations__widget) {
			super(data, parent);
		}
	}
}
export namespace Clet_declarations__let_declarations {
	export class Dexpression extends Cexpression {
		constructor(data:Tlet_declarations__let_declarations['expression'], parent:Clet_declarations__let_declarations) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this).then(context => new Cbounds({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Clower_bound_cardinality.Pone).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Ctarget.Pcontrol).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
}
export namespace Cnumber_argument {
	export class Dexpression extends Cexpression {
		constructor(data:Tnumber_argument['expression'], parent:Cnumber_argument) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this).then(context => new Cbounds({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Clower_bound_cardinality.Pone).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Cnumber_target({name: 'event', definition: conv_context});
					})
					.then(context => context?.definitions.scalar_target)
					.then(context => context?.definitions.target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
}
export namespace Cone_or_more_expression {
	export class Dmultiplicity extends StateGroup<
		{ name: 'plural', node:Cplural, init:Tplural}|
		{ name: 'singular', node:Csingular, init:Tsingular}> {
		protected initializer(state: 'plural'|'singular') {
			switch (state) {
				case 'plural': return (init:Tplural, parent:Cone_or_more_expression) => new Cplural(init, parent);
				case 'singular': return (init:Tsingular, parent:Cone_or_more_expression) => new Csingular(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'plural'|'singular') {
			switch (state) {
				case 'plural': return finalize_plural;
				case 'singular': return finalize_singular;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tone_or_more_expression['multiplicity'], parent:Cone_or_more_expression) {
			super(data, parent, 'multiplicity');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'multiplicity'`; }
	}
}
export namespace Cplural {
	export class Dexpression extends Cexpression_list {
		constructor(data:Tplural['expression'], parent:Cplural) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this).then(context => new Cbounds({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Clower_bound_cardinality.Pzero).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Cupper_bound_cardinality.Pmany).result!}), ).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
}
export namespace Csingular {
	export class Dexpression extends Cexpression {
		constructor(data:Tsingular['expression'], parent:Csingular) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.bounds()).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
}
export namespace Coptional_context_definition {
	export class Ddefine_context extends StateGroup<
		{ name: 'no', node:Cno__define_context, init:Tno__define_context}|
		{ name: 'yes', node:Cyes__define_context, init:Tyes__define_context}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__define_context, parent:Coptional_context_definition) => new Cno__define_context(init, parent);
				case 'yes': return (init:Tyes__define_context, parent:Coptional_context_definition) => new Cyes__define_context(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__define_context;
				case 'yes': return finalize_yes__define_context;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Toptional_context_definition['define context'], parent:Coptional_context_definition) {
			super(data, parent, 'define context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'define context'`; }
	}
}
export namespace Cyes__define_context {
	export class Dvariable extends Cvariable {
		constructor(data:Tyes__define_context['variable'], parent:Cyes__define_context) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context())
					.then(context => context?.component_root.output.binding_context()).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context())
					.then(context => context?.component_root.output.widget_configuration_context()).result!)
			}, 'variable')
		}
	}
}
export namespace Cscalar__widget {
	export class Dbinding_type extends StateGroup<
		{ name: 'binding', node:Cbinding__binding_type, init:Tbinding__binding_type}|
		{ name: 'configuration', node:Cconfiguration__binding_type, init:Tconfiguration__binding_type}|
		{ name: 'current time', node:Ccurrent_time, init:Tcurrent_time}|
		{ name: 'event', node:Cevent, init:Tevent}|
		{ name: 'list expression', node:Clist_expression, init:Tlist_expression}|
		{ name: 'list index', node:Clist_index, init:Tlist_index}|
		{ name: 'phrase', node:Cphrase, init:Tphrase}|
		{ name: 'static number', node:Cstatic_number, init:Tstatic_number}|
		{ name: 'static text', node:Cstatic_text, init:Tstatic_text}|
		{ name: 'unary expression', node:Cunary_expression, init:Tunary_expression}> {
		protected initializer(state: 'binding'|'configuration'|'current time'|'event'|'list expression'|'list index'|'phrase'|'static number'|'static text'|'unary expression') {
			switch (state) {
				case 'binding': return (init:Tbinding__binding_type, parent:Cscalar__widget) => new Cbinding__binding_type(init, parent);
				case 'configuration': return (init:Tconfiguration__binding_type, parent:Cscalar__widget) => new Cconfiguration__binding_type(init, parent);
				case 'current time': return (init:Tcurrent_time, parent:Cscalar__widget) => new Ccurrent_time(init, parent);
				case 'event': return (init:Tevent, parent:Cscalar__widget) => new Cevent(init, parent);
				case 'list expression': return (init:Tlist_expression, parent:Cscalar__widget) => new Clist_expression(init, parent);
				case 'list index': return (init:Tlist_index, parent:Cscalar__widget) => new Clist_index(init, parent);
				case 'phrase': return (init:Tphrase, parent:Cscalar__widget) => new Cphrase(init, parent);
				case 'static number': return (init:Tstatic_number, parent:Cscalar__widget) => new Cstatic_number(init, parent);
				case 'static text': return (init:Tstatic_text, parent:Cscalar__widget) => new Cstatic_text(init, parent);
				case 'unary expression': return (init:Tunary_expression, parent:Cscalar__widget) => new Cunary_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding'|'configuration'|'current time'|'event'|'list expression'|'list index'|'phrase'|'static number'|'static text'|'unary expression') {
			switch (state) {
				case 'binding': return finalize_binding__binding_type;
				case 'configuration': return finalize_configuration__binding_type;
				case 'current time': return finalize_current_time;
				case 'event': return finalize_event;
				case 'list expression': return finalize_list_expression;
				case 'list index': return finalize_list_index;
				case 'phrase': return finalize_phrase;
				case 'static number': return finalize_static_number;
				case 'static text': return finalize_static_text;
				case 'unary expression': return finalize_unary_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscalar__widget['binding type'], parent:Cscalar__widget) {
			super(data, parent, 'binding type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding type'`; }
	}
	export class Dformat extends StateGroup<
		{ name: 'no', node:Cno__format, init:Tno__format}|
		{ name: 'yes', node:Cyes__format, init:Tyes__format}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__format, parent:Cscalar__widget) => new Cno__format(init, parent);
				case 'yes': return (init:Tyes__format, parent:Cscalar__widget) => new Cyes__format(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__format;
				case 'yes': return finalize_yes__format;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscalar__widget['format'], parent:Cscalar__widget) {
			super(data, parent, 'format');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'format'`; }
	}
	export class Dtransform extends StateGroup<
		{ name: 'no', node:Cno__transform, init:Tno__transform}|
		{ name: 'yes', node:Cyes__transform, init:Tyes__transform}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__transform, parent:Cscalar__widget) => new Cno__transform(init, parent);
				case 'yes': return (init:Tyes__transform, parent:Cscalar__widget) => new Cyes__transform(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__transform;
				case 'yes': return finalize_yes__transform;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tscalar__widget['transform'], parent:Cscalar__widget) {
			super(data, parent, 'transform');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'transform'`; }
	}
}
export namespace Cbinding__binding_type {
	export class Dcontext extends Ccontext_selection {
		public readonly inferences:{
			node: () => client_bindings.Cnode__client_bindings
		}
		constructor(data:Tbinding__binding_type['context'], parent:Cbinding__binding_type) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
			this.inferences = {
				node: cache((detach:boolean) => {
					const widget__scalar__binding_type__binding__context_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.properties.context)
						.then(context => context?.component_root.output.result_binding_context())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as client_bindings.Cnode__client_bindings : undefined).result!;
				})

			}
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'key', node:Ckey, init:Tkey}|
		{ name: 'property', node:Cproperty, init:Tproperty}> {
		protected initializer(state: 'key'|'property') {
			switch (state) {
				case 'key': return (init:Tkey, parent:Cbinding__binding_type) => new Ckey(init, parent);
				case 'property': return (init:Tproperty, parent:Cbinding__binding_type) => new Cproperty(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'key'|'property') {
			switch (state) {
				case 'key': return finalize_key;
				case 'property': return finalize_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinding__binding_type['type'], parent:Cbinding__binding_type) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cproperty {
	export class Dproperty extends Reference<client_bindings.Cproperties,string> {

		constructor(data:string, $this:Cproperty) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.context?.inferences.node())
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__property, init:Tnumber__type__property}|
		{ name: 'reference', node:Creference, init:Treference}|
		{ name: 'text', node:Ctext__type__property, init:Ttext__type__property}> {
		protected initializer(state: 'number'|'reference'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__property, parent:Cproperty) => new Cnumber__type__property(init, parent);
				case 'reference': return (init:Treference, parent:Cproperty) => new Creference(init, parent);
				case 'text': return (init:Ttext__type__property, parent:Cproperty) => new Ctext__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'reference'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__property;
				case 'reference': return finalize_reference;
				case 'text': return finalize_text__type__property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty['type'], parent:Cproperty) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cconfiguration__binding_type {
	export class Dattribute extends Reference<widget.Cconfiguration__type__attributes__widget_configuration_node,string> {

		constructor(data:string, $this:Cconfiguration__binding_type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.result_widget_configuration_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dcontext extends Ccontext_selection {
		constructor(data:Tconfiguration__binding_type['context'], parent:Cconfiguration__binding_type) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__configuration__binding_type, init:Tnumber__type__configuration__binding_type}|
		{ name: 'text', node:Ctext__type__configuration__binding_type, init:Ttext__type__configuration__binding_type}> {
		protected initializer(state: 'number'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__configuration__binding_type, parent:Cconfiguration__binding_type) => new Cnumber__type__configuration__binding_type(init, parent);
				case 'text': return (init:Ttext__type__configuration__binding_type, parent:Cconfiguration__binding_type) => new Ctext__type__configuration__binding_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__configuration__binding_type;
				case 'text': return finalize_text__type__configuration__binding_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconfiguration__binding_type['type'], parent:Cconfiguration__binding_type) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccurrent_time {
	export class Dthrottle extends StateGroup<
		{ name: 'no', node:Cno__throttle, init:Tno__throttle}|
		{ name: 'yes', node:Cyes__throttle, init:Tyes__throttle}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__throttle, parent:Ccurrent_time) => new Cno__throttle(init, parent);
				case 'yes': return (init:Tyes__throttle, parent:Ccurrent_time) => new Cyes__throttle(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__throttle;
				case 'yes': return finalize_yes__throttle;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcurrent_time['throttle'], parent:Ccurrent_time) {
			super(data, parent, 'throttle');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'throttle'`; }
	}
}
export namespace Cyes__throttle {
	export class Dinterval extends AlanInteger {
		constructor(data:Tyes__throttle['interval'], parent:Cyes__throttle) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'interval'`; }
	}
}
export namespace Cevent {
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__event, init:Tnumber__type__event}|
		{ name: 'text', node:Ctext__type__event, init:Ttext__type__event}> {
		protected initializer(state: 'number'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__event, parent:Cevent) => new Cnumber__type__event(init, parent);
				case 'text': return (init:Ttext__type__event, parent:Cevent) => new Ctext__type__event(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__event;
				case 'text': return finalize_text__type__event;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tevent['type'], parent:Cevent) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Clist_expression {
	export class Doperation extends StateGroup<
		{ name: 'maximum', node:Cmaximum, init:Tmaximum}|
		{ name: 'minimum', node:Cminimum, init:Tminimum}|
		{ name: 'product', node:Cproduct, init:Tproduct}|
		{ name: 'sum', node:Csum, init:Tsum}> {
		protected initializer(state: 'maximum'|'minimum'|'product'|'sum') {
			switch (state) {
				case 'maximum': return (init:Tmaximum, parent:Clist_expression) => new Cmaximum(init, parent);
				case 'minimum': return (init:Tminimum, parent:Clist_expression) => new Cminimum(init, parent);
				case 'product': return (init:Tproduct, parent:Clist_expression) => new Cproduct(init, parent);
				case 'sum': return (init:Tsum, parent:Clist_expression) => new Csum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'maximum'|'minimum'|'product'|'sum') {
			switch (state) {
				case 'maximum': return finalize_maximum;
				case 'minimum': return finalize_minimum;
				case 'product': return finalize_product;
				case 'sum': return finalize_sum;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlist_expression['operation'], parent:Clist_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
	export class Dvalue extends Cone_or_more_expression {
		constructor(data:Tlist_expression['value'], parent:Clist_expression) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this).then(context => new Cbounds({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Clower_bound_cardinality.Pzero).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Cupper_bound_cardinality.Pmany).result!}), ).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cnumber_target.Pother)
					.then(context => context?.definitions.scalar_target)
					.then(context => context?.definitions.target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'value')
		}
	}
}
export namespace Clist_index {
	export class Dcontext extends Ccontext_selection {
		public readonly inferences:{
			list: () => widget.Clist__type
		}
		constructor(data:Tlist_index['context'], parent:Clist_index) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'context')
			this.inferences = {
				list: cache((detach:boolean) => {
					const widget__scalar__binding_type__list_index__context_nval = this;
					return resolve(this)
						.then(context => widget__scalar__binding_type__list_index__context_nval)
						.then(context => context?.component_root.output.result_widget_configuration_node())
						.then(context => context?.component_root.output.location())
						.then(context => context?.variant.name === 'configuration list' ? context.variant.definition as widget.Clist__type : undefined).result!;
				})

			}
		}
	}
}
export namespace Cphrase {
	export class Dphrase extends Reference<phrases.Cphrases__,string> {

		constructor(data:string, $this:Cphrase) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.phrases)
				.then(context => {
					const entry = context?.properties.phrases.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'phrase'`; }
	}
}
export namespace Cstatic_number {
	export class Dvalue extends AlanInteger {
		constructor(data:Tstatic_number['value'], parent:Cstatic_number) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstatic_text {
}
export namespace Cunary_expression {
	export class Dtype extends StateGroup<
		{ name: 'absolute value', node:Cabsolute_value, init:Tabsolute_value}|
		{ name: 'sign inversion', node:Csign_inversion, init:Tsign_inversion}> {
		protected initializer(state: 'absolute value'|'sign inversion') {
			switch (state) {
				case 'absolute value': return (init:Tabsolute_value, parent:Cunary_expression) => new Cabsolute_value(init, parent);
				case 'sign inversion': return (init:Tsign_inversion, parent:Cunary_expression) => new Csign_inversion(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'absolute value'|'sign inversion') {
			switch (state) {
				case 'absolute value': return finalize_absolute_value;
				case 'sign inversion': return finalize_sign_inversion;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tunary_expression['type'], parent:Cunary_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dvalue extends Cscalar__widget {
		constructor(data:Tunary_expression['value'], parent:Cunary_expression) {
			super(data, parent, {
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.target()).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'value')
		}
	}
}
export namespace Cyes__format {
	export class Dformatter extends Reference<client_bindings.Cformatters,string> {
		public readonly inferences:{
			expected_value_type: () => widget.Cscalar_value
		}

		constructor(data:string, $this:Cyes__format) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.formatters.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				expected_value_type: cache((detach:boolean) => {
					const widget__scalar__format__yes__formatter_nval = $this.properties.formatter.ref;
					return resolve($this.properties.formatter.ref)
						.then(context => widget__scalar__format__yes__formatter_nval)
						.then(context => context?.properties.from)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'number': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(() => $this)
												.then(context => context?.parent)
												.then(context => context?.properties.transform?.state.node.output.transformed_value()).result;
											const right = resolve(context)
												.then(() => $this)
												.then(() => widget.Cscalar_value.Pnumber).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'text': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(() => $this)
												.then(context => context?.parent)
												.then(context => context?.properties.transform?.state.node.output.transformed_value()).result;
											const right = resolve(context)
												.then(() => $this)
												.then(() => widget.Cscalar_value.Ptext).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'formatter'`; }
	}
}
export namespace Cyes__transform {
	export class Dtransformer extends Reference<client_bindings.Ctransformers,string> {
		public readonly inferences:{
			expected_value_type: () => widget.Cscalar_value
		}

		constructor(data:string, $this:Cyes__transform) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.transformers.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				expected_value_type: cache((detach:boolean) => {
					const widget__scalar__transform__yes__transformer_nval = $this.properties.transformer.ref;
					return resolve($this.properties.transformer.ref)
						.then(context => widget__scalar__transform__yes__transformer_nval)
						.then(context => context?.properties.from)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'number': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(() => $this)
												.then(context => context?.parent)
												.then(context => context?.properties.binding_type?.state.node.output.value()).result;
											const right = resolve(context)
												.then(() => $this)
												.then(() => widget.Cscalar_value.Pnumber).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case 'text': {
									return resolve(context)
										.then(context => {
											const left = resolve(context)
												.then(() => $this)
												.then(context => context?.parent)
												.then(context => context?.properties.binding_type?.state.node.output.value()).result;
											const right = resolve(context)
												.then(() => $this)
												.then(() => widget.Cscalar_value.Ptext).result;
											return left?.is(right!) ? left : undefined
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'transformer'`; }
	}
}
export namespace Ctext_argument {
	export class Dexpression extends Cexpression {
		constructor(data:Ttext_argument['expression'], parent:Ctext_argument) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this).then(context => new Cbounds({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Clower_bound_cardinality.Pzero).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Cupper_bound_cardinality.Pmany).result!}), ).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.control_instruction())
					.then(context => {
						const conv_context = resolve(context).result!;
						return new Ctext_target({name: 'event', definition: conv_context});
					})
					.then(context => context?.definitions.scalar_target)
					.then(context => context?.definitions.target).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
}
export namespace Cvariable {
	export class Dcontext extends Ccontext_selection {
		constructor(data:Tvariable['context'], parent:Cvariable) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.implicit_implementation_context)
					.then(context => context?.definitions.widget_implementation_context).result!)
			}, 'context')
		}
	}
	export class Didentifier extends StateGroup<
		{ name: 'anonymous', node:Canonymous__identifier, init:Tanonymous__identifier}|
		{ name: 'named', node:Cnamed__identifier, init:Tnamed__identifier}> {
		protected initializer(state: 'anonymous'|'named') {
			switch (state) {
				case 'anonymous': return (init:Tanonymous__identifier, parent:Cvariable) => new Canonymous__identifier(init, parent);
				case 'named': return (init:Tnamed__identifier, parent:Cvariable) => new Cnamed__identifier(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'anonymous'|'named') {
			switch (state) {
				case 'anonymous': return finalize_anonymous__identifier;
				case 'named': return finalize_named__identifier;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvariable['identifier'], parent:Cvariable) {
			super(data, parent, 'identifier');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'identifier'`; }
	}
	export class Dlet_declarations extends Clet_declarations__widget {
		constructor(data:Tvariable['let declarations'], parent:Cvariable) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.widget_implementation_frame)
					.then(context => context?.definitions.widget_implementation_context).result!)
			}, 'let declarations')
		}
	}
	export class Dmore_variables extends StateGroup<
		{ name: 'no', node:Cno__more_variables, init:Tno__more_variables}|
		{ name: 'yes', node:Cyes__more_variables, init:Tyes__more_variables}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__more_variables, parent:Cvariable) => new Cno__more_variables(init, parent);
				case 'yes': return (init:Tyes__more_variables, parent:Cvariable) => new Cyes__more_variables(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__more_variables;
				case 'yes': return finalize_yes__more_variables;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvariable['more variables'], parent:Cvariable) {
			super(data, parent, 'more variables');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'more variables'`; }
	}
}
export namespace Cnamed__identifier {
	export class Dname extends Reference<widget.Cnamed_values,string> {
		public readonly inferences:{
			single_name: () => widget.Cno__has_successor
		}

		constructor(data:string, $this:Cnamed__identifier) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.named_values.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				single_name: cache((detach:boolean) => {
					const widget__variable__identifier__named__name_nval = $this.properties.name.ref;
					return resolve($this.properties.name.ref)
						.then(context => widget__variable__identifier__named__name_nval)
						.then(context => {
							if (context?.properties.has_successor.state.name === 'no') {
								return resolve(context.properties.has_successor.state.node as widget.Cno__has_successor).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'name'`; }
	}
	export class Dnamed_values extends AlanDictionary<{ node:Cnamed_values, init:Tnamed_values},Cnamed__identifier> {
		protected initialize(parent:Cnamed__identifier, key:string, entry_init:Tnamed_values) { return new Cnamed_values(key, entry_init, parent); }
		protected finalize = finalize_named_values
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'named values'`; }
		constructor(data:Tnamed__identifier['named values'], parent:Cnamed__identifier) {
			super(data, parent);
		}
	}
}
export namespace Cnamed_values {
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor, init:Tno__has_successor}|
		{ name: 'yes', node:Cyes__has_successor, init:Tyes__has_successor}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor, parent:Cnamed_values) => new Cno__has_successor(init, parent);
				case 'yes': return (init:Tyes__has_successor, parent:Cnamed_values) => new Cyes__has_successor(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor;
				case 'yes': return finalize_yes__has_successor;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnamed_values['has successor'], parent:Cnamed_values) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
}
export namespace Cyes__more_variables {
	export class Dvariable extends Cvariable {
		constructor(data:Tyes__more_variables['variable'], parent:Cyes__more_variables) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context)
					.then(context => context?.component_root.output.result_binding_context()).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.widget_implementation_frame)
					.then(context => context?.definitions.widget_implementation_context).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context)
					.then(context => context?.component_root.output.result_widget_configuration_node()).result!)
			}, 'variable')
		}
	}
}
export namespace Cvariable_assignment {
	export class Dassign extends StateGroup<
		{ name: 'no', node:Cno__assign, init:Tno__assign}|
		{ name: 'yes', node:Cyes__assign, init:Tyes__assign}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__assign, parent:Cvariable_assignment) => new Cno__assign(init, parent);
				case 'yes': return (init:Tyes__assign, parent:Cvariable_assignment) => new Cyes__assign(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__assign;
				case 'yes': return finalize_yes__assign;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvariable_assignment['assign'], parent:Cvariable_assignment) {
			super(data, parent, 'assign');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'assign'`; }
	}
}
export namespace Cyes__assign {
	export class Dvariable extends Cvariable {
		constructor(data:Tyes__assign['variable'], parent:Cyes__assign) {
			super(data, parent, {
				binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.binding_context()).result!),
				current_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.current_context()).result!),
				widget_configuration_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_configuration_context()).result!)
			}, 'variable')
		}
	}
}
export namespace Cwidget_configuration_node {
	export class Dattributes extends AlanDictionary<{ node:Cattributes__widget_configuration_node, init:Tattributes__widget_configuration_node},Cwidget_configuration_node> {
		protected initialize(parent:Cwidget_configuration_node, key:string, entry_init:Tattributes__widget_configuration_node) { return new Cattributes__widget_configuration_node(key, entry_init, parent); }
		protected finalize = finalize_attributes__widget_configuration_node
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Twidget_configuration_node['attributes'], parent:Cwidget_configuration_node) {
			super(data, parent);
		}
	}
}
export namespace Cattributes__widget_configuration_node {
	export class Dswitch_client_binding_context extends StateGroup<
		{ name: 'no', node:Cno__switch_client_binding_context__attributes, init:Tno__switch_client_binding_context__attributes}|
		{ name: 'yes', node:Cyes__switch_client_binding_context__attributes, init:Tyes__switch_client_binding_context__attributes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__switch_client_binding_context__attributes, parent:Cattributes__widget_configuration_node) => new Cno__switch_client_binding_context__attributes(init, parent);
				case 'yes': return (init:Tyes__switch_client_binding_context__attributes, parent:Cattributes__widget_configuration_node) => new Cyes__switch_client_binding_context__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__switch_client_binding_context__attributes;
				case 'yes': return finalize_yes__switch_client_binding_context__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes__widget_configuration_node['switch client binding context'], parent:Cattributes__widget_configuration_node) {
			super(data, parent, 'switch client binding context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'switch client binding context'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'binding', node:Cbinding__type__attributes, init:Tbinding__type__attributes}|
		{ name: 'configuration', node:Cconfiguration__type__attributes__widget_configuration_node, init:Tconfiguration__type__attributes__widget_configuration_node}|
		{ name: 'view', node:Cview__type__attributes, init:Tview__type__attributes}|
		{ name: 'widget', node:Cwidget__type, init:Twidget__type}|
		{ name: 'window', node:Cwindow__type, init:Twindow__type}> {
		protected initializer(state: 'binding'|'configuration'|'view'|'widget'|'window') {
			switch (state) {
				case 'binding': return (init:Tbinding__type__attributes, parent:Cattributes__widget_configuration_node) => new Cbinding__type__attributes(init, parent);
				case 'configuration': return (init:Tconfiguration__type__attributes__widget_configuration_node, parent:Cattributes__widget_configuration_node) => new Cconfiguration__type__attributes__widget_configuration_node(init, parent);
				case 'view': return (init:Tview__type__attributes, parent:Cattributes__widget_configuration_node) => new Cview__type__attributes(init, parent);
				case 'widget': return (init:Twidget__type, parent:Cattributes__widget_configuration_node) => new Cwidget__type(init, parent);
				case 'window': return (init:Twindow__type, parent:Cattributes__widget_configuration_node) => new Cwindow__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'binding'|'configuration'|'view'|'widget'|'window') {
			switch (state) {
				case 'binding': return finalize_binding__type__attributes;
				case 'configuration': return finalize_configuration__type__attributes__widget_configuration_node;
				case 'view': return finalize_view__type__attributes;
				case 'widget': return finalize_widget__type;
				case 'window': return finalize_window__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes__widget_configuration_node['type'], parent:Cattributes__widget_configuration_node) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__switch_client_binding_context__attributes {
	export class Dconstrained_on_containing_binding extends StateGroup<
		{ name: 'no', node:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, init:Tno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes}|
		{ name: 'yes', node:Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, init:Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, parent:Cyes__switch_client_binding_context__attributes) => new Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes(init, parent);
				case 'yes': return (init:Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, parent:Cyes__switch_client_binding_context__attributes) => new Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__constrained_on_containing_binding__yes__switch_client_binding_context__attributes;
				case 'yes': return finalize_yes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__switch_client_binding_context__attributes['constrained on containing binding'], parent:Cyes__switch_client_binding_context__attributes) {
			super(data, parent, 'constrained on containing binding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'`; }
	}
	export class Dtype_path extends Cclient_binding_type_path {
		constructor(data:Tyes__switch_client_binding_context__attributes['type path'], parent:Cyes__switch_client_binding_context__attributes) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.node()).result!)
			}, 'type path')
		}
	}
}
export namespace Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes {
	export class Dinstance_binding extends Reference<client_bindings.Cbindings__client_bindings,string> {
		public readonly inferences:{
			instance_binding: () => client_bindings.Cno__constrained_on_containing_binding
		}

		constructor(data:string, $this:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				instance_binding: cache((detach:boolean) => {
					const widget__widget_configuration_node__attributes__switch_client_binding_context__yes__constrained_on_containing_binding__no__instance_binding_nval = $this.properties.instance_binding.ref;
					return resolve($this.properties.instance_binding.ref)
						.then(context => widget__widget_configuration_node__attributes__switch_client_binding_context__yes__constrained_on_containing_binding__no__instance_binding_nval)
						.then(context => {
							if (context?.properties.constrained_on_containing_binding.state.name === 'no') {
								return resolve(context.properties.constrained_on_containing_binding.state.node as client_bindings.Cno__constrained_on_containing_binding).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instance binding'`; }
	}
}
export namespace Cbinding__type__attributes {
	export class Dconstrained_on_containing_binding extends StateGroup<
		{ name: 'no', node:Cno__constrained_on_containing_binding__binding, init:Tno__constrained_on_containing_binding__binding}|
		{ name: 'yes', node:Cyes__constrained_on_containing_binding__binding, init:Tyes__constrained_on_containing_binding__binding}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__constrained_on_containing_binding__binding, parent:Cbinding__type__attributes) => new Cno__constrained_on_containing_binding__binding(init, parent);
				case 'yes': return (init:Tyes__constrained_on_containing_binding__binding, parent:Cbinding__type__attributes) => new Cyes__constrained_on_containing_binding__binding(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__constrained_on_containing_binding__binding;
				case 'yes': return finalize_yes__constrained_on_containing_binding__binding;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tbinding__type__attributes['constrained on containing binding'], parent:Cbinding__type__attributes) {
			super(data, parent, 'constrained on containing binding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'`; }
	}
	export class Dnode extends Cwidget_configuration_node {
		constructor(data:Tbinding__type__attributes['node'], parent:Cbinding__type__attributes) {
			super(data, parent, {
				client_binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.constrained_on_containing_binding?.state.node.output.client_binding())
					.then(context => context?.properties.binding)
					.then(context => context?.definitions.binding_context).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.widget_configuration_node_location).result!),
				requirement: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cconfiguration_requirement.Prequired).result!)
			}, 'node')
		}
	}
}
export namespace Cno__constrained_on_containing_binding__binding {
	export class Dinstance_binding extends Reference<client_bindings.Cbindings__client_bindings,string> {
		public readonly inferences:{
			instance_binding: () => client_bindings.Cno__constrained_on_containing_binding
		}

		constructor(data:string, $this:Cno__constrained_on_containing_binding__binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				instance_binding: cache((detach:boolean) => {
					const widget__widget_configuration_node__attributes__type__binding__constrained_on_containing_binding__no__instance_binding_nval = $this.properties.instance_binding.ref;
					return resolve($this.properties.instance_binding.ref)
						.then(context => widget__widget_configuration_node__attributes__type__binding__constrained_on_containing_binding__no__instance_binding_nval)
						.then(context => {
							if (context?.properties.constrained_on_containing_binding.state.name === 'no') {
								return resolve(context.properties.constrained_on_containing_binding.state.node as client_bindings.Cno__constrained_on_containing_binding).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instance binding'`; }
	}
}
export namespace Cyes__constrained_on_containing_binding__binding {
	export class Dinstance_binding extends Reference<client_bindings.Cbindings__node,string> {

		constructor(data:string, $this:Cyes__constrained_on_containing_binding__binding) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.type_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instance binding'`; }
	}
	export class Dtype_path extends Cclient_binding_type_path {
		constructor(data:Tyes__constrained_on_containing_binding__binding['type path'], parent:Cyes__constrained_on_containing_binding__binding) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.node()).result!)
			}, 'type path')
		}
	}
}
export namespace Cconfiguration__type__attributes__widget_configuration_node {
	export class Dtype extends StateGroup<
		{ name: 'list', node:Clist__type, init:Tlist__type}|
		{ name: 'number', node:Cnumber__type__configuration__type__attributes__widget_configuration_node, init:Tnumber__type__configuration__type__attributes__widget_configuration_node}|
		{ name: 'state group', node:Cstate_group__type__configuration__type__attributes__widget_configuration_node, init:Tstate_group__type__configuration__type__attributes__widget_configuration_node}|
		{ name: 'text', node:Ctext__type__configuration__type__attributes__widget_configuration_node, init:Ttext__type__configuration__type__attributes__widget_configuration_node}> {
		protected initializer(state: 'list'|'number'|'state group'|'text') {
			switch (state) {
				case 'list': return (init:Tlist__type, parent:Cconfiguration__type__attributes__widget_configuration_node) => new Clist__type(init, parent);
				case 'number': return (init:Tnumber__type__configuration__type__attributes__widget_configuration_node, parent:Cconfiguration__type__attributes__widget_configuration_node) => new Cnumber__type__configuration__type__attributes__widget_configuration_node(init, parent);
				case 'state group': return (init:Tstate_group__type__configuration__type__attributes__widget_configuration_node, parent:Cconfiguration__type__attributes__widget_configuration_node) => new Cstate_group__type__configuration__type__attributes__widget_configuration_node(init, parent);
				case 'text': return (init:Ttext__type__configuration__type__attributes__widget_configuration_node, parent:Cconfiguration__type__attributes__widget_configuration_node) => new Ctext__type__configuration__type__attributes__widget_configuration_node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'list'|'number'|'state group'|'text') {
			switch (state) {
				case 'list': return finalize_list__type;
				case 'number': return finalize_number__type__configuration__type__attributes__widget_configuration_node;
				case 'state group': return finalize_state_group__type__configuration__type__attributes__widget_configuration_node;
				case 'text': return finalize_text__type__configuration__type__attributes__widget_configuration_node;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconfiguration__type__attributes__widget_configuration_node['type'], parent:Cconfiguration__type__attributes__widget_configuration_node) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Clist__type {
	export class Dnode extends Cwidget_configuration_node {
		constructor(data:Tlist__type['node'], parent:Clist__type) {
			super(data, parent, {
				client_binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.switch_client_binding_context?.state.node.output.client_binding()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.widget_configuration_node_location).result!),
				requirement: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cconfiguration_requirement.Prequired).result!)
			}, 'node')
		}
	}
}
export namespace Cnumber__type__configuration__type__attributes__widget_configuration_node {
	export class Dhas_default extends StateGroup<
		{ name: 'no', node:Cno__has_default__number, init:Tno__has_default__number}|
		{ name: 'yes', node:Cyes__has_default__number, init:Tyes__has_default__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_default__number, parent:Cnumber__type__configuration__type__attributes__widget_configuration_node) => new Cno__has_default__number(init, parent);
				case 'yes': return (init:Tyes__has_default__number, parent:Cnumber__type__configuration__type__attributes__widget_configuration_node) => new Cyes__has_default__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_default__number;
				case 'yes': return finalize_yes__has_default__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__configuration__type__attributes__widget_configuration_node['has default'], parent:Cnumber__type__configuration__type__attributes__widget_configuration_node) {
			super(data, parent, 'has default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has default'`; }
	}
	export class Dpersistence extends Cconfiguration_attribute_persistence {
		constructor(data:Tnumber__type__configuration__type__attributes__widget_configuration_node['persistence'], parent:Cnumber__type__configuration__type__attributes__widget_configuration_node) {
			super(data, parent, 'persistence')
		}
	}
}
export namespace Cyes__has_default__number {
	export class Dvalue extends AlanInteger {
		constructor(data:Tyes__has_default__number['value'], parent:Cyes__has_default__number) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstate_group__type__configuration__type__attributes__widget_configuration_node {
	export class Dhas_default extends StateGroup<
		{ name: 'no', node:Cno__has_default__state_group, init:Tno__has_default__state_group}|
		{ name: 'yes', node:Cyes__has_default__state_group, init:Tyes__has_default__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_default__state_group, parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) => new Cno__has_default__state_group(init, parent);
				case 'yes': return (init:Tyes__has_default__state_group, parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) => new Cyes__has_default__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_default__state_group;
				case 'yes': return finalize_yes__has_default__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__configuration__type__attributes__widget_configuration_node['has default'], parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) {
			super(data, parent, 'has default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has default'`; }
	}
	export class Dpersistence extends Cconfiguration_attribute_persistence {
		constructor(data:Tstate_group__type__configuration__type__attributes__widget_configuration_node['persistence'], parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) {
			super(data, parent, 'persistence')
		}
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__state_group, init:Tstates__state_group},Cstate_group__type__configuration__type__attributes__widget_configuration_node> {
		protected initialize(parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node, key:string, entry_init:Tstates__state_group) { return new Cstates__state_group(key, entry_init, parent); }
		protected finalize = finalize_states__state_group
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__configuration__type__attributes__widget_configuration_node['states'], parent:Cstate_group__type__configuration__type__attributes__widget_configuration_node) {
			super(data, parent);
		}
	}
}
export namespace Cyes__has_default__state_group {
	export class Dstate extends Reference<widget.Cstates__state_group,string> {

		constructor(data:string, $this:Cyes__has_default__state_group) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cstates__state_group {
	export class Dnode extends Cwidget_configuration_node {
		constructor(data:Tstates__state_group['node'], parent:Cstates__state_group) {
			super(data, parent, {
				client_binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.switch_client_binding_context?.state.node.output.client_binding()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.widget_configuration_node_location).result!),
				requirement: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state_default?.inferences.requirement()).result!)
			}, 'node')
		}
	}
	export class Dstate_default extends Coptional_state_HACK {
		public readonly inferences:{
			requirement: () => widget.Cconfiguration_requirement
		}
		constructor(data:Tstates__state_group['state default'], parent:Cstates__state_group) {
			super(data, parent, 'state default')
			this.inferences = {
				requirement: cache((detach:boolean) => {
					const widget__widget_configuration_node__attributes__type__configuration__type__state_group__states__state_default_nval = this;
					return resolve(this)
						.then(() => parent)
						.then(context => context?.parent)
						.then(context => context?.properties.has_default)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'no': {
									return resolve(context)
										.then(() => parent)
										.then(() => widget.Cconfiguration_requirement.Prequired).result;
								}
								case 'yes': {
									const widget__widget_configuration_node__attributes__type__configuration__type__state_group__states__state_default_inf___requirement___yes_nval = state_group.cast('yes');
									return resolve(context)
										.then(match_context => { 
											const expression_context = resolve(match_context)
											.then(context => {
												const left = resolve(context)
													.then(() => parent).result;
												const right = resolve(context)
													.then(context => widget__widget_configuration_node__attributes__type__configuration__type__state_group__states__state_default_inf___requirement___yes_nval)
													.then(context => context?.properties.state?.ref).result;
												return left?.is(right!) ? left : undefined
											}).result;
											if (expression_context !== undefined) {
											const widget__widget_configuration_node__attributes__type__configuration__type__state_group__states__state_default_inf___requirement___yes___struct_nval = expression_context;
											return resolve(match_context)
											.then(() => parent)
											.then(() => widget.Cconfiguration_requirement.Poptional).result
											} else {
												return resolve(match_context)
												.then(() => parent)
												.then(() => widget.Cconfiguration_requirement.Prequired).result
											}
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result!;
				})

			}
		}
	}
}
export namespace Ctext__type__configuration__type__attributes__widget_configuration_node {
	export class Dhas_default extends StateGroup<
		{ name: 'no', node:Cno__has_default__text, init:Tno__has_default__text}|
		{ name: 'yes', node:Cyes__has_default__text, init:Tyes__has_default__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_default__text, parent:Ctext__type__configuration__type__attributes__widget_configuration_node) => new Cno__has_default__text(init, parent);
				case 'yes': return (init:Tyes__has_default__text, parent:Ctext__type__configuration__type__attributes__widget_configuration_node) => new Cyes__has_default__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_default__text;
				case 'yes': return finalize_yes__has_default__text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__configuration__type__attributes__widget_configuration_node['has default'], parent:Ctext__type__configuration__type__attributes__widget_configuration_node) {
			super(data, parent, 'has default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has default'`; }
	}
	export class Dpersistence extends Cconfiguration_attribute_persistence {
		constructor(data:Ttext__type__configuration__type__attributes__widget_configuration_node['persistence'], parent:Ctext__type__configuration__type__attributes__widget_configuration_node) {
			super(data, parent, 'persistence')
		}
	}
}
export namespace Cyes__has_default__text {
	export class Dtype extends StateGroup<
		{ name: 'static', node:Cstatic__type, init:Tstatic__type}|
		{ name: 'unique id', node:Cunique_id, init:Tunique_id}> {
		protected initializer(state: 'static'|'unique id') {
			switch (state) {
				case 'static': return (init:Tstatic__type, parent:Cyes__has_default__text) => new Cstatic__type(init, parent);
				case 'unique id': return (init:Tunique_id, parent:Cyes__has_default__text) => new Cunique_id(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'static'|'unique id') {
			switch (state) {
				case 'static': return finalize_static__type;
				case 'unique id': return finalize_unique_id;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_default__text['type'], parent:Cyes__has_default__text) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstatic__type {
}
export namespace Cwindow__type {
	export class Dnode extends Cwidget_configuration_node {
		constructor(data:Twindow__type['node'], parent:Cwindow__type) {
			super(data, parent, {
				client_binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.client_binding_context()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.widget_configuration_node_location).result!),
				requirement: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cconfiguration_requirement.Prequired).result!)
			}, 'node')
		}
	}
}
export namespace Cwidget_implementation_attribute {
	export class Dexpression extends Cone_or_more_expression {
		constructor(data:Twidget_implementation_attribute['expression'], parent:Cwidget_implementation_attribute) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.attribute())
					.then(context => context?.properties.type)
					.then(context => {
						const state_group = context;
						switch (state_group?.state.name) {
							case 'control': {
								return resolve(context).then(context => new Cbounds({
									lower_bound_cardinality: () => resolve(parent)
										.then(() => parent)
										.then(() => widget.Clower_bound_cardinality.Pone).result!, 
									upper_bound_cardinality: () => resolve(parent)
										.then(() => parent)
										.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result;
							}
							case 'instruction': {
								return resolve(context).then(context => new Cbounds({
									lower_bound_cardinality: () => resolve(parent)
										.then(() => parent)
										.then(() => widget.Clower_bound_cardinality.Pzero).result!, 
									upper_bound_cardinality: () => resolve(parent)
										.then(() => parent)
										.then(() => widget.Cupper_bound_cardinality.Pmany).result!}), ).result;
							}
							case 'markup': {
								return resolve(context).then(context => new Cbounds({
									lower_bound_cardinality: () => resolve(parent)
										.then(() => parent)
										.then(() => widget.Clower_bound_cardinality.Pone).result!, 
									upper_bound_cardinality: () => resolve(parent)
										.then(() => parent)
										.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result;
							}
							case 'property': {
								const widget__widget_implementation_attribute__expression_in___bounds___property_nval = state_group.cast('property');
								return resolve(context)
									.then(context => widget__widget_implementation_attribute__expression_in___bounds___property_nval)
									.then(context => context?.properties.type)
									.then(context => {
										const state_group = context;
										switch (state_group?.state.name) {
											case 'collection': {
												return resolve(context).then(context => new Cbounds({
													lower_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Clower_bound_cardinality.Pzero).result!, 
													upper_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Cupper_bound_cardinality.Pmany).result!}), ).result;
											}
											case 'number': {
												return resolve(context).then(context => new Cbounds({
													lower_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Clower_bound_cardinality.Pone).result!, 
													upper_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result;
											}
											case 'state group': {
												return resolve(context).then(context => new Cbounds({
													lower_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Clower_bound_cardinality.Pone).result!, 
													upper_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result;
											}
											case 'text': {
												return resolve(context).then(context => new Cbounds({
													lower_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Clower_bound_cardinality.Pzero).result!, 
													upper_bound_cardinality: () => resolve(parent)
														.then(() => parent)
														.then(() => widget.Cupper_bound_cardinality.Pmany).result!}), ).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
										};}).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
						};}).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.attribute())
					.then(context => context?.properties.type)
					.then(context => {
						const state_group = context;
						switch (state_group?.state.name) {
							case 'control': {
								const widget__widget_implementation_attribute__expression_in___target___control_nval = state_group.cast('control');
								return resolve(context)
									.then(context => widget__widget_implementation_attribute__expression_in___target___control_nval)
									.then(context => context?.properties.control?.ref)
									.then(context => context?.key?.ref)
									.then(context => context?.properties.node)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Ctarget({name: 'create node', definition: conv_context});
									}).result;
							}
							case 'instruction': {
								const widget__widget_implementation_attribute__expression_in___target___instruction_nval = state_group.cast('instruction');
								return resolve(context)
									.then(context => widget__widget_implementation_attribute__expression_in___target___instruction_nval)
									.then(context => {
										const conv_context = resolve(context).result!;
										return new Ctarget({name: 'instruction attribute', definition: conv_context});
									}).result;
							}
							case 'markup': {
								return resolve(context)
									.then(() => parent)
									.then(() => widget.Ctarget.Pcontrol).result;
							}
							case 'property': {
								const widget__widget_implementation_attribute__expression_in___target___property_nval = state_group.cast('property');
								return resolve(context)
									.then(context => widget__widget_implementation_attribute__expression_in___target___property_nval)
									.then(context => context?.properties.type)
									.then(context => {
										const state_group = context;
										switch (state_group?.state.name) {
											case 'collection': {
												const widget__widget_implementation_attribute__expression_in___target___property___collection_nval = state_group.cast('collection');
												return resolve(context)
													.then(context => widget__widget_implementation_attribute__expression_in___target___property___collection_nval)
													.then(context => context?.properties.node)
													.then(context => {
														const conv_context = resolve(context).result!;
														return new Ctarget({name: 'create node', definition: conv_context});
													}).result;
											}
											case 'number': {
												return resolve(context)
													.then(() => parent)
													.then(() => widget.Cnumber_target.Pother)
													.then(context => context?.definitions.scalar_target)
													.then(context => context?.definitions.target).result;
											}
											case 'state group': {
												const widget__widget_implementation_attribute__expression_in___target___property___state_group_nval = state_group.cast('state group');
												return resolve(context)
													.then(context => widget__widget_implementation_attribute__expression_in___target___property___state_group_nval)
													.then(context => {
														const conv_context = resolve(context).result!;
														return new Ctarget({name: 'select state', definition: conv_context});
													}).result;
											}
											case 'text': {
												return resolve(context)
													.then(() => parent)
													.then(() => widget.Ctext_target.Pother)
													.then(context => context?.definitions.scalar_target)
													.then(context => context?.definitions.target).result;
											}
											case undefined: return undefined;
											default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
										};}).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
						};}).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
}
export namespace Cwidget_implementation_node {
	export class Dattributes extends AlanDictionary<{ node:Cattributes__widget_implementation_node, init:Tattributes__widget_implementation_node},Cwidget_implementation_node> {
		protected initialize(parent:Cwidget_implementation_node, key:string, entry_init:Tattributes__widget_implementation_node) { return new Cattributes__widget_implementation_node(key, entry_init, parent); }
		protected finalize = finalize_attributes__widget_implementation_node
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(widget__widget_implementation_node__attributes_key_nval:control_library.Cattributes__control_node):boolean {
				const widget__widget_implementation_node__attributes_cond_nval = widget__widget_implementation_node__attributes_key_nval;
				const condition_context = resolve(widget__widget_implementation_node__attributes_cond_nval)
				.then(context => widget__widget_implementation_node__attributes_cond_nval)
				.then(context => context?.properties.type?.state.node.output.requirement())
				.then(context => context?.variant.name === 'required' ? context.variant.definition as control_library.Crequired : undefined).result;
				if (condition_context === undefined) {
					return false;
				}
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.context_control_node())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Twidget_implementation_node['attributes'], parent:Cwidget_implementation_node) {
			super(data, parent);
		}
	}
}
export namespace Cattributes__widget_implementation_node {
	export class Dattribute extends Cwidget_implementation_attribute {
		constructor(data:Tattributes__widget_implementation_node['attribute'], parent:Cattributes__widget_implementation_node) {
			super(data, parent, {
				attribute: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.widget_implementation_context()).result!)
			}, 'attribute')
		}
	}
}
export namespace Cwidget {
	export class Dbinding_context extends StateGroup<
		{ name: 'none', node:Cnone__binding_context, init:Tnone__binding_context}|
		{ name: 'select', node:Cselect, init:Tselect}> {
		protected initializer(state: 'none'|'select') {
			switch (state) {
				case 'none': return (init:Tnone__binding_context, parent:Cwidget) => new Cnone__binding_context(init, parent);
				case 'select': return (init:Tselect, parent:Cwidget) => new Cselect(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'none'|'select') {
			switch (state) {
				case 'none': return finalize_none__binding_context;
				case 'select': return finalize_select;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twidget['binding context'], parent:Cwidget) {
			super(data, parent, 'binding context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding context'`; }
	}
	export class Dexpression extends Cexpression {
		constructor(data:Twidget['expression'], parent:Cwidget) {
			super(data, parent, {
				bounds: cache((detach:boolean) => resolve(this).then(context => new Cbounds({
					lower_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Clower_bound_cardinality.Pone).result!, 
					upper_bound_cardinality: () => resolve(parent)
						.then(() => parent)
						.then(() => widget.Cupper_bound_cardinality.Pone).result!}), ).result!),
				target: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Ctarget.Pcontrol).result!),
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.optional_context_definition)
					.then(context => context?.component_root.output.widget_implementation_context()).result!)
			}, 'expression')
		}
	}
	export class Doptional_context_definition extends Coptional_context_definition {
		constructor(data:Twidget['optional context definition'], parent:Cwidget) {
			super(data, parent, {
				widget_implementation_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.implicit_implementation_context)
					.then(context => context?.definitions.widget_implementation_context).result!)
			}, 'optional context definition')
		}
	}
	export class Dwidget extends Cwidget_configuration_node {
		constructor(data:Twidget['widget'], parent:Cwidget) {
			super(data, parent, {
				client_binding_context: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.binding_context?.state.node.output.binding_context()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.widget_configuration_node_location).result!),
				requirement: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => widget.Cconfiguration_requirement.Prequired).result!)
			}, 'widget')
		}
	}
}
export namespace Cselect {
	export class Dbinding extends Reference<client_bindings.Cbindings__client_bindings,string> {

		constructor(data:string, $this:Cselect) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'binding'`; }
	}
	export class Dswitch_client_binding_context extends StateGroup<
		{ name: 'no', node:Cno__switch_client_binding_context__select, init:Tno__switch_client_binding_context__select}|
		{ name: 'yes', node:Cyes__switch_client_binding_context__select, init:Tyes__switch_client_binding_context__select}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__switch_client_binding_context__select, parent:Cselect) => new Cno__switch_client_binding_context__select(init, parent);
				case 'yes': return (init:Tyes__switch_client_binding_context__select, parent:Cselect) => new Cyes__switch_client_binding_context__select(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__switch_client_binding_context__select;
				case 'yes': return finalize_yes__switch_client_binding_context__select;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tselect['switch client binding context'], parent:Cselect) {
			super(data, parent, 'switch client binding context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'switch client binding context'`; }
	}
}
export namespace Cyes__switch_client_binding_context__select {
	export class Dconstrained_on_containing_binding extends StateGroup<
		{ name: 'no', node:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select, init:Tno__constrained_on_containing_binding__yes__switch_client_binding_context__select}|
		{ name: 'yes', node:Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select, init:Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__select}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__constrained_on_containing_binding__yes__switch_client_binding_context__select, parent:Cyes__switch_client_binding_context__select) => new Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select(init, parent);
				case 'yes': return (init:Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__select, parent:Cyes__switch_client_binding_context__select) => new Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__constrained_on_containing_binding__yes__switch_client_binding_context__select;
				case 'yes': return finalize_yes__constrained_on_containing_binding__yes__switch_client_binding_context__select;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__switch_client_binding_context__select['constrained on containing binding'], parent:Cyes__switch_client_binding_context__select) {
			super(data, parent, 'constrained on containing binding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'constrained on containing binding'`; }
	}
}
export namespace Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select {
	export class Dinstance_binding extends Reference<client_bindings.Cbindings__client_bindings,string> {
		public readonly inferences:{
			instance_binding: () => client_bindings.Cno__constrained_on_containing_binding
		}

		constructor(data:string, $this:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.client_bindings)
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				instance_binding: cache((detach:boolean) => {
					const widget__binding_context__select__switch_client_binding_context__yes__constrained_on_containing_binding__no__instance_binding_nval = $this.properties.instance_binding.ref;
					return resolve($this.properties.instance_binding.ref)
						.then(context => widget__binding_context__select__switch_client_binding_context__yes__constrained_on_containing_binding__no__instance_binding_nval)
						.then(context => {
							if (context?.properties.constrained_on_containing_binding.state.name === 'no') {
								return resolve(context.properties.constrained_on_containing_binding.state.node as client_bindings.Cno__constrained_on_containing_binding).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instance binding'`; }
	}
}
export namespace Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select {
	export class Dinstance_binding extends Reference<client_bindings.Cbindings__node,string> {

		constructor(data:string, $this:Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.type_path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.bindings.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'instance binding'`; }
	}
	export class Dtype_path extends Cclient_binding_type_path {
		constructor(data:Tyes__constrained_on_containing_binding__yes__switch_client_binding_context__select['type path'], parent:Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.binding?.ref)
					.then(context => context?.properties.binding).result!)
			}, 'type path')
		}
	}
}
function finalize_bounds(obj:Cbounds, detach:boolean = false) {
}
function finalize_no__has_steps__client_binding_type_path(obj:Cno__has_steps__client_binding_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_binding__type__yes(obj:Cbinding__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__node>(obj.properties.instance_binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cyes__switchable_in_configuration_context_path>obj.properties.instance_binding.inferences.instance_binding)(detach) !== undefined || detach);
}
function finalize_collection__type(obj:Ccollection__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ccollection__type__properties>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__type(obj:Cstate__type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cstates>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cstate_group__type__properties>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__client_binding_type_path(obj:Cyes__has_steps__client_binding_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result_node)(detach) !== undefined || detach);
	finalize_client_binding_type_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'binding': finalize_binding__type__yes(obj.properties.type.state.node, detach); break;
		case 'collection': finalize_collection__type(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_client_binding_type_path(obj:Cclient_binding_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__client_binding_type_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__client_binding_type_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_collection_sort_location(obj:Ccollection_sort_location, detach:boolean = false) {
	finalize_target(obj.definitions.target, detach);
}
function finalize_no__persist(obj:Cno__persist, detach:boolean = false) {
}
function finalize_no__per_entry(obj:Cno__per_entry, detach:boolean = false) {
}
function finalize_yes__per_entry(obj:Cyes__per_entry, detach:boolean = false) {
}
function finalize_no__per_session(obj:Cno__per_session, detach:boolean = false) {
}
function finalize_yes__per_session(obj:Cyes__per_session, detach:boolean = false) {
}
function finalize_yes__persist(obj:Cyes__persist, detach:boolean = false) {
	switch (obj.properties.per_entry.state.name) {
		case 'no': finalize_no__per_entry(obj.properties.per_entry.state.node, detach); break;
		case 'yes': finalize_yes__per_entry(obj.properties.per_entry.state.node, detach); break;
	}
	switch (obj.properties.per_session.state.name) {
		case 'no': finalize_no__per_session(obj.properties.per_session.state.node, detach); break;
		case 'yes': finalize_yes__per_session(obj.properties.per_session.state.node, detach); break;
	}
}
function finalize_configuration_attribute_persistence(obj:Cconfiguration_attribute_persistence, detach:boolean = false) {
	switch (obj.properties.persist.state.name) {
		case 'no': finalize_no__persist(obj.properties.persist.state.node, detach); break;
		case 'yes': finalize_yes__persist(obj.properties.persist.state.node, detach); break;
	}
}
function finalize_configuration_requirement(obj:Cconfiguration_requirement, detach:boolean = false) {
}
function finalize_anonymous__context(obj:Canonymous__context, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Canonymous__identifier>obj.inferences.named_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cvariable>obj.inferences.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.result)(detach) !== undefined || detach);
}
function finalize_implicit(obj:Cimplicit, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cimplicit_implementation_context>obj.inferences.required_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.result)(detach) !== undefined || detach);
}
function finalize_named__context(obj:Cnamed__context, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cnamed__identifier>obj.inferences.named_variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cvariable>obj.inferences.variable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.result)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cnamed_values>(obj.properties.variable_name as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_step(obj:Cno__has_step, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.result)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'anonymous': finalize_anonymous__context(obj.properties.context.state.node, detach); break;
		case 'implicit': finalize_implicit(obj.properties.context.state.node, detach); break;
		case 'named': finalize_named__context(obj.properties.context.state.node, detach); break;
	}
}
function finalize_root(obj:Croot, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.result)(detach) !== undefined || detach);
}
function finalize_yes__has_step(obj:Cyes__has_step, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_frame>obj.inferences.valid_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.result)(detach) !== undefined || detach);
	finalize_context_ancestor_path(obj.properties.tail, detach);
}
function finalize_context_ancestor_path(obj:Ccontext_ancestor_path, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.result)(detach) !== undefined || detach);
	switch (obj.properties.has_step.state.name) {
		case 'no': finalize_no__has_step(obj.properties.has_step.state.node, detach); break;
		case 'root': finalize_root(obj.properties.has_step.state.node, detach); break;
		case 'yes': finalize_yes__has_step(obj.properties.has_step.state.node, detach); break;
	}
}
function finalize_engine_state(obj:Cengine_state, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.widget_configuration_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.engine_state_binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cengine_state__type>obj.properties.engine_state_binding.inferences.engine_state_binding)(detach) !== undefined || detach);
}
function finalize_other_context(obj:Cother_context, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.widget_configuration_node)(detach) !== undefined || detach);
	finalize_context_ancestor_path(obj.properties.ancestor_path, detach);
	finalize_context_selection_path(obj.properties.path, detach);
}
function finalize_context_selection(obj:Ccontext_selection, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.result_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.result_widget_configuration_node)(detach) !== undefined || detach);
	switch (obj.properties.change_context_to.state.name) {
		case 'engine state': finalize_engine_state(obj.properties.change_context_to.state.node, detach); break;
		case 'other context': finalize_other_context(obj.properties.change_context_to.state.node, detach); break;
	}
}
function finalize_no__has_steps__context_selection_path(obj:Cno__has_steps__context_selection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.result_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.result_widget_configuration_node)(detach) !== undefined || detach);
}
function finalize_client_binding(obj:Cclient_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.new_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.new_widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__node>(obj.properties.binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cyes__switchable_in_configuration_context_path>obj.properties.binding.inferences.binding)(detach) !== undefined || detach);
}
function finalize_constrained_configuration_attribute(obj:Cconstrained_configuration_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.new_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.new_widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cattributes__widget_configuration_node>(obj.properties.configuration_attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cbinding__type__attributes>obj.properties.configuration_attribute.inferences.binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cyes__constrained_on_containing_binding__binding>obj.properties.configuration_attribute.inferences.constrained)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.properties.configuration_attribute.inferences.valid_node_type)(detach) !== undefined || detach);
}
function finalize_unconstrained_configuration_attribute(obj:Cunconstrained_configuration_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.new_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.new_widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cattributes__widget_configuration_node>(obj.properties.configuration_attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cbinding__type__attributes>obj.properties.configuration_attribute.inferences.binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cno__constrained_on_containing_binding__binding>obj.properties.configuration_attribute.inferences.unconstrained)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__context_selection_path(obj:Cyes__has_steps__context_selection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.result_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.result_widget_configuration_node)(detach) !== undefined || detach);
	finalize_context_selection_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'client binding': finalize_client_binding(obj.properties.type.state.node, detach); break;
		case 'constrained configuration attribute': finalize_constrained_configuration_attribute(obj.properties.type.state.node, detach); break;
		case 'unconstrained configuration attribute': finalize_unconstrained_configuration_attribute(obj.properties.type.state.node, detach); break;
	}
}
function finalize_context_selection_path(obj:Ccontext_selection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.result_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.result_widget_configuration_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__context_selection_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__context_selection_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_client_binding_widget(obj:Cclient_binding_widget, detach:boolean = false) {
	finalize_context_selection(obj.properties.context, detach);
	assert((<(detach?:boolean) => client_bindings.Cyes__has_widget>obj.properties.context.inferences.has_widget)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.properties.context.inferences.node)(detach) !== undefined || detach);
}
function finalize_let_declaration(obj:Clet_declaration, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Clet_declarations__let_declarations>(obj.properties.declaration as any).resolve)(detach) !== undefined || detach);
	finalize_context_ancestor_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_frame>obj.properties.path.inferences.frame)(detach) !== undefined || detach);
}
function finalize_static__binding_type(obj:Cstatic__binding_type, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Ccontrol_library>(obj.properties.control as any).resolve)(detach) !== undefined || detach);
	finalize_widget_implementation_node(obj.properties.node_binding, detach);
}
function finalize_widget__binding_type(obj:Cwidget__binding_type, detach:boolean = false) {
	finalize_context_selection(obj.properties.context, detach);
	assert((<(detach?:boolean) => widget.Cwidget__type>(obj.properties.widget as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.properties.widget.inferences.valid_binding_context)(detach) !== undefined || detach);
}
function finalize_window__binding_type(obj:Cwindow__binding_type, detach:boolean = false) {
	finalize_control_binding(obj.properties.control_binding, detach);
	finalize_variable(obj.properties.variable, detach);
	assert((<(detach?:boolean) => widget.Cwindow__type>(obj.properties.window as any).resolve)(detach) !== undefined || detach);
}
function finalize_control_binding(obj:Ccontrol_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.binding_type.state.name) {
		case 'client binding widget': finalize_client_binding_widget(obj.properties.binding_type.state.node, detach); break;
		case 'let declaration': finalize_let_declaration(obj.properties.binding_type.state.node, detach); break;
		case 'static': finalize_static__binding_type(obj.properties.binding_type.state.node, detach); break;
		case 'widget': finalize_widget__binding_type(obj.properties.binding_type.state.node, detach); break;
		case 'window': finalize_window__binding_type(obj.properties.binding_type.state.node, detach); break;
	}
}
function finalize_control__type(obj:Ccontrol__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Ccontrol__target>obj.inferences.control_target)(detach) !== undefined || detach);
	finalize_control_binding(obj.properties.control_binding, detach);
}
function finalize_control_node(obj:Ccontrol_node, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Ccontrol_node>obj.inferences.create_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => control_library.Ccontrol_node>obj.output.control_node)(detach) !== undefined || detach);
}
function finalize_state__for(obj:Cstate__for, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cstate_group__type__property__type__attributes__control_node>obj.inferences.stategroup)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => control_library.Ccontrol_node>obj.output.control_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => control_library.Cstates>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_create_entry(obj:Ccreate_entry, detach:boolean = false) {
	switch (obj.properties.for_.state.name) {
		case 'control node': finalize_control_node(obj.properties.for_.state.node, detach); break;
		case 'state': finalize_state__for(obj.properties.for_.state.node, detach); break;
	}
	finalize_widget_implementation_node(obj.properties.node, detach);
}
function finalize_instruction(obj:Cinstruction, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cinstruction>obj.inferences.instruction_attribute)(detach) !== undefined || detach);
	finalize_instruction_selection(obj.properties.instruction, detach);
}
function finalize_cases__match(obj:Ccases__match, detach:boolean = false) {
	finalize_one_or_more_expression(obj.properties.false_, detach);
	finalize_one_or_more_expression(obj.properties.true_, detach);
}
function finalize_number__type__match(obj:Cnumber__type__match, detach:boolean = false) {
	finalize_scalar__widget(obj.properties.left_expression, detach);
	finalize_scalar__widget(obj.properties.right_expression, detach);
}
function finalize_text__type__match(obj:Ctext__type__match, detach:boolean = false) {
	finalize_scalar__widget(obj.properties.left_expression, detach);
	finalize_scalar__widget(obj.properties.right_expression, detach);
}
function finalize_match(obj:Cmatch, detach:boolean = false) {
	finalize_cases__match(obj.properties.cases, detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__match(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__match(obj.properties.type.state.node, detach); break;
	}
}
function finalize_cases__node_switch(obj:Ccases__node_switch, detach:boolean = false) {
	finalize_one_or_more_expression(obj.properties.node, detach);
	finalize_one_or_more_expression(obj.properties.none, detach);
	finalize_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_collection__from(obj:Ccollection__from, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ccollection__type__properties>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_scalar__widget(obj.properties.key, detach);
}
function finalize_list__from(obj:Clist__from, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.widget_configuration_context)(detach) !== undefined || detach);
	finalize_scalar__widget(obj.properties.index, detach);
	assert((<(detach?:boolean) => widget.Clist__type>(obj.properties.list as any).resolve)(detach) !== undefined || detach);
}
function finalize_node_switch(obj:Cnode_switch, detach:boolean = false) {
	finalize_cases__node_switch(obj.properties.cases, detach);
	finalize_context_selection(obj.properties.context, detach);
	switch (obj.properties.from.state.name) {
		case 'collection': finalize_collection__from(obj.properties.from.state.node, detach); break;
		case 'list': finalize_list__from(obj.properties.from.state.node, detach); break;
	}
}
function finalize_none__type(obj:Cnone__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Clower_bound_cardinality>obj.inferences.bounds)(detach) !== undefined || detach);
}
function finalize_scalar__type(obj:Cscalar__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_target>obj.inferences.scalar)(detach) !== undefined || detach);
	finalize_scalar__widget(obj.properties.value, detach);
}
function finalize_ascending(obj:Cascending, detach:boolean = false) {
}
function finalize_descending(obj:Cdescending, detach:boolean = false) {
}
function finalize_sort_collection(obj:Csort_collection, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Ccollection_sort_location>obj.inferences.collection_attribute)(detach) !== undefined || detach);
	finalize_context_selection(obj.properties.context, detach);
	assert((<(detach?:boolean) => client_bindings.Cyes__sortable>obj.properties.context.inferences.is_sortable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.properties.context.inferences.node)(detach) !== undefined || detach);
	switch (obj.properties.direction.state.name) {
		case 'ascending': finalize_ascending(obj.properties.direction.state.node, detach); break;
		case 'descending': finalize_descending(obj.properties.direction.state.node, detach); break;
	}
}
function finalize_states__binding(obj:Cstates__binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cstates>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_one_or_more_expression(obj.properties.next, detach);
	finalize_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_binding__type__switch(obj:Cbinding__type__switch, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cstate_group__type__properties>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__binding(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_states__configuration(obj:Cstates__configuration, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_one_or_more_expression(obj.properties.next, detach);
	finalize_variable_assignment(obj.properties.variable_assignment, detach);
}
function finalize_configuration__type__switch(obj:Cconfiguration__type__switch, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__configuration(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_switch(obj:Cswitch, detach:boolean = false) {
	finalize_context_selection(obj.properties.context, detach);
	switch (obj.properties.type.state.name) {
		case 'binding': finalize_binding__type__switch(obj.properties.type.state.node, detach); break;
		case 'configuration': finalize_configuration__type__switch(obj.properties.type.state.node, detach); break;
	}
}
function finalize_configuration__type__walk(obj:Cconfiguration__type__walk, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Clist__type>(obj.properties.list as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__sort(obj:Cno__sort, detach:boolean = false) {
}
function finalize_yes__sort(obj:Cyes__sort, detach:boolean = false) {
	finalize_expression(obj.properties.by, detach);
}
function finalize_widget_binding(obj:Cwidget_binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.widget_configuration_context)(detach) !== undefined || detach);
	finalize_collection_sort_location(obj.definitions.collection_sort_location, detach);
	assert((<(detach?:boolean) => client_bindings.Ccollection__type__properties>(obj.properties.collection_property as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.sort.state.name) {
		case 'no': finalize_no__sort(obj.properties.sort.state.node, detach); break;
		case 'yes': finalize_yes__sort(obj.properties.sort.state.node, detach); break;
	}
}
function finalize_walk(obj:Cwalk, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cupper_bound_cardinality>obj.inferences.bounds)(detach) !== undefined || detach);
	finalize_context_selection(obj.properties.context, detach);
	finalize_one_or_more_expression(obj.properties.entry_expression, detach);
	switch (obj.properties.type.state.name) {
		case 'configuration': finalize_configuration__type__walk(obj.properties.type.state.node, detach); break;
		case 'widget binding': finalize_widget_binding(obj.properties.type.state.node, detach); break;
	}
	finalize_variable(obj.properties.variable, detach);
}
function finalize_expression(obj:Cexpression, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cbounds>obj.input.bounds)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Ctarget>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'control': finalize_control__type(obj.properties.type.state.node, detach); break;
		case 'create entry': finalize_create_entry(obj.properties.type.state.node, detach); break;
		case 'instruction': finalize_instruction(obj.properties.type.state.node, detach); break;
		case 'match': finalize_match(obj.properties.type.state.node, detach); break;
		case 'node switch': finalize_node_switch(obj.properties.type.state.node, detach); break;
		case 'none': finalize_none__type(obj.properties.type.state.node, detach); break;
		case 'scalar': finalize_scalar__type(obj.properties.type.state.node, detach); break;
		case 'sort collection': finalize_sort_collection(obj.properties.type.state.node, detach); break;
		case 'switch': finalize_switch(obj.properties.type.state.node, detach); break;
		case 'walk': finalize_walk(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_next(obj:Cno__has_next, detach:boolean = false) {
}
function finalize_yes__has_next(obj:Cyes__has_next, detach:boolean = false) {
	finalize_expression_list(obj.properties.next, detach);
}
function finalize_expression_list(obj:Cexpression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cbounds>obj.input.bounds)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Ctarget>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	finalize_expression(obj.properties.expression, detach);
	switch (obj.properties.has_next.state.name) {
		case 'no': finalize_no__has_next(obj.properties.has_next.state.node, detach); break;
		case 'yes': finalize_yes__has_next(obj.properties.has_next.state.node, detach); break;
	}
	finalize_optional_context_definition(obj.properties.optional_context_definition, detach);
}
function finalize_implicit_implementation_context(obj:Cimplicit_implementation_context, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.explicit_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_context)(detach) !== undefined || detach);
	finalize_widget_implementation_context(obj.definitions.widget_implementation_context, detach);
}
function finalize_exists(obj:Cexists, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cexists>obj.inferences.next_argument_exists)(detach) !== undefined || detach);
	finalize_instruction_argument(obj.properties.argument, detach);
}
function finalize_no__next_argument(obj:Cno__next_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cno__next_argument>obj.inferences.no_next_argument)(detach) !== undefined || detach);
}
function finalize_file(obj:Cfile, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cfile>obj.inferences.control_file)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cfile__type__instruction_argument>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_number__type__instruction_argument(obj:Cnumber__type__instruction_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnumber__type__instruction_argument>obj.inferences.number)(detach) !== undefined || detach);
	finalize_number_argument(obj.properties.argument, detach);
}
function finalize_text__type__instruction_argument(obj:Ctext__type__instruction_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Ctext__type__instruction_argument>obj.inferences.text)(detach) !== undefined || detach);
	finalize_text_argument(obj.properties.argument, detach);
}
function finalize_view__type__instruction_argument(obj:Cview__type__instruction_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cview__type__instruction_argument>obj.inferences.view)(detach) !== undefined || detach);
	finalize_context_selection(obj.properties.context, detach);
	assert((<(detach?:boolean) => widget.Cview__type__attributes>(obj.properties.view_configuration as any).resolve)(detach) !== undefined || detach);
}
function finalize_instruction_argument(obj:Cinstruction_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cinstruction_argument>obj.input.client_bindings_argument)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => control_library.Cinstruction>obj.input.control_instruction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.next_argument.state.name) {
		case 'exists': finalize_exists(obj.properties.next_argument.state.node, detach); break;
		case 'no': finalize_no__next_argument(obj.properties.next_argument.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'file': finalize_file(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__instruction_argument(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__instruction_argument(obj.properties.type.state.node, detach); break;
		case 'view': finalize_view__type__instruction_argument(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number__type__configuration__type__attributes__instruction_argument_configuration_node(obj:Cnumber__type__configuration__type__attributes__instruction_argument_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node>obj.inferences.number_type)(detach) !== undefined || detach);
	finalize_number_argument(obj.properties.argument, detach);
}
function finalize_state_group__type__configuration__type__attributes__instruction_argument_configuration_node(obj:Cstate_group__type__configuration__type__attributes__instruction_argument_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node>obj.inferences.state_group_type)(detach) !== undefined || detach);
	finalize_instruction_argument_configuration_node(obj.properties.node, detach);
	assert((<(detach?:boolean) => widget.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__configuration__type__attributes__instruction_argument_configuration_node(obj:Ctext__type__configuration__type__attributes__instruction_argument_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Ctext__type__configuration__type__attributes__widget_configuration_node>obj.inferences.text_type)(detach) !== undefined || detach);
	finalize_text_argument(obj.properties.argument, detach);
}
function finalize_configuration__type__attributes__instruction_argument_configuration_node(obj:Cconfiguration__type__attributes__instruction_argument_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration__type__attributes__widget_configuration_node>obj.inferences.configuration_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__configuration__type__attributes__instruction_argument_configuration_node(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__configuration__type__attributes__instruction_argument_configuration_node(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__configuration__type__attributes__instruction_argument_configuration_node(obj.properties.type.state.node, detach); break;
	}
}
function finalize_attributes__instruction_argument_configuration_node(obj:Cattributes__instruction_argument_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cattributes__widget_configuration_node>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'configuration': finalize_configuration__type__attributes__instruction_argument_configuration_node(obj.properties.type.state.node, detach); break;
	}
}
function finalize_instruction_argument_configuration_node(obj:Cinstruction_argument_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cinstruction>obj.input.control_instruction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.attributes) {
		finalize_attributes__instruction_argument_configuration_node(entry, detach);
	}
	if (!detach) {
		(obj.properties.attributes as any).eval_required_keys(detach);
	}
}
function finalize_no__has_arguments(obj:Cno__has_arguments, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cno__has_arguments>obj.inferences.has_no_arguments)(detach) !== undefined || detach);
}
function finalize_yes__has_arguments(obj:Cyes__has_arguments, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cyes__has_arguments>obj.inferences.has_arguments)(detach) !== undefined || detach);
	finalize_instruction_argument(obj.properties.first_argument, detach);
}
function finalize_binding__configuration_attribute_type(obj:Cbinding__configuration_attribute_type, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	switch (obj.properties.has_arguments.state.name) {
		case 'no': finalize_no__has_arguments(obj.properties.has_arguments.state.node, detach); break;
		case 'yes': finalize_yes__has_arguments(obj.properties.has_arguments.state.node, detach); break;
	}
	assert((<(detach?:boolean) => client_bindings.Cinstructions>(obj.properties.instruction as any).resolve)(detach) !== undefined || detach);
}
function finalize_set_number(obj:Cset_number, detach:boolean = false) {
	finalize_number_argument(obj.properties.argument, detach);
	assert((<(detach?:boolean) => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node>(obj.properties.number as any).resolve)(detach) !== undefined || detach);
}
function finalize_set_state(obj:Cset_state, detach:boolean = false) {
	finalize_instruction_argument_configuration_node(obj.properties.node, detach);
	assert((<(detach?:boolean) => widget.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_set_text(obj:Cset_text, detach:boolean = false) {
	finalize_text_argument(obj.properties.argument, detach);
	assert((<(detach?:boolean) => widget.Ctext__type__configuration__type__attributes__widget_configuration_node>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_configuration__configuration_attribute_type(obj:Cconfiguration__configuration_attribute_type, detach:boolean = false) {
	switch (obj.properties.instruction.state.name) {
		case 'set number': finalize_set_number(obj.properties.instruction.state.node, detach); break;
		case 'set state': finalize_set_state(obj.properties.instruction.state.node, detach); break;
		case 'set text': finalize_set_text(obj.properties.instruction.state.node, detach); break;
	}
}
function finalize_instruction_selection(obj:Cinstruction_selection, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cinstruction>obj.input.control_instruction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.configuration_attribute_type.state.name) {
		case 'binding': finalize_binding__configuration_attribute_type(obj.properties.configuration_attribute_type.state.node, detach); break;
		case 'configuration': finalize_configuration__configuration_attribute_type(obj.properties.configuration_attribute_type.state.node, detach); break;
	}
	finalize_context_selection(obj.properties.context, detach);
}
function finalize_let_declarations__let_declarations(obj:Clet_declarations__let_declarations, detach:boolean = false) {
	finalize_expression(obj.properties.expression, detach);
}
function finalize_let_declarations__widget(obj:Clet_declarations__widget, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.let_declarations) {
		finalize_let_declarations__let_declarations(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_lower_bound_cardinality(obj:Clower_bound_cardinality, detach:boolean = false) {
}
function finalize_number_argument(obj:Cnumber_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cinstruction>obj.input.control_instruction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	finalize_expression(obj.properties.expression, detach);
}
function finalize_number_target(obj:Cnumber_target, detach:boolean = false) {
	finalize_scalar_target(obj.definitions.scalar_target, detach);
}
function finalize_plural(obj:Cplural, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cupper_bound_cardinality>obj.inferences.bounds)(detach) !== undefined || detach);
	finalize_expression_list(obj.properties.expression, detach);
}
function finalize_singular(obj:Csingular, detach:boolean = false) {
	finalize_expression(obj.properties.expression, detach);
}
function finalize_one_or_more_expression(obj:Cone_or_more_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cbounds>obj.input.bounds)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Ctarget>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.multiplicity.state.name) {
		case 'plural': finalize_plural(obj.properties.multiplicity.state.node, detach); break;
		case 'singular': finalize_singular(obj.properties.multiplicity.state.node, detach); break;
	}
}
function finalize_no__define_context(obj:Cno__define_context, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.widget_context)(detach) !== undefined || detach);
}
function finalize_yes__define_context(obj:Cyes__define_context, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.widget_context)(detach) !== undefined || detach);
	finalize_variable(obj.properties.variable, detach);
}
function finalize_optional_context_definition(obj:Coptional_context_definition, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.define_context.state.name) {
		case 'no': finalize_no__define_context(obj.properties.define_context.state.node, detach); break;
		case 'yes': finalize_yes__define_context(obj.properties.define_context.state.node, detach); break;
	}
}
function finalize_optional_state_HACK(obj:Coptional_state_HACK, detach:boolean = false) {
}
function finalize_key(obj:Ckey, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Centry_key>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_number__type__property(obj:Cnumber__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnumber__type__properties>obj.inferences.number_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_reference(obj:Creference, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Creference>obj.inferences.reference_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_text__type__property(obj:Ctext__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Ctext__type__properties>obj.inferences.text_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_property(obj:Cproperty, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cproperties>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__property(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_binding__binding_type(obj:Cbinding__binding_type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	finalize_context_selection(obj.properties.context, detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.properties.context.inferences.node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'key': finalize_key(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number__type__configuration__binding_type(obj:Cnumber__type__configuration__binding_type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node>obj.inferences.number_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_text__type__configuration__binding_type(obj:Ctext__type__configuration__binding_type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Ctext__type__configuration__type__attributes__widget_configuration_node>obj.inferences.text_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_configuration__binding_type(obj:Cconfiguration__binding_type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cconfiguration__type__attributes__widget_configuration_node>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	finalize_context_selection(obj.properties.context, detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__configuration__binding_type(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__configuration__binding_type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__throttle(obj:Cno__throttle, detach:boolean = false) {
}
function finalize_yes__throttle(obj:Cyes__throttle, detach:boolean = false) {
}
function finalize_current_time(obj:Ccurrent_time, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.throttle.state.name) {
		case 'no': finalize_no__throttle(obj.properties.throttle.state.node, detach); break;
		case 'yes': finalize_yes__throttle(obj.properties.throttle.state.node, detach); break;
	}
}
function finalize_number__type__event(obj:Cnumber__type__event, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cnumber__argument>obj.inferences.number_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_text__type__event(obj:Ctext__type__event, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Ctext__argument>obj.inferences.text_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_event(obj:Cevent, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cinstruction>obj.inferences.event)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__event(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__event(obj.properties.type.state.node, detach); break;
	}
}
function finalize_maximum(obj:Cmaximum, detach:boolean = false) {
}
function finalize_minimum(obj:Cminimum, detach:boolean = false) {
}
function finalize_product(obj:Cproduct, detach:boolean = false) {
}
function finalize_sum(obj:Csum, detach:boolean = false) {
}
function finalize_list_expression(obj:Clist_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.operation.state.name) {
		case 'maximum': finalize_maximum(obj.properties.operation.state.node, detach); break;
		case 'minimum': finalize_minimum(obj.properties.operation.state.node, detach); break;
		case 'product': finalize_product(obj.properties.operation.state.node, detach); break;
		case 'sum': finalize_sum(obj.properties.operation.state.node, detach); break;
	}
	finalize_one_or_more_expression(obj.properties.value, detach);
}
function finalize_list_index(obj:Clist_index, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	finalize_context_selection(obj.properties.context, detach);
	assert((<(detach?:boolean) => widget.Clist__type>obj.properties.context.inferences.list)(detach) !== undefined || detach);
}
function finalize_phrase(obj:Cphrase, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => phrases.Cphrases__>(obj.properties.phrase as any).resolve)(detach) !== undefined || detach);
}
function finalize_static_number(obj:Cstatic_number, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_static_text(obj:Cstatic_text, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
}
function finalize_absolute_value(obj:Cabsolute_value, detach:boolean = false) {
}
function finalize_sign_inversion(obj:Csign_inversion, detach:boolean = false) {
}
function finalize_unary_expression(obj:Cunary_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'absolute value': finalize_absolute_value(obj.properties.type.state.node, detach); break;
		case 'sign inversion': finalize_sign_inversion(obj.properties.type.state.node, detach); break;
	}
	finalize_scalar__widget(obj.properties.value, detach);
}
function finalize_no__format(obj:Cno__format, detach:boolean = false) {
}
function finalize_yes__format(obj:Cyes__format, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.inferences.expected_target_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cformatters>(obj.properties.formatter as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.properties.formatter.inferences.expected_value_type)(detach) !== undefined || detach);
}
function finalize_no__transform(obj:Cno__transform, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.inferences.expected_value_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.transformed_value)(detach) !== undefined || detach);
}
function finalize_yes__transform(obj:Cyes__transform, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.transformed_value)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Ctransformers>(obj.properties.transformer as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.properties.transformer.inferences.expected_value_type)(detach) !== undefined || detach);
}
function finalize_scalar__widget(obj:Cscalar__widget, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_target>obj.input.target)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.binding_type.state.name) {
		case 'binding': finalize_binding__binding_type(obj.properties.binding_type.state.node, detach); break;
		case 'configuration': finalize_configuration__binding_type(obj.properties.binding_type.state.node, detach); break;
		case 'current time': finalize_current_time(obj.properties.binding_type.state.node, detach); break;
		case 'event': finalize_event(obj.properties.binding_type.state.node, detach); break;
		case 'list expression': finalize_list_expression(obj.properties.binding_type.state.node, detach); break;
		case 'list index': finalize_list_index(obj.properties.binding_type.state.node, detach); break;
		case 'phrase': finalize_phrase(obj.properties.binding_type.state.node, detach); break;
		case 'static number': finalize_static_number(obj.properties.binding_type.state.node, detach); break;
		case 'static text': finalize_static_text(obj.properties.binding_type.state.node, detach); break;
		case 'unary expression': finalize_unary_expression(obj.properties.binding_type.state.node, detach); break;
	}
	switch (obj.properties.format.state.name) {
		case 'no': finalize_no__format(obj.properties.format.state.node, detach); break;
		case 'yes': finalize_yes__format(obj.properties.format.state.node, detach); break;
	}
	switch (obj.properties.transform.state.name) {
		case 'no': finalize_no__transform(obj.properties.transform.state.node, detach); break;
		case 'yes': finalize_yes__transform(obj.properties.transform.state.node, detach); break;
	}
}
function finalize_scalar_target(obj:Cscalar_target, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cscalar_value>obj.output.value)(detach) !== undefined || detach);
	finalize_target(obj.definitions.target, detach);
}
function finalize_scalar_value(obj:Cscalar_value, detach:boolean = false) {
}
function finalize_target(obj:Ctarget, detach:boolean = false) {
}
function finalize_text_argument(obj:Ctext_argument, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cinstruction>obj.input.control_instruction)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	finalize_expression(obj.properties.expression, detach);
}
function finalize_text_target(obj:Ctext_target, detach:boolean = false) {
	finalize_scalar_target(obj.definitions.scalar_target, detach);
}
function finalize_upper_bound_cardinality(obj:Cupper_bound_cardinality, detach:boolean = false) {
}
function finalize_anonymous__identifier(obj:Canonymous__identifier, detach:boolean = false) {
}
function finalize_no__has_successor(obj:Cno__has_successor, detach:boolean = false) {
}
function finalize_yes__has_successor(obj:Cyes__has_successor, detach:boolean = false) {
}
function finalize_named_values(obj:Cnamed_values, detach:boolean = false) {
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor(obj.properties.has_successor.state.node, detach); break;
	}
}
function finalize_named__identifier(obj:Cnamed__identifier, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cnamed_values>(obj.properties.name as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cno__has_successor>obj.properties.name.inferences.single_name)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.named_values) {
		finalize_named_values(entry, detach);
	}
	if (!detach) {
		if (obj.properties.named_values.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_no__more_variables(obj:Cno__more_variables, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.context)(detach) !== undefined || detach);
}
function finalize_yes__more_variables(obj:Cyes__more_variables, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.context)(detach) !== undefined || detach);
	finalize_variable(obj.properties.variable, detach);
}
function finalize_variable(obj:Cvariable, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.current_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.widget_implementation_context)(detach) !== undefined || detach);
	finalize_implicit_implementation_context(obj.definitions.implicit_implementation_context, detach);
	finalize_widget_implementation_frame(obj.definitions.widget_implementation_frame, detach);
	finalize_context_selection(obj.properties.context, detach);
	switch (obj.properties.identifier.state.name) {
		case 'anonymous': finalize_anonymous__identifier(obj.properties.identifier.state.node, detach); break;
		case 'named': finalize_named__identifier(obj.properties.identifier.state.node, detach); break;
	}
	finalize_let_declarations__widget(obj.properties.let_declarations, detach);
	switch (obj.properties.more_variables.state.name) {
		case 'no': finalize_no__more_variables(obj.properties.more_variables.state.node, detach); break;
		case 'yes': finalize_yes__more_variables(obj.properties.more_variables.state.node, detach); break;
	}
}
function finalize_no__assign(obj:Cno__assign, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.context)(detach) !== undefined || detach);
	finalize_implicit_implementation_context(obj.definitions.implicit_implementation_context, detach);
}
function finalize_yes__assign(obj:Cyes__assign, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.context)(detach) !== undefined || detach);
	finalize_variable(obj.properties.variable, detach);
}
function finalize_variable_assignment(obj:Cvariable_assignment, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.current_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.widget_implementation_context)(detach) !== undefined || detach);
	switch (obj.properties.assign.state.name) {
		case 'no': finalize_no__assign(obj.properties.assign.state.node, detach); break;
		case 'yes': finalize_yes__assign(obj.properties.assign.state.node, detach); break;
	}
}
function finalize_no__switch_client_binding_context__attributes(obj:Cno__switch_client_binding_context__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.client_binding)(detach) !== undefined || detach);
}
function finalize_no__constrained_on_containing_binding__yes__switch_client_binding_context__attributes(obj:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.instance_binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cno__constrained_on_containing_binding>obj.properties.instance_binding.inferences.instance_binding)(detach) !== undefined || detach);
}
function finalize_yes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes(obj:Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.output.node)(detach) !== undefined || detach);
}
function finalize_yes__switch_client_binding_context__attributes(obj:Cyes__switch_client_binding_context__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.client_binding)(detach) !== undefined || detach);
	switch (obj.properties.constrained_on_containing_binding.state.name) {
		case 'no': finalize_no__constrained_on_containing_binding__yes__switch_client_binding_context__attributes(obj.properties.constrained_on_containing_binding.state.node, detach); break;
		case 'yes': finalize_yes__constrained_on_containing_binding__yes__switch_client_binding_context__attributes(obj.properties.constrained_on_containing_binding.state.node, detach); break;
	}
	finalize_client_binding_type_path(obj.properties.type_path, detach);
}
function finalize_no__constrained_on_containing_binding__binding(obj:Cno__constrained_on_containing_binding__binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.output.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.instance_binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cno__constrained_on_containing_binding>obj.properties.instance_binding.inferences.instance_binding)(detach) !== undefined || detach);
}
function finalize_yes__constrained_on_containing_binding__binding(obj:Cyes__constrained_on_containing_binding__binding, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cnode__client_bindings>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.output.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__node>(obj.properties.instance_binding as any).resolve)(detach) !== undefined || detach);
	finalize_client_binding_type_path(obj.properties.type_path, detach);
}
function finalize_binding__type__attributes(obj:Cbinding__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	finalize_widget_configuration_node_location(obj.definitions.widget_configuration_node_location, detach);
	switch (obj.properties.constrained_on_containing_binding.state.name) {
		case 'no': finalize_no__constrained_on_containing_binding__binding(obj.properties.constrained_on_containing_binding.state.node, detach); break;
		case 'yes': finalize_yes__constrained_on_containing_binding__binding(obj.properties.constrained_on_containing_binding.state.node, detach); break;
	}
	finalize_widget_configuration_node(obj.properties.node, detach);
}
function finalize_list__type(obj:Clist__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	finalize_widget_configuration_node_location(obj.definitions.widget_configuration_node_location, detach);
	finalize_widget_configuration_node(obj.properties.node, detach);
}
function finalize_no__has_default__number(obj:Cno__has_default__number, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Crequired>obj.inferences.required)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
}
function finalize_yes__has_default__number(obj:Cyes__has_default__number, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
}
function finalize_number__type__configuration__type__attributes__widget_configuration_node(obj:Cnumber__type__configuration__type__attributes__widget_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	switch (obj.properties.has_default.state.name) {
		case 'no': finalize_no__has_default__number(obj.properties.has_default.state.node, detach); break;
		case 'yes': finalize_yes__has_default__number(obj.properties.has_default.state.node, detach); break;
	}
	finalize_configuration_attribute_persistence(obj.properties.persistence, detach);
}
function finalize_no__has_default__state_group(obj:Cno__has_default__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Crequired>obj.inferences.required)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
}
function finalize_yes__has_default__state_group(obj:Cyes__has_default__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__state_group(obj:Cstates__state_group, detach:boolean = false) {
	finalize_widget_configuration_node_location(obj.definitions.widget_configuration_node_location, detach);
	finalize_widget_configuration_node(obj.properties.node, detach);
	finalize_optional_state_HACK(obj.properties.state_default, detach);
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.properties.state_default.inferences.requirement)(detach) !== undefined || detach);
}
function finalize_state_group__type__configuration__type__attributes__widget_configuration_node(obj:Cstate_group__type__configuration__type__attributes__widget_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	switch (obj.properties.has_default.state.name) {
		case 'no': finalize_no__has_default__state_group(obj.properties.has_default.state.node, detach); break;
		case 'yes': finalize_yes__has_default__state_group(obj.properties.has_default.state.node, detach); break;
	}
	finalize_configuration_attribute_persistence(obj.properties.persistence, detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_group(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__has_default__text(obj:Cno__has_default__text, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Crequired>obj.inferences.required)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
}
function finalize_static__type(obj:Cstatic__type, detach:boolean = false) {
}
function finalize_unique_id(obj:Cunique_id, detach:boolean = false) {
}
function finalize_yes__has_default__text(obj:Cyes__has_default__text, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'static': finalize_static__type(obj.properties.type.state.node, detach); break;
		case 'unique id': finalize_unique_id(obj.properties.type.state.node, detach); break;
	}
}
function finalize_text__type__configuration__type__attributes__widget_configuration_node(obj:Ctext__type__configuration__type__attributes__widget_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	switch (obj.properties.has_default.state.name) {
		case 'no': finalize_no__has_default__text(obj.properties.has_default.state.node, detach); break;
		case 'yes': finalize_yes__has_default__text(obj.properties.has_default.state.node, detach); break;
	}
	finalize_configuration_attribute_persistence(obj.properties.persistence, detach);
}
function finalize_configuration__type__attributes__widget_configuration_node(obj:Cconfiguration__type__attributes__widget_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'list': finalize_list__type(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__configuration__type__attributes__widget_configuration_node(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__configuration__type__attributes__widget_configuration_node(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__configuration__type__attributes__widget_configuration_node(obj.properties.type.state.node, detach); break;
	}
}
function finalize_view__type__attributes(obj:Cview__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
}
function finalize_widget__type(obj:Cwidget__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
}
function finalize_window__type(obj:Cwindow__type, detach:boolean = false) {
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.output.requirement)(detach) !== undefined || detach);
	finalize_widget_configuration_node_location(obj.definitions.widget_configuration_node_location, detach);
	finalize_widget_configuration_node(obj.properties.node, detach);
}
function finalize_attributes__widget_configuration_node(obj:Cattributes__widget_configuration_node, detach:boolean = false) {
	switch (obj.properties.switch_client_binding_context.state.name) {
		case 'no': finalize_no__switch_client_binding_context__attributes(obj.properties.switch_client_binding_context.state.node, detach); break;
		case 'yes': finalize_yes__switch_client_binding_context__attributes(obj.properties.switch_client_binding_context.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'binding': finalize_binding__type__attributes(obj.properties.type.state.node, detach); break;
		case 'configuration': finalize_configuration__type__attributes__widget_configuration_node(obj.properties.type.state.node, detach); break;
		case 'view': finalize_view__type__attributes(obj.properties.type.state.node, detach); break;
		case 'widget': finalize_widget__type(obj.properties.type.state.node, detach); break;
		case 'window': finalize_window__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_widget_configuration_node(obj:Cwidget_configuration_node, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.client_binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cconfiguration_requirement>obj.input.requirement)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node_location>obj.output.location)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.attributes) {
		finalize_attributes__widget_configuration_node(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_widget_configuration_node_location(obj:Cwidget_configuration_node_location, detach:boolean = false) {
}
function finalize_widget_implementation_attribute(obj:Cwidget_implementation_attribute, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cattributes__control_node>obj.input.attribute)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => control_library.Cattributes__control_node>obj.output.attribute)(detach) !== undefined || detach);
	finalize_one_or_more_expression(obj.properties.expression, detach);
}
function finalize_widget_implementation_context(obj:Cwidget_implementation_context, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.explicit_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.explicit_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.output.widget_configuration_context)(detach) !== undefined || detach);
}
function finalize_widget_implementation_frame(obj:Cwidget_implementation_frame, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.input.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.explicit_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Clet_declarations__widget>obj.input.let_declarations)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.parent_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_configuration_node>obj.input.widget_configuration_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Clet_declarations__widget>obj.output.let_declarations)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.output.parent_context)(detach) !== undefined || detach);
	finalize_widget_implementation_context(obj.definitions.widget_implementation_context, detach);
}
function finalize_attributes__widget_implementation_node(obj:Cattributes__widget_implementation_node, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Cattributes__control_node>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_widget_implementation_attribute(obj.properties.attribute, detach);
}
function finalize_widget_implementation_node(obj:Cwidget_implementation_node, detach:boolean = false) {
	assert((<(detach?:boolean) => control_library.Ccontrol_node>obj.input.context_control_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => widget.Cwidget_implementation_context>obj.input.widget_implementation_context)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.attributes) {
		finalize_attributes__widget_implementation_node(entry, detach);
	}
	if (!detach) {
		(obj.properties.attributes as any).eval_required_keys(detach);
	}
}
function finalize_none__binding_context(obj:Cnone__binding_context, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
}
function finalize_no__switch_client_binding_context__select(obj:Cno__switch_client_binding_context__select, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.output.client_binding)(detach) !== undefined || detach);
}
function finalize_no__constrained_on_containing_binding__yes__switch_client_binding_context__select(obj:Cno__constrained_on_containing_binding__yes__switch_client_binding_context__select, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.output.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.instance_binding as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cno__constrained_on_containing_binding>obj.properties.instance_binding.inferences.instance_binding)(detach) !== undefined || detach);
}
function finalize_yes__constrained_on_containing_binding__yes__switch_client_binding_context__select(obj:Cyes__constrained_on_containing_binding__yes__switch_client_binding_context__select, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.output.client_binding)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__node>(obj.properties.instance_binding as any).resolve)(detach) !== undefined || detach);
	finalize_client_binding_type_path(obj.properties.type_path, detach);
}
function finalize_yes__switch_client_binding_context__select(obj:Cyes__switch_client_binding_context__select, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>obj.output.client_binding)(detach) !== undefined || detach);
	switch (obj.properties.constrained_on_containing_binding.state.name) {
		case 'no': finalize_no__constrained_on_containing_binding__yes__switch_client_binding_context__select(obj.properties.constrained_on_containing_binding.state.node, detach); break;
		case 'yes': finalize_yes__constrained_on_containing_binding__yes__switch_client_binding_context__select(obj.properties.constrained_on_containing_binding.state.node, detach); break;
	}
}
function finalize_select(obj:Cselect, detach:boolean = false) {
	assert((<(detach?:boolean) => client_bindings.Cbinding_context>obj.output.binding_context)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => client_bindings.Cbindings__client_bindings>(obj.properties.binding as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.switch_client_binding_context.state.name) {
		case 'no': finalize_no__switch_client_binding_context__select(obj.properties.switch_client_binding_context.state.node, detach); break;
		case 'yes': finalize_yes__switch_client_binding_context__select(obj.properties.switch_client_binding_context.state.node, detach); break;
	}
}
function finalize_widget(obj:Cwidget, detach:boolean = false) {
	finalize_implicit_implementation_context(obj.definitions.implicit_implementation_context, detach);
	finalize_widget_configuration_node_location(obj.definitions.widget_configuration_node_location, detach);
	switch (obj.properties.binding_context.state.name) {
		case 'none': finalize_none__binding_context(obj.properties.binding_context.state.node, detach); break;
		case 'select': finalize_select(obj.properties.binding_context.state.node, detach); break;
	}
	finalize_expression(obj.properties.expression, detach);
	finalize_optional_context_definition(obj.properties.optional_context_definition, detach);
	finalize_widget_configuration_node(obj.properties.widget, detach);
}

export namespace Cwidget {
	export function create(init:Twidget, input: {
	'client_bindings':client_bindings.Cclient_bindings
	'control_library':{[name:string]:control_library.Ccontrol_library}
	'phrases':phrases.Cphrases}, symbols_file?: SymbolsFile):Cwidget {
		const instance = new Cwidget(init, input as any, symbols_file);
		return instance;
	};
}
